import { TableActionGroup, TableActionButton, TableActionLink } from '@channelplay/table-actions';
import type { JSX, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, AlertTriangle, Check, CheckCircle, ArrowLeft, ArrowRight, ArrowLeftRight, Building2, Phone, CalendarDays, Clock, Users, UserCog, MapPin, Store as StoreIcon, X, SlidersHorizontal, ShieldAlert, Download } from 'lucide-react';
import { employeesAPI, storesAPI, scorecardAPI, storeIntelligenceAPI, rolesAPI, menusAPI, projectsAPI, yellowFlagDashboardAPI, customDashboardsAPI, locationsAPI, storeCustomFieldsAPI, type DashRichFilterParams, type StoreCustomField, type RedFlagDashLevel, type CustomDashboardRunFilters } from '../services/api';
import { buildCsv, downloadCsv } from '../utils/csv';
import { STAFFING_STATUS_BADGE, STAFFING_STATUS_UNSET_BADGE, STORE_STATUS_REDEPLOY, storeDisplayStatus } from '../constants/storeStatus';
import { buildRegionByStateKey, regionForStateName } from '../utils/stateRegion';
import { normalizeLocationKey } from '../utils/locationKey';
import { useAuthStore } from '../stores/authStore';
import { useMenuStore } from '../stores/menuStore';
import { useCurrentPageStore } from '../stores/currentPageStore';
import {
  ALL_DASH_TABS, DASHBOARD_HOME_PATH, DEPLOYMENT_LIST_SLUG, allowedDashTabs, dashLabel, dashTabFromSlug,
  deploymentListFromSlug, storeLevelRoleNames, type DashTab, type DeploymentList,
} from '../navigation/dashboards';
import { DASHBOARD_INFO } from '../navigation/catalog';
import { menuForPath, pageMenuItems, topLevelOf } from '../navigation/menuTree';
import { useHeldPage } from '../navigation/useHeldPage';
import { formatUndeployedAon, undeployedDays } from '../utils/undeployedAon';
import { matchesStaffingStatusFilter, staffingStatusFilterOptions } from '../utils/staffingStatusFilter';
import { deriveRedeployPendingRows, parseRedeployTimestamp, redeployWaitingDays, formatRedeployWaiting, type RedeployPendingRow } from '../utils/redeployPending';
import { Skeleton } from '../components/Skeleton';
import { BlockLoader, InlineLoader } from '../components/loading';
import AttendanceTab from '../components/dashboard/AttendanceTab';
import RedFlagTab, { type RedFlagTypeMeta } from '../components/dashboard/RedFlagTab';
import { flagTypeLabel, mergeFlagTypeList } from '../utils/redFlagDisplay';
import { useRedFlagRulesEnabled } from '../components/red-flag-rules/useRedFlagRulesEnabled';
import YellowFlagTab from '../components/dashboard/YellowFlagTab';
import MoodTab from '../components/dashboard/MoodTab';
import AppealsTab from '../components/dashboard/AppealsTab';
import AttritionTab, { type AttritionFacets } from '../components/dashboard/AttritionTab';
import { ATTRITION_TENURE_OPTIONS, mergeLocationOptions } from '../components/dashboard/attritionFilters';
import ApprovalsTab from '../components/dashboard/ApprovalsTab';
import OnboardingTab from '../components/dashboard/OnboardingTab';
import TrainingTab from '../components/dashboard/TrainingTab';
import ScoreMovementTab from '../components/dashboard/ScoreMovementTab';
import UniformTab from '../components/dashboard/UniformTab';
import DataHygieneTab from '../components/dashboard/DataHygieneTab';
import EwsTab from '../components/dashboard/EwsTab';
import { EWS_EMPLOYEE_OPTIONS, monthLabel, type EwsEmployees } from '../components/dashboard/ews';
import HiringDashboardTab from './HiringDashboardTab';
import { hiringUserKeyOf, useHiringAccess } from '../components/hiring/useHiringAccess';
import { STORE_ROW_HIRING_WORDS, storeRaiseRefusal, useStoreRowHiring, type HiringRowStore, type StoreRaiseRefusal } from '../components/hiring/useStoreRowHiring';
import { useDeployHiringCheck, type DeployHiringAsk } from '../components/hiring/useDeployHiringCheck';
import { StoreHiringDot } from '../components/hiring/StoreHiringDot';
import { STORES_PAGE, storeFixLink } from '../components/dashboard/dataHygiene';
import DashboardFilterModal, { type FilterSection } from '../components/dashboard/DashboardFilterModal';
import { useTableSort, SortTh } from '../components/dashboard/useTableSort';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { useDashboardList } from '../components/dashboard/useDashboardList';
import AssignStoreManagerModal from '../components/dashboard/AssignStoreManagerModal';
import {
  AssignManagerButton, StoreManagerCell, TickAllVacantStores, TickVacantStore, storeIdsSharingCode, useStoreVacancyManagers,
} from '../components/dashboard/useStoreVacancyManagers';
import {
  isClosedStoreStatus, minimumPeople, storeCodeKey, storesShortOfPeople,
} from '../components/dashboard/lists/deployment';
import CustomDashboardView from '../components/dashboard/custom/CustomDashboardView';
// A store earns its category's payout band and costs the salaries standing in it;
// these render what a redeployment does to that, on both sides of the move. They are
// absent entirely for a user without a financial-visibility grant.
import {
  NetImpactChip,
  FinancialImpactPanel,
  SourceStoreFinancialStrip,
  CandidateFinancialBlock,
  EmployeeCtc,
  type RedeployFinancial,
  type SourceStoreFinancials,
} from '../components/dashboard/RedeployFinancialImpact';
import type { CustomDashboard } from '../components/dashboard/custom/types';

type StatCard = {
  label: string;
  value: string;
  // A label of two to four words under the number. Never a sentence.
  helper: string;
  // The longer explanation, when there is one: the tile's hover text, not on the page.
  hint?: string;
  accent?: 'primary' | 'orange' | 'purple';
  // The list this number opens. Set ONLY when that list shows exactly this many rows
  // and the number is above zero; without it the tile is plain.
  list?: DeploymentList;
};

type StoreAnalytics = {
  id: string;
  name: string;
  trade: string;
  storeCode: string;
  category: string;
  city: string;
  state: string;
  /** stores.staffing_status — the manpower-pipeline axis, independent of store_status.
   *  A store is routinely Active AND 'Hiring to be done' at the same time. */
  staffingStatus: string | null;
  /** stores.store_status exactly as stored (Active, Notice, …). Read only to tell whether
   *  a manager assigned on Undeployed Stores reaches the store: only an exactly-'Active'
   *  store does (the server's rule). */
  storeStatus?: string | null;
  address?: string | null;
  pinCode?: string | null;
  employees: number;
  // Effective per-category minimum headcount (category min_employees, ≥1; defaults
  // to 1). A store counts as "undeployed" when its active headcount is BELOW this —
  // i.e. vacant (0) OR understaffed (e.g. an Apple L3 store, min 2, with only 1).
  minEmployees: number;
  // Aggregated active headcount, attached only on the Undeployed Stores list so the
  // table can render the "1 / 2" staffing deficit. Undefined elsewhere.
  activeHeadcount?: number;
  eligibility: string;
  status: 'Deployed' | 'Pending';
  monthlyCost: string;
  monthlyCostValue: number;
  payoutAmount: string;
  payoutMinValue: number | null;
  payoutMaxValue: number | null;
  profitLossValue: string;
  profitLossPercent: string;
  profitStatus: 'Loss Making' | 'Profitable';
  monthlyPayout: string;
  aonChange?: 'up' | 'down';
  compliance: {
    aon: boolean;
    seed: boolean;
  };
  meta: {
    ageOnNetwork?: 'lt-6' | '6-12' | 'gt-12';
  };
  storeSeedScore?: number | null;
  // For a currently-undeployed store: the date it became empty (day after the last
  // employee's last day, from store_employee_history; or the store's created_at if
  // it was never deployed). null when the store currently HAS an employee. Drives
  // the "AON" (how long undeployed) column on the Undeployed Stores tab.
  undeployedSince?: string | null;
  // When the store last dropped BELOW its category minimum (else the day it was added).
  // Sent for every store, so it is only meaningful once you know the store IS understaffed
  // — read it through `aonSinceOf`, never directly.
  understaffedSince?: string | null;
  // Project-defined store attributes (Master Data → Stores → Custom Fields) as
  // { fieldId: value }, straight from GET /stores. The Undeployed Stores tab reads
  // its "Staffing Type" column from here.
  customFields?: Record<string, unknown>;
  // The store as the Raise hiring window's store line shows it, straight from GET /stores
  // (the same fields the Stores page hands that window): the Undeployed Stores Actions.
  // Raw values, blank where none — not the dashes the columns above draw.
  hiringCard?: {
    storeCode: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    hasMapLocation: boolean;
    status: string;
    // Read off the RAW store_status and redeploy_pending, as the server's raise check reads them.
    raiseRefused: StoreRaiseRefusal | null;
  };
  deployedEmployees: {
    name: string;
    code: string;
    monthlyCtc: number | null;
    /** Step 1: what this store's margin becomes without them, minus what it is now. */
    impactIfMoved?: number | null;
    dateOfJoining?: string | null;
    seedScore?: number | null;
  }[];
};

// A row from the NSW / exit-initiation ledger (employee_exit_initiations).
// Surfaced on the store list so a store left vacant by an NSW exit is flagged.
type NswExitRow = {
  id: number;
  employeeCode: string;
  employeeName: string | null;
  storeId: string | null;
  storeName: string | null;
  reason: string | null;
  avg6m: number | null;
  quadrant: string | null;
  initiatedBy: string | null;
  createdAt: string;
};

// Hover text for the store-row "NSW" badge. Rows arrive newest-first, so rows[0]
// is the most recent exit; older ones are summarised as "+N more".
const formatNswTooltip = (rows: NswExitRow[]): string => {
  const r = rows[0];
  if (!r) return 'NSW';
  const name = r.employeeName?.trim() || r.employeeCode;
  const date = r.createdAt ? r.createdAt.slice(0, 10) : '';
  const bits = [
    `NSW: ${name} (${r.employeeCode})`,
    r.quadrant || null,
    r.avg6m != null ? `6-mo ${r.avg6m}` : null,
    date ? `on ${date}` : null,
  ].filter(Boolean);
  let tip = bits.join(' · ');
  if (rows.length > 1) tip += ` · +${rows.length - 1} more`;
  return tip;
};

// ── Store Intelligence (Store Performance dashboard) ──
// Store performance = the deployed employees' scorecard standing, aggregated
// per store by /store-intelligence. The summary still carries lens/performance
// fields server-side; the dashboard only consumes the people signals.
type StorePerformanceSignals = {
  targetAchievedPct: number | null;
  targetMetricCount: number;
  taskSubmissions: number;
  taskActiveDays: number;
  visits: number;
  visitDays: number;
};

type StoreEmployeeStanding = {
  employeeCode: string;
  employeeName: string;
  monthScore: number | null;
  avg6mScore: number | null;
  quadrant: Quadrant;
  isBottomBoxing: boolean;
  insufficientHistory: boolean;
  /** Age On Network (tenure since joining), e.g. "2y 3m". Null when unknown. */
  aonLabel?: string | null;
  aonDays?: number | null;
  /** True when the employee is serving notice (resignation / last-working-day set). */
  onNotice?: boolean;
  /** YYYY-MM-DD last working day, when known (tooltip on the Notice tag). */
  lastWorkingDay?: string | null;
  /** Designation / role (from user_roles), used by the Role filter. Null when unknown. */
  roleName?: string | null;
};

type StorePeopleSignals = {
  employeeCount: number;
  avgScore: number | null;
  avg6mScore: number | null;
  bottomBoxCount: number;
  groomingAvg: number | null;
  randomAttendancePct: number | null;
  trainingPct: number | null;
  employees: StoreEmployeeStanding[];
  ams?: { code: string; name: string }[];
  tradeHeads?: { code: string; name: string }[];
};

type StoreIntelligenceSummary = {
  // people = a roster exists (drives the Employee/AON columns); scorecard = the project
  // is scored (drives Standing + 6-mo Avg). A project can have the first without the
  // second — several payrolling programs do — and the table says so rather than
  // claiming there is no employee data.
  lenses: { financial: boolean; performance: boolean; people: boolean; scorecard: boolean };
  periodMonth: string;
  /**
   * false = the server could NOT confirm who has exited, so every headcount in `stores`
   * still counts leavers (store_employees is exit-blind and the live CRS check failed open).
   * Optional on purpose: an API that predates the flag sends nothing, and undefined must
   * keep the old behaviour — only an explicit false withholds the deployment KPIs.
   */
  rosterResolved?: boolean;
  // storeId → YYYY-MM-DD it became vacant, for stores whose only mapped employees have
  // exited (GET /stores can't date those while the stale rows exist). Optional: an older
  // API omits it and those stores keep showing "—".
  vacantSince?: Record<string, string>;
  // Month-end mode (this tab asks for it): set only for a PAST month. asOfDate = the month's
  // last day, the day the store + people picture is taken; openStoreIds = the stores that
  // existed and were open then; historyStartsOn = when deployment history began (a month
  // ending before it is approximate). All optional — without them the live view is kept.
  asOfDate?: string;
  openStoreIds?: string[];
  historyStartsOn?: string | null;
  stores: Record<string, { performance?: StorePerformanceSignals; people?: StorePeopleSignals }>;
};

// Per-employee standing. Quadrant = role-grouped 6-month standing (top-25 Stars /
// next-25 Achievers / next-45 Strivers / bottom-5 At Risk); "At Risk" is the
// bottom-box / redeployment flag.
// 'Excluded' (maternity hold-out) is stripped to null by the backend for all Dashboard
// standing sources, so it normally never reaches here — kept in the union + styled below
// only as a defensive guard against the unguarded QUADRANT_STYLE index.
type Quadrant = 'Stars' | 'Achievers' | 'Strivers' | 'At Risk' | 'New Joiner' | 'Excluded' | null;

// Bottom Box (bottom 3%) — one row per bottom-3% employee + their store.
type BottomBoxEmployee = {
  employeeCode: string;
  employeeName: string;
  storeId: string | null;
  storeName: string | null;
  storeCode: string | null;
  city: string | null;
  avg6mScore: number;
  quadrant: Quadrant;
  managerName: string | null;
  /** Assistant Manager (the direct reporting manager; "AM" throughout the code)
   *  + Trade Head (the AM's manager) codes —
   *  drive the AM / Trade Head filters. Null when the hierarchy is unresolved. */
  amCode?: string | null;
  thCode?: string | null;
  /** Designation / role (from user_roles), used by the Role filter. Null when unknown. */
  roleName?: string | null;
};
type BottomBoxData = {
  periodMonth: string;
  total: number;
  storeCount: number;
  employees: BottomBoxEmployee[];
};

// Sortable columns on the Store Performance table. Each maps to a per-store
// numeric value (see sortValueFor) so the table can be ordered by any of them.
type StoreSortKey = 'store' | 'category' | 'employees' | 'employee' | 'aon' | 'standing' | 'score' | 'avg6m';

// Rank quadrants best→worst so "Standing" sorts meaningfully (desc = Stars first).
// Only the four performance tiers carry a rank; 'New Joiner' (recent joiner,
// not yet ranked) is intentionally absent so it sorts as unranked like null.
const QUADRANT_RANK: Partial<Record<NonNullable<Quadrant>, number>> = {
  'Stars': 4,
  'Achievers': 3,
  'Strivers': 2,
  'At Risk': 1,
};

// Clickable column header for the Store Performance table. Shows a neutral ↕ when
// inactive and ▲/▼ for the active sort direction.
const SortHeader: React.FC<{
  label: React.ReactNode;
  sortKey: StoreSortKey;
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: (k: StoreSortKey) => void;
  align?: 'left' | 'right';
  title?: string;
}> = ({ label, sortKey, active, dir, onSort, align = 'left', title }) => (
  <th className={`whitespace-nowrap px-6 py-4 ${align === 'right' ? 'text-right' : ''}`} title={title}>
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="inline-flex items-center gap-1 hover:text-gray-700"
    >
      {label}
      <span className={`text-[10px] ${active ? 'text-primary-600' : 'text-gray-300'}`}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  </th>
);

// Colored pill for the 6-month quadrant (Stars / Achievers / Strivers / At Risk).
const QUADRANT_STYLE: Record<NonNullable<Quadrant>, string> = {
  Stars: 'bg-emerald-100 text-emerald-700',
  Achievers: 'bg-sky-100 text-sky-700',
  Strivers: 'bg-amber-100 text-amber-700',
  'At Risk': 'bg-rose-100 text-rose-700',
  'New Joiner': 'bg-indigo-100 text-indigo-700',
  'Excluded': 'bg-slate-100 text-slate-700',
};
const QuadrantBadge = ({ q }: { q: Quadrant }) =>
  q ? <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${QUADRANT_STYLE[q]}`}>{q}</span> : null;

// "Bottom Box" marker — the bottom 3% of each manager's team by 6-month average
// (the isBottomBoxing flag), a strict subset of the bottom-25% "At Risk" band.
// Rendered ALONGSIDE the quadrant pill (these employees are also At Risk), styled a
// deeper red so the critical tail reads as more severe.
const BottomBoxBadge = () => (
  <span
    className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800 ring-1 ring-red-300"
    title="Bottom Box — bottom 3% by 6-month average within the manager’s team (subset of At Risk)"
  >
    <ShieldAlert className="h-3 w-3" /> Bottom Box
  </span>
);

// One nearby store in the redeployment drill-down. Tier-sorted server-side:
// vacant+category match → vacant → occupied+category match → occupied.
type RedeployCandidate = {
  storeId: string;
  storeCode: string;
  storeName: string;
  city: string;
  trade: string;
  address: string;
  state: string;
  pinCode: string;
  distanceKm: number;
  categoryId: number | null;
  categoryName: string | null;
  categoryMatch: boolean;
  /** Same store trade as the origin — ranked above distance. */
  tradeMatch?: boolean;
  // isVacant is true when the store is eligible to receive this employee — either
  // empty, OR understaffed vs its category minimum (e.g. an Apple L3 store with 1 of
  // 2). When understaffed-but-occupied, deployedEmployee carries the lone incumbent
  // (for the "1/2" label) and slotsRemaining > 0 — the incumbent is NOT displaced.
  isVacant: boolean;
  employeeCount: number;
  minEmployees: number;
  slotsRemaining: number;
  deployedEmployee: {
    employeeCode: string;
    employeeName: string;
    seedScore: number | null;
    monthScore: number | null;
    avg6mScore: number | null;
    reportingManagerName: string | null;
    isBottomBoxing: boolean;
    insufficientHistory: boolean;
    rankInGroup: number | null;
    groupSize: number | null;
    quadrant: Quadrant;
    dateOfJoining: string | null;
    aonDays: number | null;
    aonLabel: string | null;
  } | null;
  // Present only in eligibility-gate mode (MisDeployed redeploy). Whether the
  // source employee meets THIS store's tenure band, the incumbent's tenure, and
  // whether a valid two-way swap exists (incumbent fits the source store).
  eligibility?: {
    reqDays: number;
    reqLabel: string;
    employeeFits: boolean;
    incumbentAonDays: number | null;
    incumbentAonLabel: string | null;
    swapValid: boolean;
  };
  // null unless the caller holds a financial-visibility grant AND the employee being
  // moved could be priced. Absent means "not allowed to know" — never "breaks even".
  financial?: RedeployFinancial | null;
};

// Decision context for one employee (master + team + score) — from
// scorecardAPI.getEmployeeContext. Reused for the source + replaced employees.
type EmployeeContext = {
  employee: {
    code: string;
    name: string;
    mobile: string | null;
    dateOfJoining: string | null;
    aon: { days: number | null; label: string | null };
    reportingManagerCode: string | null;
    reportingManagerName: string | null;
    teamSize: number;
  };
  score: {
    periodMonth: string | null;
    monthScore: number | null;
    avg6mScore: number | null;
    quadrant: Quadrant;
    isBottomBoxing: boolean;
    insufficientHistory: boolean;
    rankInGroup: number | null;
    groupSize: number | null;
  };
};

type ApiStoreRecord = {
  id?: string;
  store_name?: string;
  trade?: string;
  store_code?: string;
  store_category?: number;
  category_name?: string;
  payout_min?: number | string | null;
  payout_max?: number | string | null;
  eligibility_criteria?: string | null;
  seed_level?: number | null;
  // Per-category minimum staffing (store_categories.min_employees). NULL for
  // categories that never set one → treated as 1 (see normalizeStoreRecord).
  min_employees?: number | null;
  address?: string | null;
  state?: string | null;
  city?: string | null;
  pin_code?: string | null;
  // The store's map point, read only for the Raise hiring window's "no map location" line.
  latitude?: string | number | null;
  longitude?: string | number | null;
  /** The Status the Stores page shows (e.g. 'Outside Approved – Redeploy' over a stored 'Active'). */
  status_display?: string | null;
  /** Manpower-pipeline state, a SEPARATE column from store_status. Already returned by
   *  GET /stores (Master Data → Stores reads the same field); only the type was missing. */
  staffing_status?: string | null;
  /** The same state read against the live 1Office roster: an automatic 'Deployed' becomes
   *  'Hiring to be done' as soon as the last active employee leaves, without waiting for
   *  the reconcile sweep to unmap them. Absent on an older API — fall back to the stored
   *  value. A deliberate pipeline state is passed through unchanged. */
  staffing_status_effective?: string | null;
  employee_name?: string | null;
  designation?: string | null;
  employee_code?: string | null;
  date_of_joining?: string | null;
  salary?: number | string | null;
  store_status?: string | null;
  /** "Outside Approved – Redeploy": the store is still stored 'Active' (so it stays in
   *  every count here), and this flag says its people are due to be moved elsewhere. */
  redeploy_pending?: boolean | null;
  redeploy_pending_since?: string | null;
  ipro_status?: string | null;
  // New fields from store_employees junction table
  employees?: Array<{
    employee_code?: string;
    employee_name?: string;
    designation?: string;
    date_of_joining?: string | null;
    salary?: number | string | null;
    ipro_status?: string;
    seed_score?: number | null;
    is_primary?: boolean;
    role_name?: string | null;
    reporting_manager_name?: string | null;
    reporting_manager_code?: string | null;
    /** false = has left 1Office; the mapping is a leftover the roster sync removes. */
    is_active?: boolean;
  }>;
  employee_count?: number;
  // YYYY-MM-DD the store became undeployed (null when currently deployed).
  undeployed_since?: string | null;
  understaffed_since?: string | null;
  // Store custom-field values keyed by field id ({} when the store has none).
  custom_fields?: Record<string, unknown> | null;
};

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return currencyFormatter.format(value);
};

const getAgeOnNetworkBucket = (date?: string | null): 'lt-6' | '6-12' | 'gt-12' | undefined => {
  if (!date) return undefined;
  const joined = new Date(date);
  if (Number.isNaN(joined.getTime())) return undefined;
  const now = new Date();
  const diffInMonths = (now.getFullYear() - joined.getFullYear()) * 12 + (now.getMonth() - joined.getMonth());
  if (diffInMonths < 6) return 'lt-6';
  if (diffInMonths <= 12) return '6-12';
  return 'gt-12';
};

const toNumber = (value?: number | string | null): number | undefined => {
  if (value === null || value === undefined) return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// "How long undeployed" is formatUndeployedAon (utils/undeployedAon): always in days.
// A YYYY-MM-DD day as "31 Aug 2026" (the month-end banner).
const formatDayLabel = (iso: string): string =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

// A TIMESTAMP's calendar day in the viewer's timezone, as "4 Oct 2026". Unlike
// formatDayLabel it does not slice the ISO string first: a timestamptz arrives in UTC,
// and anything marked before 05:30 IST would print as the previous day. A value that
// cannot be read prints the dash, never "Invalid Date".
const formatTimestampDay = (value?: string | null): string => {
  const ms = parseRedeployTimestamp(value);
  return ms === null
    ? '—'
    : new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// A store custom-field value as display text: multiple_choice arrays comma-joined,
// booleans Yes/No, anything else as-is. '' when the store has no value.
const formatStoreCustomFieldValue = (value: unknown): string => {
  if (value == null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value).trim();
};

// How far the MisDeployed export looks for a swap partner. Matches the redeploy
// cockpit's own default radius, so a store the sheet calls fixable is one the cockpit
// will actually offer a partner for when you open it.
const MISDEPLOYED_EXPORT_RADIUS_KM = 10;

/**
 * Secondary employee facts, folded away by default.
 *
 * Step 1 asks one question — which of these people do you move — and mobile number,
 * reporting manager, joining date and team size do not answer it. They are still worth
 * having one click away, so they are collapsed rather than removed.
 *
 * Module level, NOT defined inside the modal's render function: a component declared
 * in a closure gets a fresh identity on every render, so React would unmount it and the
 * panel would snap shut whenever anything else on the page changed.
 */
const CollapsibleDetails = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 transition hover:text-gray-800"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {open ? 'Hide details' : 'More details'}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
};

// ── MisDeployed helpers ──
// Employee tenure ("AON") in days from date_of_joining. Mirrors the backend
// aonFromDoj so the check lines up with the AON shown in the table.
const aonDaysFromDoj = (doj?: string | null): number | null => {
  if (!doj) return null;
  const d = new Date(doj);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / 86_400_000);
};
const aonLabelFromDays = (days: number | null): string => {
  if (days == null) return '—';
  // A 30-day month fits twelve times before 365: 360–364 days is a year, never "12m".
  const rest = Math.floor((days % 365) / 30);
  const years = Math.floor(days / 365) + (rest === 12 ? 1 : 0);
  const months = rest === 12 ? 0 : rest;
  return years > 0 ? `${years}y ${months}m` : months > 0 ? `${months}m` : `${days}d`;
};

// Compact "date of joining" label ("12 Mar 2023"); falls back to the raw string.
const fmtJoinDate = (doj?: string | null): string => {
  if (!doj) return '—';
  const d = new Date(String(doj).slice(0, 10));
  return Number.isNaN(d.getTime())
    ? String(doj)
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── AON filter bands ──
// Employee tenure ("AON") band from days on network — used by the Store
// Performance / MisDeployed filters. Buckets align with the eligibility tiers.
const AON_BANDS = ['< 6 months', '6 months – 1 year', '1 – 2 years', '> 2 years'] as const;
const aonBandOf = (days: number | null): string | null => {
  if (days == null) return null;
  if (days < 183) return '< 6 months';
  if (days < 365) return '6 months – 1 year';
  if (days < 730) return '1 – 2 years';
  return '> 2 years';
};
// "How long undeployed" band from an undeployed-since date — used by the
// Undeployed Stores filter (a store's vacancy age, not an employee tenure).
const UNDEPLOYED_AON_BANDS = ['< 1 month', '1 – 3 months', '3 – 6 months', '> 6 months'] as const;
/**
 * The date the Undeployed Stores "AON" column ages from, for BOTH kinds of row.
 *
 * A vacancy is a vacancy whether the store is empty or merely short-handed, so an
 * understaffed store (an L3 at 1 of 2) ages from the day it fell below its minimum —
 * previously it had no date at all and the column rendered an em dash, hiding the oldest
 * gaps in the list. A fully vacant store keeps its existing meaning exactly: the day its
 * last employee left.
 *
 * ⚠️ Use this everywhere the AON is read — the cell, the vacancy-band filter and the CSV.
 * They drifted apart once before; a single source is what keeps the number the user
 * filters on identical to the one they see and download.
 *
 * Month-end mode is deliberately excluded: `understaffedSince` is computed as of TODAY,
 * so replaying it against a past month would date the gap from the wrong side of the
 * month boundary. Those rows keep the em dash until the history is queried as-of.
 */
const aonSinceOf = (
  s: { undeployedSince?: string | null; understaffedSince?: string | null },
  understaffed: boolean,
  asOf?: string | null,
): string | null => {
  if (understaffed) return asOf ? null : (s.understaffedSince ?? null);
  return s.undeployedSince ?? null;
};

const undeployedAonBandOf = (since?: string | null, asOf?: string | null): string | null => {
  if (!since) return null;
  const start = new Date(`${String(since).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  // Measured to a past month's last day in month-end mode, otherwise to now.
  const ref = asOf ? new Date(`${asOf}T00:00:00`).getTime() : Date.now();
  const days = Math.floor((ref - start.getTime()) / 86400000);
  if (days < 0) return null;
  if (days < 31) return '< 1 month';
  if (days < 92) return '1 – 3 months';
  if (days < 183) return '3 – 6 months';
  return '> 6 months';
};
// A store-category's eligibility criteria → the minimum tenure it requires, in
// days. 0 = no criteria (any tenure is fine, e.g. Base / L0). Parses the format
// the store-category form emits — "Greater than N years/months experience" (and
// the legacy "> N …") — so it handles any tier (6 months / 1 year / 2 years, and
// future ones like 9 or 18 months), takes the strictest when several apply, and
// treats non-matching text as "no criteria" (a store is never wrongly flagged).
// Years use 365d and months 365/12d so "1 year" and "12 months" both map to 365.
const eligibilityDays = (criteria?: string | null): number => {
  const t = (criteria || '').toLowerCase();
  let maxDays = 0;
  const re = /(?:greater than|>=?|≥)\s*(\d+(?:\.\d+)?)\s*-?\s*(year|month)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t)) !== null) {
    const n = parseFloat(m[1]);
    if (!Number.isFinite(n) || n <= 0) continue;
    const days = m[2] === 'year' ? Math.round(n * 365) : Math.round((n * 365) / 12);
    if (days > maxDays) maxDays = days;
  }
  return maxDays;
};
const eligibilityReqLabel = (lo: number): string => (lo >= 730 ? '> 2 years' : lo >= 365 ? '> 1 year' : lo >= 183 ? '> 6 months' : lo > 0 ? `> ${Math.max(1, Math.round(lo / 30))} months` : '—');
// MisDeployed AON grace period: months → days with the same 365/12-day month as
// eligibilityDays, so a 2-month grace and a "> 2 months" requirement are the same span.
// Mirrors aonGraceDays / MAX_AON_GRACE_MONTHS in apps/api/src/utils/eligibility.ts — keep in lockstep.
const MAX_AON_GRACE_MONTHS = 12;
const aonGraceDays = (months: number): number => Math.round((Math.max(0, months) * 365) / 12);

const normalizeStoreRecord = (store: ApiStoreRecord): StoreAnalytics => {
  // Convert salary to monthly if it appears to be annual (> 1 lakh likely means annual CTC)
  const rawSalary = toNumber(store.salary) ?? 0;
  const salary = rawSalary > 100000 ? Math.round(rawSalary / 12) : rawSalary;
  const payoutMin = toNumber(store.payout_min) ?? null;
  const payoutMax = toNumber(store.payout_max) ?? null;
  const representativePayout = payoutMax ?? payoutMin ?? 0;
  const profit = representativePayout - salary;
  const profitPercent = salary > 0 ? (profit / salary) * 100 : 0;
  const hasEmployee = !!(store.employee_code && store.employee_code.trim() !== '');
  const status = hasEmployee ? 'Deployed' : 'Pending';
  const complianceAon = (store.ipro_status ?? '').toLowerCase().includes('aon') || hasEmployee;
  const complianceSeed = (store.seed_level ?? 0) > 0;
  const ageOnNetwork = getAgeOnNetworkBucket(store.date_of_joining);
  const payoutDisplay = payoutMin && payoutMax && payoutMin !== payoutMax
    ? `${formatCurrency(payoutMin)} – ${formatCurrency(payoutMax)}`
    : formatCurrency(representativePayout || undefined);
  
  // Use employees array from store_employees junction table if available
  const deployedEmployees = Array.isArray(store.employees) && store.employees.length > 0
    ? store.employees.map((emp: any) => ({
        name: emp.employee_name ?? 'Unnamed employee',
        code: emp.employee_code ?? '—',
        monthlyCtc: emp.salary ? Number(emp.salary) : null,
        dateOfJoining: emp.date_of_joining,
        seedScore: emp.seed_score ?? null,
      }))
    : store.employee_name || store.employee_code
    ? [
        {
          name: store.employee_name ?? 'Unnamed employee',
          code: store.employee_code ?? '—',
          monthlyCtc: salary ?? null,
          dateOfJoining: store.date_of_joining,
          seedScore: store.seed_level ?? null,
        },
      ]
    : [];

  // Use employee_count from API or fallback to employees array length
  const employeeCount = store.employee_count ?? deployedEmployees.length;

  return {
    id: store.id ?? store.store_code ?? store.store_name ?? crypto.randomUUID(),
    name: store.store_name ?? 'Unnamed Store',
    trade: store.trade ?? 'Unknown',
    storeCode: storeCodeKey(store.store_code),
    category: store.category_name ?? 'Uncategorized',
    city: store.city ?? '—',
    state: store.state ?? '—',
    // The server's exit-aware reading: a store whose staff have all left 1Office reads
    // 'Hiring to be done' straight away, instead of keeping its automatic 'Deployed' badge
    // until the reconcile sweep deletes their store_employees rows. A deliberate pipeline
    // state is passed through untouched. Older API (no field) → the stored value, as before.
    staffingStatus: store.staffing_status_effective ?? store.staffing_status ?? null,
    storeStatus: store.store_status ?? null,
    employees: employeeCount,
    // Mirror the server's `effectiveMin` (apple-scorecard redeploy): NULL/invalid/<1 → 1,
    // so categories that never opted in behave exactly as before (undeployed = 0 deployed).
    minEmployees: minimumPeople(store.min_employees),
    eligibility: store.eligibility_criteria ?? 'Eligibility not provided',
    status,
    monthlyCost: formatCurrency(salary || undefined),
    monthlyCostValue: salary,
    payoutAmount: payoutDisplay,
    payoutMinValue: payoutMin,
    payoutMaxValue: payoutMax,
    profitLossValue: `${profit >= 0 ? '+' : '−'}${formatCurrency(Math.abs(profit))}`.replace('−₹', '−₹').replace('+₹', '+₹'),
    profitLossPercent: salary > 0 ? `${profit >= 0 ? '+' : '−'}${Math.abs(profitPercent).toFixed(1)}%` : '—',
    profitStatus: profit >= 0 ? 'Profitable' : 'Loss Making',
    monthlyPayout: payoutDisplay,
    aonChange: profit >= 0 ? 'up' : 'down',
    compliance: {
      aon: complianceAon,
      seed: complianceSeed,
    },
    meta: {
      ageOnNetwork,
    },
    storeSeedScore: store.seed_level ?? null,
    // Only meaningful when the store is currently undeployed (backend sends null
    // otherwise). Used by the Undeployed Stores "AON" column.
    undeployedSince: store.undeployed_since ?? null,
    understaffedSince: store.understaffed_since ?? null,
    customFields: store.custom_fields ?? {},
    hiringCard: {
      storeCode: store.store_code ?? '',
      address: store.address ?? '',
      city: store.city ?? '',
      state: store.state ?? '',
      pincode: store.pin_code ?? '',
      hasMapLocation: !!String(store.latitude ?? '').trim() && !!String(store.longitude ?? '').trim(),
      status: storeDisplayStatus(store),
      raiseRefused: storeRaiseRefusal(store),
    },
    deployedEmployees,
  };
};

/** A row of Undeployed Stores as the hiring windows are given it (the Stores page hands them the same). */
const hiringRowStoreOf = (store: StoreAnalytics & { storeIds?: readonly string[] }): HiringRowStore => ({
  storeId: store.id,
  storeIds: store.storeIds,
  storeName: store.name,
  storeCode: store.hiringCard?.storeCode ?? '',
  address: store.hiringCard?.address ?? '',
  city: store.hiringCard?.city ?? '',
  state: store.hiringCard?.state ?? '',
  pincode: store.hiringCard?.pincode ?? '',
  hasMapLocation: store.hiringCard?.hasMapLocation ?? false,
  status: store.hiringCard?.status ?? '',
  staffingStatus: store.staffingStatus ?? '',
  raiseRefused: store.hiringCard?.raiseRefused ?? null,
});

// Red Flag tab → "Red Flag Type" filter. The options come from the API: the trend's
// `types` (the month's rule set — enabled standard + custom rules, labels included).
// This list is only the FALLBACK used until/unless the API supplies them (request
// failed, or an API without `types`); its keys are the six standard codes.
const LEGACY_RED_FLAG_TYPE_OPTIONS = [
  { value: '', label: 'All Red Flag Types' },
  { value: 'grooming', label: 'Uniform not compliant' },
  { value: 'uninformed_absence', label: 'Uninformed absence' },
  { value: 'missed_training', label: 'Missed training' },
  { value: 'missing_signout', label: 'Missing sign-out' },
  { value: 'late_signin', label: 'Late sign-in' },
  { value: 'missed_random', label: 'Missed random attendance' },
];
const LEGACY_RED_FLAG_TYPES: RedFlagTypeMeta[] = LEGACY_RED_FLAG_TYPE_OPTIONS
  .filter((o) => o.value !== '')
  .map((o) => ({ key: o.value, label: o.label }));
const LEGACY_RED_FLAG_TYPE_KEYS = new Set(LEGACY_RED_FLAG_TYPES.map((t) => t.key));
// Red Flag tab → optional "Level" scope ('' = everyone). Offered only while Red Flag
// Rules is switched on on the server; sent as level= only when chosen.
const RED_FLAG_LEVEL_OPTIONS = [
  { value: '', label: 'All levels' },
  { value: 'frontline', label: 'Store-level employees' },
  { value: 'manager', label: 'Managers' },
];

// The dashboards that are handed `roleId` and ask the server with it. Each waits for the
// store-level Role default before it is drawn (see `roleDefaultSettled`). Deployment and
// Data Hygiene filter by role in the browser (Data Hygiene's Employees side waits for the
// default itself), and Onboarding and Uniform take no Role filter at all.
const ROLE_FILTERED_TABS: ReadonlySet<DashTab> = new Set<DashTab>([
  'Attendance', 'Red Flag', 'Yellow Flag', 'Mood', 'Appeals', 'Attrition', 'Approvals', 'Training', 'Score Movement', 'EWS',
]);

// A dashboard's measure: the one the home screen and the section pages use (see HomePage
// and SectionFrontPage), so every screen lines up. Each of the twelve dashboards, and one
// the team built, is drawn inside it.
const PAGE = 'mx-auto w-full max-w-[1352px] space-y-4 pb-2 pt-4 sm:px-2';
// The frame of a white block, as on the home screen and the section pages.
const CARD = 'rounded-2xl border border-gray-200 bg-white shadow-sm';
// A list's card holds its table edge to edge. Clipped, so a tinted or hovered last row
// (and the pinned Actions cell) stop at the card's rounded corners. Everything that could
// be cut off already sits inside the table's own sideways scroller.
const LIST_CARD = `overflow-hidden ${CARD}`;
// A headline number on Deployment's strip: the frame, with the padding of the tile the
// other dashboards draw (StatTile), so a number looks the same on every dashboard.
const TILE = `${CARD} p-5`;

// "Related", at the foot of a built-in dashboard: where the work behind it is done.
// A dashboard shows what is happening; the list where it is acted on is another page,
// and people were leaving the Dashboard to hunt for it through the menus.
//
// Only pages this person HOLDS are offered, read from the menu the shell loaded and
// narrowed by the top bar's own hiding rules — a link to a page they cannot open would
// be worse than no link. Until the menu has loaded nothing is known, so nothing shows.
// The page's name is its menu row's name; the catalogue supplies only the reason to go.
function RelatedRow({ tab }: { tab: DashTab }): ReactElement | null {
  const items = useMenuStore((state) => state.items);
  const loaded = useMenuStore((state) => state.loaded);
  const isSuperAdmin = useAuthStore((state) => state.user?.adminRole === 'super_admin');
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const links = useMemo(() => {
    if (!loaded) return [];
    const pages = pageMenuItems(items, { isSuperAdmin, selectedProjectId });
    return DASHBOARD_INFO[tab].related.flatMap((r) => {
      const row = menuForPath(pages, r.path);
      if (!row) return [];
      const top = topLevelOf(pages, row);
      // A top-level page (My Approvals) is its own section: its name is not said twice.
      const where = top.id === row.id ? row.name : `${top.name} › ${row.name}`;
      return [{ path: r.path, label: `${r.what} · ${where}` }];
    });
  }, [tab, items, loaded, isSuperAdmin, selectedProjectId]);

  if (links.length === 0) return null;
  return (
    <nav aria-label="Related" className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
      <span>Related:</span>
      {links.map((l) => (
        <Link
          key={l.path}
          to={l.path}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 font-semibold text-gray-700 transition hover:border-primary-200 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {l.label}
          <ArrowRight className="h-3 w-3" aria-hidden />
        </Link>
      ))}
    </nav>
  );
}

export default function DashboardPage() {
  // EVERY store the API returns, closed ones included. `stores` (derived below, once the
  // summary is in scope) picks the month's universe from it.
  const [allStores, setAllStores] = useState<StoreAnalytics[]>([]);
  // Stores closed TODAY (store_status 'inactive') — left out of the live view.
  const [closedStoreIds, setClosedStoreIds] = useState<Set<string>>(() => new Set());
  const [employeesDeployedCount, setEmployeesDeployedCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    storeCode: '',
    storeName: '',
  });
  // Multi-select filter states (rendered via the generic filter modal; the modal
  // owns each control's open/search UI state internally).
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  // Attrition tab only: tenure-band KEYS (see attritionFilters.ts). Narrows which
  // exits the tab counts; unlike every other filter here it does not touch headcount.
  const [selectedTenures, setSelectedTenures] = useState<string[]>([]);
  // 1Office master states. The ONLY authoritative state->region mapping we have:
  // every state row carries regionId/regionName (e.g. Bihar -> "East"). Fetched once;
  // the endpoint is itself cached server-side for 5 minutes.
  const [apiStates, setApiStates] = useState<Array<{ stateName?: string; regionName?: string }>>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  // Hierarchy filters — selected AM / Trade Head codes (display name from the
  // store-intelligence people lens).
  const [selectedAMs, setSelectedAMs] = useState<string[]>([]);
  const [selectedTradeHeads, setSelectedTradeHeads] = useState<string[]>([]);
  const [selectedBudgets, setSelectedBudgets] = useState<string[]>([]);
  const [budgetOptions, setBudgetOptions] = useState<{ value: string; label: string }[]>([]);
  // Role filter — selected designation / role names (from each employee's roleName
  // in the store-intelligence people lens). Plain string list, mirrors the AM filter.
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  // Project roles (id ↔ name) — resolves the shared Role filter's role NAMES into
  // the role IDs the Attendance tab's backend filter expects. Soft-fails to [].
  const [projectRoles, setProjectRoles] = useState<Array<{ id: string; name: string; category: string }>>([]);
  // The project those roles are of: null until a roles request has answered, or failed.
  // After a project switch the roles held are still the last project's until then.
  const [projectRolesFor, setProjectRolesFor] = useState<number | null>(null);

  // ── Generic filter modal ──
  const [showFilterModal, setShowFilterModal] = useState(false);
  // Yellow Flag tab: reason scope. Yellow reasons are per-project DB rows, so the
  // options are FETCHED per project (the Red Flag type list below is API-driven too).
  const [yellowReason, setYellowReason] = useState('');
  const [yellowReasonOptions, setYellowReasonOptions] = useState<Array<{ value: string; label: string }>>([
    { value: '', label: 'All Yellow Flag Reasons' },
  ]);
  // Extended per-view filters (shown contextually by the modal; see filterConfig).
  const [selectedStandings, setSelectedStandings] = useState<string[]>([]);         // 6-mo quadrant standing
  const [empNameQuery, setEmpNameQuery] = useState('');                             // employee name (contains)
  const [empCodeQuery, setEmpCodeQuery] = useState('');                             // employee code (contains)
  const [ewsEmployees, setEwsEmployees] = useState<EwsEmployees>('active');            // EWS: who is listed (active / left / both)
  const [redFlagType, setRedFlagType] = useState('');                               // Red Flag tab: single red-flag parameter ('' = all)
  // Red Flag tab: subject level ('' = all). Set only through the Level section, which
  // exists only once the server reports Red Flag Rules switched on — so while the feature
  // is dark this stays '' and the filter count, requests and tab are exactly as before.
  const [redFlagLevel, setRedFlagLevel] = useState<'' | RedFlagDashLevel>('');
  // Red Flag tab: the type list reported by the tab's trend (null = not loaded / unavailable
  // → LEGACY_RED_FLAG_TYPE_OPTIONS). Reset per project, since custom rules are per project.
  const [redFlagTypesFromApi, setRedFlagTypesFromApi] = useState<RedFlagTypeMeta[] | null>(null);
  // An UNFILTERED trend lists the month's whole rule set, so it replaces the list. A
  // trend fetched WITH a type filter lists only that type, so it may only ADD to the
  // list — otherwise picking a type would shrink the dropdown to that one entry. An
  // unavailable list (null) keeps what we have (the fallback list when nothing).
  const handleRedFlagTypesLoaded = useCallback((types: RedFlagTypeMeta[] | null, requested: string | undefined) => {
    setRedFlagTypesFromApi((prev) => mergeFlagTypeList(prev, types, requested, LEGACY_RED_FLAG_TYPES));
  }, []);
  const [selectedReportingHeads, setSelectedReportingHeads] = useState<string[]>([]); // manager name (Bottom Box)
  const [selectedAonBands, setSelectedAonBands] = useState<string[]>([]);           // employee tenure band (MisDeployed)
  const [selectedVacancyBands, setSelectedVacancyBands] = useState<string[]>([]);   // "how long undeployed" band (Undeployed Stores)
  const [selectedStaffingStatuses, setSelectedStaffingStatuses] = useState<string[]>([]); // staffing status, or "not set" (Undeployed Stores)
  const [selectedEligibilities, setSelectedEligibilities] = useState<string[]>([]); // category eligibility req label
  const [selectedFits, setSelectedFits] = useState<string[]>([]);                   // MisDeployed: Under / Over qualified
  const [aonGraceMonths, setAonGraceMonths] = useState(0);                          // MisDeployed: AON grace period (0 = none)
  const [selectedDeployedCounts, setSelectedDeployedCounts] = useState<string[]>([]); // # employees deployed at store

  const [insightsStore, setInsightsStore] = useState<StoreAnalytics | null>(null);
  // ── Store Intelligence summary ──
  // The dashboard is a single Store Performance view: a store's performance is
  // its deployed employees' scorecard standing. Summary failure degrades to a
  // bare store list (no scores/highlights) — never blocks the table.
  const [intelligence, setIntelligence] = useState<StoreIntelligenceSummary | null>(null);
  // Which project and month that summary was asked for, and where the request stands.
  // The summary alone cannot say: the one of the project or month just left stays up until
  // the next arrives, and null is both "not answered yet" and "could not be read". An
  // answer with no summary in it is 'failed' too. Kept through a refresh of the same
  // project and month, which keeps its summary meanwhile (see redeployPendingLoad).
  const [intelligenceLoad, setIntelligenceLoad] =
    useState<{ projectId: number | null; month: string | null; state: 'loading' | 'ready' | 'failed' }>({ projectId: null, month: null, state: 'loading' });
  // The Deployment tab's store universe. For a past month the summary (month-end mode) names
  // the stores that existed and were open on its last day — so a store opened later is left
  // out and one closed since is back in. Otherwise (current month, or until that summary
  // arrives): every store that isn't closed today.
  const stores = useMemo(() => {
    const open = intelligence?.asOfDate && intelligence.openStoreIds ? new Set(intelligence.openStoreIds) : null;
    return open
      ? allStores.filter((s) => open.has(s.id))
      : allStores.filter((s) => !closedStoreIds.has(s.id));
  }, [allStores, closedStoreIds, intelligence]);
  // Bottom Box = bottom 3% of each employee's DIRECT manager's full reporting
  // hierarchy (by 6-month average), independent of the role-grouped Standing
  // quadrant. Drives the "Bottom Box" KPI + table.
  const [bottomBox, setBottomBox] = useState<BottomBoxData | null>(null);
  // Undeployed (active) project employees — not currently placed at any store.
  // Drives the "Undeployed Employees" KPI card + Show tab. `roleName` (user_roles →
  // roles.name) is attached server-side so the Role filter can scope this list too.
  //
  // Kept with the project they were loaded for: `undeployedEmps` (below, once the project
  // on screen is known) is these rows only while that project is the one on screen.
  const [undeployedEmpsLoad, setUndeployedEmpsLoad] = useState<{
    projectId: number | null;
    rows: Array<{ name: string; code: string; designation?: string; city?: string; state?: string; department?: string; roleName?: string | null; amCode?: string | null; thCode?: string | null }>;
  }>({ projectId: null, rows: [] });
  // "Outside Approved – Redeploy": active employees still deployed at a store marked
  // outside the approved list. Read straight off GET /stores (see fetchStores), so it
  // is today's picture whichever month is selected — like Undeployed Employees.
  const [redeployPendingAll, setRedeployPendingAll] = useState<RedeployPendingRow[]>([]);
  // Which load that list reflects. An empty list only means "nobody is waiting" once a
  // load of THIS project has succeeded; until then, or after a failed one, the tab says
  // so instead. Kept apart from isLoading, which is also raised by the refresh that
  // follows every redeploy — the rows stay up through that one.
  const [redeployPendingLoad, setRedeployPendingLoad] =
    useState<{ projectId: number | null; state: 'loading' | 'ready' | 'failed' }>({ projectId: null, state: 'loading' });
  // Month under review — anchors the per-employee scores + 6-mo window.
  //
  // It starts on the current month, and the filters on their defaults, every time the
  // page is opened from the home screen — on purpose; do not carry them across it. The
  // home screen's numbers are the current month's with the default filters, and each must
  // equal the dashboard it opens. Moving from one dashboard straight to another keeps
  // both, because the page stays mounted.
  const [periodMonth, setPeriodMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  // monthOptions is Deployment-tab-aware, so it's declared below (after `activeTab`).
  // ── Redeployment cockpit state (full-screen store view) ──
  // Nearby-store candidates (score + proximity, tier-sorted server-side).
  const [redeployCandidates, setRedeployCandidates] = useState<RedeployCandidate[] | null>(null);
  // Nearby stores the server withheld because they have no category defined. A
  // store with no category has no eligibility_criteria, so a move into it can't
  // be validated — we show the count so a short list reads as "filtered", not "broken".
  const [redeployExcludedUncat, setRedeployExcludedUncat] = useState(0);
  // Whether THIS response carried financial numbers — the server's answer, not a
  // client guess. False for anyone without a row in financial_visibility_users, and
  // the whole impact UI is absent rather than empty in that case.
  const [redeployFinancialsVisible, setRedeployFinancialsVisible] = useState(false);
  // Step 1 loads before any candidate fetch, so it needs its own answer — the insights
  // response carries one. Both are the SERVER's verdict, never a client guess.
  const [insightsFinancialsVisible, setInsightsFinancialsVisible] = useState(false);
  // The open store's own payout / cost / margin, for step 1.
  const [insightsStoreFinancials, setInsightsStoreFinancials] = useState<SourceStoreFinancials | null>(null);
  // The source store's own economics today (payout, cost, margin), for the "before"
  // column of every projection.
  const [sourceStoreFinancials, setSourceStoreFinancials] = useState<SourceStoreFinancials | null>(null);
  const [redeployRadiusKm, setRedeployRadiusKm] = useState(10);
  // Live slider value (committed to redeployRadiusKm + a refetch on release).
  const [radiusDraft, setRadiusDraft] = useState(10);
  const [redeployLoading, setRedeployLoading] = useState(false);
  // The employee (A) the user chose to redeploy out of the current store.
  const [redeployEmp, setRedeployEmp] = useState<StoreAnalytics['deployedEmployees'][0] | null>(null);
  // ELIGIBILITY mode — set when the cockpit is opened from a MisDeployed row.
  // Switches candidate gating to tenure-fit (backend gate=eligibility) and makes
  // Confirm a TWO-WAY SWAP (both employees keep their jobs) instead of NSW.
  const [eligibilityMode, setEligibilityMode] = useState(false);
  // The employee the user chose to NSW (review-then-confirm; skips Destination).
  const [nswEmp, setNswEmp] = useState<StoreAnalytics['deployedEmployees'][0] | null>(null);
  // The chosen target store (X) for A, and its currently-deployed employee (B,
  // the one who will be set to NSW + undeployed when the move is saved).
  // `isVacant` here means "eligible to receive without displacing anyone" — empty
  // OR understaffed vs the category minimum (e.g. Apple L3 with 1/2). When vacant +
  // an `employee` is present, that's the lone incumbent kept on (we ADD a 2nd), so
  // the redeployment must NOT mark them NSW. slotsRemaining/counts drive the copy.
  const [redeployTarget, setRedeployTarget] = useState<{ storeId: string; storeName: string; storeCode: string; isVacant: boolean; employeeCount: number; minEmployees: number; slotsRemaining: number; employee: { code: string; name: string; score: number | null; isBottomBoxing: boolean; quadrant: Quadrant } | null; financial: RedeployFinancial | null } | null>(null);
  // Rich decision context per deployed employee in the open store (master +
  // team + score), keyed by employee code; fed by getEmployeeContext.
  const [empContexts, setEmpContexts] = useState<Record<string, EmployeeContext>>({});
  const [contextsLoading, setContextsLoading] = useState(false);
  // Month the Step-1 employee cards show the score for (the dropdown).
  const [contextMonth, setContextMonth] = useState<string>(periodMonth);
  // Store-details card collapsed by default — keep the focus on the employees.
  const [storeDetailsOpen, setStoreDetailsOpen] = useState(false);
  // Step-2 "Redeploying" employee card collapsed by default (compact summary).
  const [srcCardOpen, setSrcCardOpen] = useState(false);
  // Context of the incumbent at the chosen target store (the replaced employee).
  const [displacedContext, setDisplacedContext] = useState<EmployeeContext | null>(null);
  // True while the whole chain is being applied.
  const [chainExecuting, setChainExecuting] = useState(false);
  // Bumped after a redeployment so the score/roster overlays (bottom-boxing +
  // store-intelligence) refetch — otherwise the table keeps showing a moved
  // employee at their old store until a full page reload.
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Per-store NSW exit ledger (keyed by store uuid) → drives the store-row "NSW" badge.
  const [nswByStore, setNswByStore] = useState<Map<string, NswExitRow[]>>(new Map());
  // Store-table column sort. null = default ordering (At-Risk first). Clicking a
  // column header cycles desc → asc → back to default.
  const [sortBy, setSortBy] = useState<StoreSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = useCallback((key: StoreSortKey) => {
    setSortBy((prevKey) => {
      if (prevKey !== key) { setSortDir('desc'); return key; }
      // Same column: desc → asc → clear (back to default ordering).
      let cleared = false;
      setSortDir((d) => { if (d === 'desc') return 'asc'; cleared = true; return 'desc'; });
      return cleared ? null : key;
    });
  }, []);
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  // Bumped each time the cockpit is opened or closed. Whatever waited on the insights
  // request compares it afterwards — state read after an await is a stale closure.
  const cockpitOpeningRef = useRef(0);
  // The store the cockpit was opened on FROM the "Outside Approved – Redeploy" list;
  // null for every other way in. See executeRedeploymentChain.
  const cockpitVacateStoreRef = useRef<string | null>(null);

  // Swap modal states
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [employeeToSwap, setEmployeeToSwap] = useState<StoreAnalytics['deployedEmployees'][0] | null>(null);
  const [swapModalActiveTab, setSwapModalActiveTab] = useState<'deployed' | 'undeployed'>('deployed');
  const [swapModalStep, setSwapModalStep] = useState<1 | 2>(1);
  const [swapSearchTerm, setSwapSearchTerm] = useState('');
  const [eligibleEmployees, setEligibleEmployees] = useState<Array<{
    name: string;
    code: string;
    designation?: string;
    dateOfJoining?: string;
    monthlyCtc: number | null;
    seedScore?: number | null;
    store: {
      id: string;
      name: string;
      storeCode: string;
      trade: string;
      city: string;
      state: string;
      category: string;
    };
  }>>([]);
  const [undeployedEmployees, setUndeployedEmployees] = useState<Array<{
    name: string;
    code: string;
    designation?: string;
    dateOfJoining?: string;
    monthlyCtc: number | null;
    seedScore?: number | null;
    city?: string;
    state?: string;
    department?: string;
  }>>([]);
  const [isLoadingEligibleEmployees] = useState(false);
  const [selectedEmployeeForSwap, setSelectedEmployeeForSwap] = useState<typeof eligibleEmployees[0] | typeof undeployedEmployees[0] | null>(null);
  const [availableStores, setAvailableStores] = useState<Array<{
    id: string;
    storeCode: string;
    name: string;
    city: string;
    state: string;
    trade: string;
    status: string;
    employeeCode: string;
    employeeName: string;
    salary: number;
    category: {
      id: string;
      name: string;
      payoutMin: number | null;
      payoutMax: number | null;
      eligibility: string;
      seedLevel: number | null;
    };
  }>>([]);
  const [selectedNewStore, setSelectedNewStore] = useState<typeof availableStores[0] | null>(null);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);
  
  // Sorting criteria for swap modal
  const defaultSortingCriteria = [
    { id: 'tradeMatch', name: 'Trade Match', enabled: true, priority: 1 },
    { id: 'categoryMatch', name: 'Store Category Match', enabled: true, priority: 2 },
    { id: 'cityMatch', name: 'City Match', enabled: true, priority: 3 },
    { id: 'experience', name: 'Age on Network', enabled: true, priority: 4 },
    { id: 'seedScore', name: 'Seed Score Match', enabled: true, priority: 5 },
    { id: 'financial', name: 'Financial Impact', enabled: true, priority: 6 }
  ];

  // Count of ACTIVE filters (drives the modal badge + the "Clear all" enable +
  // the KPI "Matching filters" copy). Month is excluded — it always has a value.
  const activeFilterCount = useMemo(() =>
    (filters.storeName ? 1 : 0) +
    (filters.storeCode ? 1 : 0) +
    (selectedTrades.length ? 1 : 0) +
    (selectedStates.length ? 1 : 0) +
    (selectedRegions.length ? 1 : 0) +
    (selectedCities.length ? 1 : 0) +
    (selectedCategories.length ? 1 : 0) +
    (selectedRoles.length ? 1 : 0) +
    (selectedStandings.length ? 1 : 0) +
    (empNameQuery.trim() ? 1 : 0) +
    (empCodeQuery.trim() ? 1 : 0) +
    (selectedReportingHeads.length ? 1 : 0) +
    (selectedAonBands.length ? 1 : 0) +
    (selectedVacancyBands.length ? 1 : 0) +
    (selectedStaffingStatuses.length ? 1 : 0) +
    (selectedEligibilities.length ? 1 : 0) +
    (selectedFits.length ? 1 : 0) +
    (selectedDeployedCounts.length ? 1 : 0) +
    (selectedAMs.length ? 1 : 0) +
    (selectedTradeHeads.length ? 1 : 0) +
    (selectedBudgets.length ? 1 : 0) +
    (redFlagType ? 1 : 0) +
    (redFlagLevel ? 1 : 0) +
    (yellowReason ? 1 : 0) +
    (ewsEmployees !== 'active' ? 1 : 0)
  , [filters, selectedTrades, selectedStates, selectedRegions, selectedCities, selectedCategories, selectedRoles, selectedStandings, empNameQuery, empCodeQuery, selectedReportingHeads, selectedAonBands, selectedVacancyBands, selectedStaffingStatuses, selectedEligibilities, selectedFits, selectedDeployedCounts, selectedAMs, selectedTradeHeads, selectedBudgets, redFlagType, redFlagLevel, ewsEmployees]);
  const hasFiltersApplied = activeFilterCount > 0;

  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const user = useAuthStore((state) => state.user);

  // The open dashboard comes from the ADDRESS: /dashboard/<slug> for a built-in one,
  // /dashboard/custom/<id> for one the team built. It used to be page memory, so a
  // dashboard could not be linked or bookmarked and a refresh went back to Deployment.
  //
  // 'Custom' is a SENTINEL, not a real DashTab: it means "a user-built custom
  // dashboard is showing, identified by activeCustomId". Widening the type
  // rather than DashTab itself keeps DASH_TAB_MENU_CODE exhaustive, and makes every
  // existing `activeTab === '<built-in>'` render guard evaluate false for free — so
  // no built-in tab's markup had to be touched to add this.
  //
  // null = the address names no dashboard. Nothing is drawn for it: the person is sent
  // to the home screen (see `leaveForHome`).
  const { slug, customId } = useParams<{ slug?: string; customId?: string }>();
  const activeTab: DashTab | 'Custom' | null = customId !== undefined ? 'Custom' : dashTabFromSlug(slug);
  const activeCustomId = customId !== undefined && /^\d+$/.test(customId) ? Number(customId) : null;
  // With an address each, the browser's Back and Forward can change the dashboard under
  // an open Filters modal — the strip of tabs never could, the modal covered it. It
  // closes rather than stay up over a dashboard that may have no Filters at all.
  useEffect(() => { setShowFilterModal(false); }, [activeTab, activeCustomId]);
  // What the Attrition tab's last response said its filters should offer: the bands
  // the API actually counts, and where this viewer's leavers were last posted. Until
  // the first response the Tenure options are the hand-kept copy and the location
  // options are the staffed stores'. Replaced wholesale on every response and dropped
  // on a project switch, so another project's cities are never on offer.
  const [attritionFacets, setAttritionFacets] = useState<AttritionFacets | null>(null);
  const handleAttritionFacetsLoaded = useCallback((facets: AttritionFacets) => setAttritionFacets(facets), []);
  useEffect(() => { setAttritionFacets(null); }, [selectedProjectId]);
  // The count on the Filters button and in the modal header. The Tenure selection is
  // added here rather than in activeFilterCount because it applies on the Attrition
  // tab ONLY: counted page-wide it lit the badge on every other tab — whose modal has
  // no Tenure control to see or clear it — and turned the Deployment KPI cards to
  // "Matching filters" over numbers that were not filtered at all.
  // Data Hygiene takes the Role filter and nothing else (its modal offers Roles alone), so
  // its badge counts Role only: a store filter left on from Deployment does not reach it.
  const roleFilterOnly = activeTab === 'Data Hygiene';
  // A custom dashboard counts only the six filters it offers (see buildFilterSections).
  const customFilterCount = [selectedStaffingStatuses, selectedTrades, selectedStates, selectedCities, selectedAMs, selectedTradeHeads]
    .filter((list) => list.length > 0).length;
  const shownFilterCount = roleFilterOnly
    ? (selectedRoles.length ? 1 : 0)
    : activeTab === 'Custom'
      ? customFilterCount
      : activeFilterCount + (activeTab === 'Attrition' && selectedTenures.length ? 1 : 0);
  // Red Flag Rules switched on? (asked once, when the Red Flag tab first opens). Gates the
  // tab's Level filter: until the server says so, nothing about the tab changes.
  const redFlagRulesEnabled = useRedFlagRulesEnabled(activeTab === 'Red Flag');

  // Per-tab access. Defaults to ALL tabs (matches current behaviour + shown while
  // the user's menus load); narrowed to the role's granted Dashboard child menus.
  const [allowedTabs, setAllowedTabs] = useState<DashTab[]>([...ALL_DASH_TABS]);
  // Hiring is offered only where the server's own access answer says yes: its menu row is
  // the Hiring rights, and the project must have hiring switched on (dark until the server
  // says so). Asked once per person and project, shared with every hiring screen.
  const hiringAccess = useHiringAccess(selectedProjectId, hiringUserKeyOf(user));
  const shownTabs = useMemo(
    () => (hiringAccess.canView ? allowedTabs : allowedTabs.filter((t) => t !== 'Hiring')),
    [allowedTabs, hiringAccess.canView],
  );
  // The Hiring tab puts its buttons (Raise hiring, Link an opening, Open Manage Budgets) here,
  // beside the title, where the other dashboards keep their Month and Filters.
  const [hiringActionSlot, setHiringActionSlot] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const userId = user?.userCode?.toString() || user?.userName || user?.id;
    if (!userId) return;
    let cancelled = false;
    menusAPI
      .getUserMenus(userId)
      .then((res) => {
        if (cancelled) return;
        const menus: Array<{ code?: string }> = res.data?.data || [];
        // Fail-open (inside allowedDashTabs): Dashboard access but no per-tab children
        // yet → ALL tabs (never a blank dashboard). Super admins get every menu → all tabs.
        setAllowedTabs(allowedDashTabs(menus.map((m) => String(m.code || ''))));
      })
      .catch(() => { if (!cancelled) setAllowedTabs([...ALL_DASH_TABS]); });
    return () => { cancelled = true; };
  }, [user]);

  // ── Custom dashboards (user-built, prompt-generated) ──────────────────────
  // Building a new one starts from the home screen; this page still needs the list (for
  // the one that is open, and for "Other dashboards") and whether this person may edit.
  const [customDashboards, setCustomDashboards] = useState<CustomDashboard[]>([]);
  const [canAuthorDashboards, setCanAuthorDashboards] = useState(false);
  // False until the list has answered once (or failed): an empty list before then does
  // not mean the dashboard in the address is gone.
  const [customDashboardsLoaded, setCustomDashboardsLoaded] = useState(false);

  const reloadCustomDashboards = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const [access, list] = await Promise.all([
        customDashboardsAPI.access(selectedProjectId),
        customDashboardsAPI.list(selectedProjectId),
      ]);
      setCanAuthorDashboards(Boolean(access.data?.canAuthor));
      setCustomDashboards((list.data?.dashboards ?? []) as CustomDashboard[]);
    } catch {
      // Soft-fail: a dashboards outage must not take down the built-in tabs.
      setCanAuthorDashboards(false);
      setCustomDashboards([]);
    }
    setCustomDashboardsLoaded(true);
  }, [selectedProjectId]);

  useEffect(() => { void reloadCustomDashboards(); }, [reloadCustomDashboards]);

  const activeCustomDashboard = useMemo(
    () => customDashboards.find((d) => d.id === activeCustomId) ?? null,
    [customDashboards, activeCustomId],
  );

  // An address this page cannot show sends the person to the home screen, where every
  // dashboard they hold is listed — never a blank page, and never a different dashboard
  // from the one they asked for (the page used to switch silently to the first tab held).
  //  • It names no dashboard: a mistyped or retired address.
  //  • It names a built-in dashboard this person does not hold. Nobody is sent away
  //    before their menus have answered, or if they cannot be read: allowedTabs is every
  //    dashboard until then, and is fail-open for a role with no per-dashboard menus.
  //  • It names a custom dashboard that is gone (deleted, unpublished, or the project
  //    was switched). Decided only once the list has loaded.
  //  • It names a custom dashboard and no project is selected. They belong to a project,
  //    so without one the list is never asked for: waiting for it would hold the title's
  //    place for ever. (A built-in dashboard stays open without a project, as before.)
  //  • It names Hiring, and the server has said this person may not see hiring in this
  //    project (switched off, or no Hiring tick). Not while it has not answered.
  const leaveForHome =
    activeTab === null ||
    (activeTab === 'Custom'
      ? activeCustomId === null || !selectedProjectId || (customDashboardsLoaded && !activeCustomDashboard)
      : !allowedTabs.includes(activeTab) || (activeTab === 'Hiring' && hiringAccess.state === 'no'));

  // A dashboard the team built is known to the shell only by the id in its address, so
  // this page tells it the name, for the trail and the browser tab. Null until the name
  // is known, on a built-in dashboard (the shell names those itself) and on leaving.
  const openCustomTitle = (activeTab === 'Custom' && activeCustomDashboard?.title) || null;
  useEffect(() => {
    const { setTitle } = useCurrentPageStore.getState();
    setTitle(openCustomTitle);
    return () => setTitle(null);
  }, [openCustomTitle]);

  // Selectable months — the current month is offered on EVERY tab, labelled "(current)".
  //
  // Deployment used to skip it, because its figures come from the scorecard and a
  // part-way-through month is only partly scored. It is now offered by request, so that
  // the month-to-date position is visible rather than having to wait for the 1st.
  //
  // Read a "(current)" Deployment month as PROVISIONAL, not final. It moves in both
  // directions: target metrics whose values have not been uploaded yet contribute
  // 0 points × their weight (pulling scores DOWN), while count-based metrics such as
  // attendance normalise against the whole calendar month even though data only runs to
  // today (pushing them UP). It settles once the month completes and the nightly
  // snapshot re-runs. In particular, do not action Bottom Box off a partial month —
  // that list feeds PIP and exit initiation.
  const monthOptions = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const label = d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      out.push({ value, label: i === 0 ? `${label} (current)` : label });
    }
    return out;
  }, []);

  // EWS shows one FINISHED month (and reads the one before it to compare), so its Month
  // offers the last three finished months, each by name ("Oct 2026"); further back, every
  // 1Office read for it gets longer (the server goes no further either). The Month stays the page's one
  // value: on EWS a month it does not offer reads as the last finished one, so moving
  // between dashboards never loses the choice.
  const ewsMonthOptions = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [];
    const now = new Date();
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      out.push({ value, label: monthLabel(value.slice(0, 7)) });
    }
    return out;
  }, []);
  const ewsEndMonth = ewsMonthOptions.some((o) => o.value === periodMonth) ? periodMonth : ewsMonthOptions[0].value;

  // Guard against a FUTURE month only. The current month is legitimate everywhere now, but
  // store-intelligence's monthWindow returns null for a strictly future month, which would
  // empty the people roster and — with a Role filter on — collapse the store list to zero.
  useEffect(() => {
    const now = new Date();
    const currentValue = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    if (periodMonth > currentValue) setPeriodMonth(currentValue);
  }, [periodMonth]);

  // Deployment tab shows ONE report table at a time, chosen from a selector at the
  // top of the tab — so the user picks the table they want instead of scrolling
  // past all three (Store Performance / Bottom Box / Undeployed Stores).
  //
  // Which one is kept in the address as ?list=<slug>, so the home screen (and a
  // colleague's link) can land on, say, Undeployed Stores. A list named in the address
  // always wins. Picking another list REPLACES the address rather than adding a step to
  // the browser's Back, and leaves every other value in the query as it was.
  //
  // It is the same ?list every dashboard keeps its open list in (useDashboardList), each
  // with its own slugs. So it is read as one of Deployment's ONLY on Deployment's own
  // address: another dashboard's slug, even one spelt like ours, is never taken for the
  // list that is open here. A slug Deployment does not know is ignored.
  //
  // No ?list = the list that was last open here. "Other dashboards" and page search open
  // Deployment's bare address (and Back can return to it), and when Deployment was a tab
  // it was still on the list it had been left on. Remembered by the page alone, for as long
  // as it stays mounted: arriving from the home screen is a fresh page, so that opens
  // Store Performance.
  const { list: listInAddress, openList } = useDashboardList();
  const lastDeploymentListRef = useRef<DeploymentList>('Store Performance');
  const deploymentTable: DeploymentList =
    (activeTab === 'Deployment' ? deploymentListFromSlug(listInAddress) : null) ?? lastDeploymentListRef.current;
  useEffect(() => {
    if (activeTab === 'Deployment') lastDeploymentListRef.current = deploymentTable;
  }, [activeTab, deploymentTable]);
  const setDeploymentTable = useCallback(
    (list: DeploymentList) => openList(DEPLOYMENT_LIST_SLUG[list]),
    [openList],
  );
  // The headline numbers sit above the list strip, so the list they open can be below the
  // fold. They bring the strip to the top; the strip's own buttons, which are already in
  // view, do not move the page.
  const deploymentListsRef = useRef<HTMLDivElement>(null);
  const openDeploymentList = useCallback((list: DeploymentList) => {
    setDeploymentTable(list);
    // Guarded: jsdom has no scrollIntoView, and neither do some older browsers.
    deploymentListsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [setDeploymentTable]);

  const fetchStores = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const loadingFor = selectedProjectId ?? null;
    // A refresh of the project already on screen keeps its rows; a first load, another
    // project or a retry after a failure has nothing trustworthy to show meanwhile.
    setRedeployPendingLoad((prev) =>
      prev.state === 'ready' && prev.projectId === loadingFor ? prev : { projectId: loadingFor, state: 'loading' });
    try {
      const [storesResponse, employeesResponse] = await Promise.all([
        storesAPI.getAll(selectedProjectId!),
        employeesAPI.getProjectEmployees(true, selectedProjectId ?? undefined),
      ]);

      const empResponseData = employeesResponse.data?.data || employeesResponse.data || {};
      const empMeta = employeesResponse.data?.meta || {};
      const deployed = empResponseData.deployed || [];
      const deployedCount = empMeta.deployedCount || deployed.length;
      setEmployeesDeployedCount(deployedCount);

      const payload = (storesResponse.data?.data ?? storesResponse.data) as ApiStoreRecord[];
      // Closed stores are not vacancies. GET /stores returns EVERY store in the project
      // (no store_status filter — see getAllStores in stores.controller.ts), so a
      // deactivated store was landing in "Total Stores" and, having nobody deployed,
      // in the "Undeployed Stores / No employee deployed" card — where it reads as a
      // store someone needs to staff. On iPro that was 39 of 2,539 stores, a meaningful
      // share of a 158-store vacancy list.
      //
      // Filtered HERE rather than in the endpoint on purpose: /stores has five callers
      // and Master Data → Stores exists precisely to show Inactive ones, so a
      // server-side filter would have to be opt-in anyway. This keeps the blast radius
      // to the dashboard.
      //
      // A DENYLIST, not an allowlist — this is the important bit. `store_status` is
      // VARCHAR(50) with NO check constraint (schema.sql:47), and the STORE_STATUS
      // constant documents only Active/Inactive — but production also carries
      // 'Notice' (a store serving notice: closing soon, still trading, still staffed).
      // An allowlist of 'Active' would have dropped it, and would silently drop every
      // future status somebody adds. So we exclude only what definitively means CLOSED
      // and keep everything else, which preserves the old count-everything behaviour
      // for any value we haven't seen.
      //
      // Blank counts as open: the column is nullable on older rows and defaults to
      // 'Active' on insert, so treating unknown as closed would shrink every number.
      // The list itself lives in lists/deployment, beside the rest of the rule for
      // "stores short of people", so the server's copy can be tested against one place.
      const isOpenStore = (s: ApiStoreRecord): boolean => !isClosedStoreStatus(s.store_status);
      // Closed stores are KEPT, tagged in closedIds, rather than dropped here: a past month's
      // Deployment view can include a store that has closed since. The live view still leaves
      // them out — see `stores`.
      const rows = Array.isArray(payload) ? payload : [];
      setRedeployPendingAll(deriveRedeployPendingRows(rows));
      setRedeployPendingLoad({ projectId: loadingFor, state: 'ready' });
      const normalizedAll = rows.map(normalizeStoreRecord);
      const closedIds = new Set(normalizedAll.filter((_, i) => !isOpenStore(rows[i])).map((s) => s.id));
      const normalizedStores = normalizedAll.filter((s) => !closedIds.has(s.id));
      if (normalizedStores.length === 0) {
        setAllStores([]);
        setClosedStoreIds(new Set());
        setError('No live records found.');
        setLastUpdated(null);
      } else {
        setAllStores(normalizedAll);
        setClosedStoreIds(closedIds);
        setError(null);
        setLastUpdated(new Date().toISOString());
      }
    } catch (err) {
      console.error('Failed to fetch stores', err);
      setAllStores([]);
      setClosedStoreIds(new Set());
      setRedeployPendingAll([]);
      setRedeployPendingLoad({ projectId: loadingFor, state: 'failed' });
      setError('Unable to reach store analytics API.');
      setLastUpdated(null);
      setEmployeesDeployedCount(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Store-intelligence summary — independent fetch; refetches when the project
  // or the month under review changes.
  useEffect(() => {
    if (!selectedProjectId) { setIntelligence(null); return; }
    let cancelled = false;
    const loadingFor = { projectId: selectedProjectId, month: periodMonth };
    setIntelligenceLoad((prev) =>
      prev.state === 'ready' && prev.projectId === loadingFor.projectId && prev.month === loadingFor.month
        ? prev
        : { ...loadingFor, state: 'loading' });
    storeIntelligenceAPI.getSummary(selectedProjectId, periodMonth, { monthEnd: true })
      .then((res) => {
        if (cancelled) return;
        const summary = (res.data?.data ?? null) as StoreIntelligenceSummary | null;
        setIntelligence(summary);
        setIntelligenceLoad({ ...loadingFor, state: summary ? 'ready' : 'failed' });
      })
      .catch(() => {
        if (cancelled) return;
        setIntelligence(null);
        setIntelligenceLoad({ ...loadingFor, state: 'failed' });
      });
    return () => { cancelled = true; };
  }, [selectedProjectId, periodMonth, refreshNonce]);
  // Read against the project and month on screen, so a summary still held from the ones
  // just left is never taken for this one's. With no project nothing is on its way.
  const intelligenceState: 'loading' | 'ready' | 'failed' =
    !selectedProjectId
      ? 'failed'
      : intelligenceLoad.projectId === selectedProjectId && intelligenceLoad.month === periodMonth
        ? intelligenceLoad.state
        : 'loading';

  // Bottom Box (bottom 3%) — same anchor month as the summary above.
  useEffect(() => {
    if (!selectedProjectId) { setBottomBox(null); return; }
    let cancelled = false;
    storeIntelligenceAPI.getBottomBox(selectedProjectId, periodMonth, { monthEnd: true })
      .then((res) => {
        if (cancelled) return;
        setBottomBox((res.data?.data ?? null) as BottomBoxData | null);
      })
      .catch(() => { if (!cancelled) setBottomBox(null); });
    return () => { cancelled = true; };
  }, [selectedProjectId, periodMonth, refreshNonce]);

  // Undeployed (active) project employees — count + list for the KPI card and the
  // "Undeployed Employees" Show tab. Soft-fails to []. Shares refreshNonce so it
  // updates immediately after a redeployment / NSW action.
  useEffect(() => {
    if (!selectedProjectId) { setUndeployedEmpsLoad({ projectId: null, rows: [] }); return; }
    let cancelled = false;
    storesAPI.getUndeployedEmployees(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        setUndeployedEmpsLoad({ projectId: selectedProjectId, rows: res.data?.data ?? [] });
      })
      .catch(() => { if (!cancelled) setUndeployedEmpsLoad({ projectId: selectedProjectId, rows: [] }); });
    return () => { cancelled = true; };
  }, [selectedProjectId, refreshNonce]);
  // Read against the project on screen (as redeployPendingState is): after a project
  // switch the rows held are still the last project's until this one's list answers, and
  // the tile, the pill and the table would all show its people under this project's
  // name. A refresh of the same project keeps its rows meanwhile.
  const undeployedEmps = useMemo(
    () => (undeployedEmpsLoad.projectId === (selectedProjectId ?? null) ? undeployedEmpsLoad.rows : []),
    [undeployedEmpsLoad, selectedProjectId],
  );

  // The project's "Staffing Type" store custom field, if it defines one — drives the
  // Staffing Type column on the Undeployed Stores tab. Matched by LABEL because field
  // ids are per-project UUIDs (active labels are unique per project, so the match is
  // unambiguous). Soft-fails to null, which simply hides the column.
  const [staffingTypeField, setStaffingTypeField] = useState<StoreCustomField | null>(null);
  useEffect(() => {
    if (!selectedProjectId) { setStaffingTypeField(null); return; }
    let cancelled = false;
    storeCustomFieldsAPI.list(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        const defs = (res.data?.data ?? []) as StoreCustomField[];
        setStaffingTypeField(defs.find((f) => (f.label || '').trim().toLowerCase() === 'staffing type') ?? null);
      })
      .catch(() => { if (!cancelled) setStaffingTypeField(null); });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // NSW exit ledger → per-store map. A store shows an "NSW" marker while it has
  // an unfilled slot that an NSW exit vacated (see showNsw in the row render).
  // Shares refreshNonce so it updates immediately after an NSW / redeployment
  // action. Not month-scoped — the exit ledger is a running record.
  useEffect(() => {
    if (!selectedProjectId) { setNswByStore(new Map()); return; }
    let cancelled = false;
    storeIntelligenceAPI.getExitInitiations(selectedProjectId)
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data?.data ?? []) as NswExitRow[];
        const map = new Map<string, NswExitRow[]>();
        for (const r of rows) {
          if (!r.storeId) continue;
          const list = map.get(r.storeId) ?? [];
          list.push(r);
          map.set(r.storeId, list);
        }
        setNswByStore(map);
      })
      .catch(() => { if (!cancelled) setNswByStore(new Map()); });
    return () => { cancelled = true; };
  }, [selectedProjectId, refreshNonce]);

  /** Intelligence signals for one store row (keyed by the store's uuid). */
  const storeIntel = useCallback(
    (store: StoreAnalytics) => intelligence?.stores?.[store.id] ?? null,
    [intelligence]
  );

  // Monotonic request id — the latest fetch wins. Rapid radius drags or an
  // employee switch can start overlapping requests; without this the older (and
  // potentially differently-gated) response could resolve last and clobber the list.
  const redeployReqRef = useRef(0);
  // Load nearby redeployment candidates for the open insights store.
  const loadRedeployCandidates = useCallback(async (radiusKm: number) => {
    if (!insightsStore || !selectedProjectId) return;
    const reqId = ++redeployReqRef.current;
    setRedeployLoading(true);
    try {
      // Pass the employee being redeployed (source A) so the backend gates the
      // candidate stores to incumbents weaker-or-equal to A (At Risk, or a lower
      // 6-mo avg) — never surface a store held by a stronger performer.
      const res = await scorecardAPI.getRedeployCandidates(selectedProjectId, insightsStore.id, radiusKm, periodMonth, redeployEmp?.code, eligibilityMode ? 'eligibility' : undefined);
      if (reqId !== redeployReqRef.current) return; // superseded by a newer fetch
      setRedeployCandidates((res.data?.data ?? []) as RedeployCandidate[]);
      setRedeployExcludedUncat(Number(res.data?.meta?.excluded_uncategorised) || 0);
      setRedeployFinancialsVisible(Boolean(res.data?.meta?.financials_visible));
      setSourceStoreFinancials((res.data?.meta?.source_store_financials ?? null) as SourceStoreFinancials | null);
      setRedeployRadiusKm(radiusKm);
      // NOTE: do NOT setRadiusDraft here — radiusDraft is the live slider value
      // owned by the range input's onChange. Writing it on fetch-resolve snaps the
      // thumb backward if the user has already started a new drag.
    } catch {
      if (reqId !== redeployReqRef.current) return;
      setRedeployCandidates([]);
      setRedeployExcludedUncat(0);
      setRedeployFinancialsVisible(false);
      setSourceStoreFinancials(null);
    } finally {
      if (reqId === redeployReqRef.current) setRedeployLoading(false);
    }
  }, [insightsStore, selectedProjectId, periodMonth, redeployEmp, eligibilityMode]);

  // Helper functions to get unique values for multi-select dropdowns
  const getUniqueTrades = useCallback(() => {
    return stores
      .map(store => store.trade)
      .filter((trade, index, arr) => trade && trade.trim() !== '' && arr.indexOf(trade) === index)
      .sort();
  }, [stores]);

  // Load the 1Office state master once, purely for its region mapping. Fails soft: if
  // the call fails the Region filter simply offers nothing, and every other filter and
  // table on the dashboard is unaffected.
  useEffect(() => {
    let cancelled = false;
    locationsAPI
      .getStates()
      .then((res) => {
        const rows = res.data?.status === 'success' && Array.isArray(res.data.data) ? res.data.data : [];
        if (!cancelled) setApiStates(rows);
      })
      .catch((err) => console.error('Failed to fetch states for region filter:', err?.message));
    return () => { cancelled = true; };
  }, []);

  // Authoritative region per state, keyed by normalized state name so a dirty store
  // string ("KERALA", " Kerala", "Jammu & Kashmir") still resolves to its master row.
  const regionByStateKey = useMemo(() => buildRegionByStateKey(apiStates), [apiStates]);

  // A store's region, or undefined when its state has no master row.
  const regionForState = useCallback(
    (stateRaw?: string | null): string | undefined => regionForStateName(regionByStateKey, stateRaw),
    [regionByStateKey]
  );

  // The selected Regions expanded to their member states, as a CSV for the server-side
  // tabs. Sends the CANONICAL master spellings; the backend folds both sides through
  // normalizeLocationKey, so a store saved as "KERALA" still matches "Kerala".
  const regionStatesParam = useMemo(() => {
    if (!selectedRegions.length) return undefined;
    const wanted = new Set(selectedRegions);
    const names = apiStates
      .filter((s) => s.regionName && wanted.has(s.regionName))
      .map((s) => (s.stateName || '').trim())
      .filter(Boolean);
    return names.length ? names.join(',') : undefined;
  }, [apiStates, selectedRegions]);

  // Regions actually present in the loaded stores — no empty buckets in the dropdown.
  const getUniqueRegions = useCallback(() => {
    const present = new Set<string>();
    for (const s of stores) {
      const r = regionForState(s.state);
      if (r) present.add(r);
    }
    return [...present].sort();
  }, [stores, regionForState]);

  const getUniqueStates = useCallback(() => {
    return stores
      .map(store => store.state)
      .filter((state, index, arr) => state && state.trim() !== '' && arr.indexOf(state) === index)
      .sort();
  }, [stores]);

  const getUniqueCities = useCallback(() => {
    const stateFilteredCities = selectedStates.length > 0
      ? stores.filter(store => selectedStates.includes(store.state || '')).map(store => store.city)
      : stores.map(store => store.city);
    return stateFilteredCities
      .filter((city, index, arr) => city && city.trim() !== '' && arr.indexOf(city) === index)
      .sort();
  }, [stores, selectedStates]);

  const getUniqueCategories = useCallback(() => {
    return stores
      .map(store => store.category)
      .filter((cat, index, arr) => cat && cat.trim() !== '' && arr.indexOf(cat) === index)
      .sort();
  }, [stores]);

  // Unique AMs / Trade Heads across all stores (from the store-intelligence
  // people lens). Keyed by code, displayed by name.
  const getUniqueAMs = useCallback(() => {
    const map = new Map<string, string>();
    for (const s of stores) {
      for (const am of intelligence?.stores?.[s.id]?.people?.ams ?? []) {
        if (am.code) map.set(am.code, am.name || am.code);
      }
    }
    return [...map.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [stores, intelligence]);

  const getUniqueTradeHeads = useCallback(() => {
    const map = new Map<string, string>();
    for (const s of stores) {
      for (const th of intelligence?.stores?.[s.id]?.people?.tradeHeads ?? []) {
        if (th.code) map.set(th.code, th.name || th.code);
      }
    }
    return [...map.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [stores, intelligence]);

  // Distinct roles (designations) across all stores' deployed employees (from the
  // store-intelligence people lens). Plain string list — role name is its own value.
  // All project role NAMES (from the roles table via projectRoles) — the shared Role
  // dropdown lists EVERY role, not just those with a currently-deployed employee.
  const allRoleNames = useMemo(
    () => [...new Set(projectRoles.map((r) => r.name.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [projectRoles],
  );

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  // Where the store-level Role default stands (see below): the project it has been decided
  // for, and whether it was worked out there (`applied`) or only ruled out because one of
  // its two inputs could not be read, which a later answer may still overturn.
  const [roleDefault, setRoleDefault] = useState<{ projectId: number | null; applied: boolean }>({ projectId: null, applied: false });

  // Fetch the project's roles (id ↔ name ↔ ladder category) so the shared Role filter
  // — which tracks role NAMES — can be translated into the role IDs the server-side
  // tabs need, and so the store-level default below can be computed.
  useEffect(() => {
    if (!selectedProjectId) { setProjectRoles([]); return; }
    let alive = true;
    rolesAPI
      .getByProject(selectedProjectId)
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.data ?? []) as Array<{ id: string | number; name: string; category?: string | null }>;
        const mapped = list.map((r) => ({
          id: String(r.id),
          name: r.name,
          category: String(r.category ?? '').trim(),
        }));
        setProjectRoles(mapped);
        setProjectRolesFor(selectedProjectId);
      })
      .catch(() => {
        if (!alive) return;
        setProjectRoles([]);
        setProjectRolesFor(selectedProjectId);
      });
    return () => { alive = false; };
  }, [selectedProjectId]);

  // Default the Role filter to the roles that actually WORK AT A STORE, once per project.
  //
  // Which roles those are is storeLevelRoleNames (navigation/dashboards): an L1 role
  // with at least one employee deployed to a store. The home screen counts the same
  // people through the same function, so its numbers match the dashboard they open.
  //
  // Waits for BOTH inputs, and for both to be THIS project's: after a project switch the
  // roles and the summary held are the last project's until its own arrive, and a default
  // worked out from those would be the wrong project's. A slow intelligence fetch
  // cannot burn the one attempt and leave no default at all.
  //
  // Names come back TRIMMED to match `allRoleNames` (the dropdown trims too). An untrimmed
  // name would be counted in the badge while matching no option — "4 selected" with
  // only 2 ticks — and would resolve to no role id for the server-side tabs.
  //
  // Fails OPEN: nothing matched (or intelligence never loaded) leaves the filter
  // empty — everyone — rather than filtering the dashboard down to nothing.
  //
  // Either way the project is then DECIDED, which is what the role-filtered dashboards
  // wait for (`roleDefaultSettled`). So it must always come to a decision:
  //  • both inputs read: worked out, and applied when a role qualifies;
  //  • the project has no roles, or they could not be read: ruled out at once, since
  //    without them no name can become the id a dashboard is handed;
  //  • the summary could not be read: ruled out once the roles have answered. It is
  //    asked for again when the month changes, and if that answer can be read the
  //    default is worked out then, as it always was.
  useEffect(() => {
    if (!selectedProjectId) return;
    const decided = roleDefault.projectId === selectedProjectId;
    if (decided && roleDefault.applied) return;
    if (projectRolesFor !== selectedProjectId) return;
    if (projectRoles.length > 0 && intelligenceState === 'ready') {
      const storeLevel = storeLevelRoleNames(projectRoles, intelligence);
      if (storeLevel.length > 0) setSelectedRoles(storeLevel);
      setRoleDefault({ projectId: selectedProjectId, applied: true });
    } else if (!decided && (projectRoles.length === 0 || intelligenceState === 'failed')) {
      setRoleDefault({ projectId: selectedProjectId, applied: false });
    }
  }, [selectedProjectId, roleDefault, projectRoles, projectRolesFor, intelligence, intelligenceState]);
  // True once the default Role filter has been applied or ruled out for the project on
  // screen; a project switch makes it false again until that project is decided.
  //
  // Until then the role-filtered dashboards are not drawn, so they ask for nothing. Asked
  // at once they would be handed no role, and when that answer landed first the dashboard
  // (its numbers, and a list the home screen opened by address) showed everyone for
  // a moment, more people than the home screen's number promised, before the filter
  // arrived and it asked again. With no project there is nothing to wait for: each
  // dashboard says "Select a project" itself.
  const roleDefaultSettled = !selectedProjectId || roleDefault.projectId === selectedProjectId;
  const awaitingRoleDefault =
    !roleDefaultSettled && activeTab !== null && activeTab !== 'Custom' && ROLE_FILTERED_TABS.has(activeTab);

  // Budget filter options — the per-project 1Office budget list (budgetId ↔ name),
  // the same source Project Management's budget filter uses. Reloads on project change.
  useEffect(() => {
    if (!selectedProjectId) { setBudgetOptions([]); return; }
    let alive = true;
    projectsAPI
      .getActiveBudgets(selectedProjectId)
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.data ?? []) as Array<{ budgetId: number | string; budgetName: string }>;
        // Match on budget NAME, not budgetId: the CRS payroll fetch that carries budgetId
        // 500s for large projects, so the backend resolves emp→budgetName instead. Dedupe
        // by name (1Office can list the same budget name twice with different ids).
        const seen = new Set<string>();
        const opts: { value: string; label: string }[] = [];
        for (const b of list) {
          const name = (b.budgetName || '').trim();
          if (name && !seen.has(name)) { seen.add(name); opts.push({ value: name, label: name }); }
        }
        setBudgetOptions(opts);
      })
      .catch(() => { if (alive) setBudgetOptions([]); });
    return () => { alive = false; };
  }, [selectedProjectId]);

  // The selected role NAMES resolved into role IDs (for the Attendance backend filter).
  const selectedRoleIds = useMemo(() => {
    // Trimmed both sides: `allRoleNames` (what the user picks) trims, so an untrimmed
    // roles-table name would resolve to NO id here — the server-side tabs would then
    // run unfiltered while the Deployment tab filtered, with nothing on screen saying so.
    const want = new Set(selectedRoles.map((n) => n.trim().toLowerCase()).filter(Boolean));
    return projectRoles.filter((r) => want.has(r.name.trim().toLowerCase())).map((r) => r.id);
  }, [projectRoles, selectedRoles]);

  const handleAMToggle = (code: string) => {
    setSelectedAMs(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const handleTradeHeadToggle = (code: string) => {
    setSelectedTradeHeads(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  // Toggle handlers for multi-select dropdowns
  const handleTradeToggle = (trade: string) => {
    setSelectedTrades(prev => prev.includes(trade) ? prev.filter(t => t !== trade) : [...prev, trade]);
  };

  const handleStateToggle = (state: string) => {
    setSelectedStates(prev => {
      const newStates = prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state];
      // Clear cities that don't belong to selected states
      if (newStates.length > 0) {
        const validCities = stores
          .filter(store => newStates.includes(store.state || ''))
          .map(store => store.city)
          .filter(city => city);
        setSelectedCities(prev => prev.filter(city => validCities.includes(city)));
      }
      return newStates;
    });
  };

  const handleCityToggle = (city: string) => {
    setSelectedCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };

  // Store-ATTRIBUTE filters (code / name / trade / state / city / category). These
  // describe the STORE itself, so they apply whether or not anyone is deployed there.
  const storeMatchesAttrs = useCallback((store: StoreAnalytics): boolean => {
    const matchesStoreCode = filters.storeCode
      ? store.storeCode.toLowerCase().includes(filters.storeCode.toLowerCase())
      : true;
    const matchesTrade = selectedTrades.length > 0 ? selectedTrades.includes(store.trade) : true;
    const matchesStoreName = filters.storeName
      ? store.name.toLowerCase().includes(filters.storeName.toLowerCase())
      : true;
    const matchesState = selectedStates.length > 0 ? selectedStates.includes(store.state || '') : true;
    // Region resolves through the 1Office master, so it matches on the NORMALIZED state
    // name — a store saved as "KERALA" still counts as South. A store whose state has no
    // master row matches no region, so it drops out only when a region is actually chosen.
    const region = selectedRegions.length > 0 ? regionForState(store.state) : undefined;
    const matchesRegion = selectedRegions.length > 0 ? !!region && selectedRegions.includes(region) : true;
    const matchesCity = selectedCities.length > 0 ? selectedCities.includes(store.city || '') : true;
    const matchesCategory = selectedCategories.length > 0 ? selectedCategories.includes(store.category || '') : true;
    return matchesStoreCode && matchesTrade && matchesStoreName && matchesState && matchesRegion && matchesCity && matchesCategory;
  }, [filters, selectedTrades, selectedStates, selectedRegions, regionForState, selectedCities, selectedCategories]);

  // Same trimmed comparison as roleMatches, declared early: filteredStores runs
  // before roleMatches exists.
  const roleMatchesName = useCallback(
    (roleName?: string | null): boolean => {
      if (selectedRoles.length === 0) return true;
      const name = (roleName || '').trim().toLowerCase();
      return !!name && selectedRoles.some((r) => r.trim().toLowerCase() === name);
    },
    [selectedRoles],
  );

  const filteredStores = useMemo(() => {
    return stores.filter((store) => {
      if (!storeMatchesAttrs(store)) return false;
      // Employee-based filters (AM / Trade Head / Role) — a store matches if any of
      // its DEPLOYED employees satisfies them. Stores with nobody deployed never
      // match an employee filter (expected for the Store Performance table).
      const ppl = intelligence?.stores?.[store.id]?.people;
      const matchesAM = selectedAMs.length > 0
        ? (ppl?.ams ?? []).some((a) => selectedAMs.includes(a.code))
        : true;
      const matchesTradeHead = selectedTradeHeads.length > 0
        ? (ppl?.tradeHeads ?? []).some((t) => selectedTradeHeads.includes(t.code))
        : true;
      const matchesRole = selectedRoles.length > 0
        ? (ppl?.employees ?? []).some((e) => roleMatchesName(e.roleName))
        : true;
      // Standing (6-mo quadrant) + employee name/code — a store matches if ANY of
      // its deployed employees satisfies the filter.
      const matchesStanding = selectedStandings.length > 0
        ? (ppl?.employees ?? []).some((e) => e.quadrant && selectedStandings.includes(e.quadrant))
        : true;
      const q = empNameQuery.trim().toLowerCase();
      const matchesEmpName = q
        ? (store.deployedEmployees ?? []).some((e) => (e.name || '').toLowerCase().includes(q))
        : true;
      const qc = empCodeQuery.trim().toLowerCase();
      const matchesEmpCode = qc
        ? (store.deployedEmployees ?? []).some((e) => (e.code || '').toLowerCase().includes(qc))
        : true;
      return matchesAM && matchesTradeHead && matchesRole && matchesStanding && matchesEmpName && matchesEmpCode;
    });
  }, [stores, storeMatchesAttrs, selectedAMs, selectedTradeHeads, selectedRoles, selectedStandings, empNameQuery, empCodeQuery, intelligence]);

  /**
   * ACTIVE headcount for one raw store row — people still employed, per the store-intelligence
   * roster (which drops anyone who has EXITED in 1Office but still carries a stale
   * store_employees row).
   *
   * `store.employees` is the raw store_employees row count and is exit-BLIND, so a store whose
   * only employee left months ago still counted as deployed. Meanwhile every employee-level
   * filter reads the intelligence roster, which does not. That mismatch is what made a role
   * filter appear to delete ~53 stores: they were "deployed" for the card and empty for the
   * filter. Both sets now ask the same question.
   *
   * Falls back to the raw count while intelligence is still loading, so the page never briefly
   * renders every store as vacant.
   */
  const activeHeadcountOf = useCallback(
    (store: StoreAnalytics) =>
      intelligence
        ? (intelligence.stores?.[store.id]?.people?.employees?.length ?? 0)
        : store.employees,
    [intelligence],
  );

  /**
   * The live "who has exited" lookup failed server-side, so activeHeadcountOf above is
   * counting leavers. Every count built on it is wrong in a specific, silent direction:
   * deployed figures inflate, vacancy figures deflate. Observed on iPro 2026-09-09 —
   * 2432 / 2633 / 79 in this state vs the true 2411 / 2599 / 102 once the roster resolved.
   *
   * Only an explicit false counts: undefined means an older API that never sends the flag.
   */
  const rosterUnresolved = intelligence?.rosterResolved === false;

  // Month-end mode (a past month): the day the tab shows, and whether that day predates
  // deployment history (its picture is then approximate).
  const monthEndAsOf = intelligence?.asOfDate ?? null;
  const monthEndApproximate = !!monthEndAsOf && !!intelligence?.historyStartsOn && monthEndAsOf < intelligence.historyStartsOn;

  // Undeployed stores IGNORE the employee-based filters (Role / AM / Trade Head): a
  // store with nobody deployed can't match an employee attribute, so those filters
  // would wrongly empty this set. Store-attribute filters (geo / category / trade)
  // still apply. Aggregated by store_code so a multi-row store collapses to one.
  //
  // undeployedStoresBase is that set before the two filters only this list has (vacancy
  // age and staffing status); undeployedStoresList, below it, is what the table draws.
  const undeployedStoresBase = useMemo(() => {
    // BELOW the category minimum, not just empty: a store needing 2 (Apple L3) with only
    // 1 deployed is understaffed and belongs here too. min_employees defaults to 1, so every
    // other category keeps the old "0 deployed = undeployed" behaviour unchanged. Carry the
    // active count so the table can show the "1 / 2" deficit.
    // The rule itself (sum per store code, compare with the minimum) is storesShortOfPeople:
    // one function, tested against the cases the server's home-screen count is tested with.
    // No status is passed: `stores` is already the open ones, today's or the month-end's.
    const listed = stores.filter(storeMatchesAttrs);
    const storeIdsOf = storeIdsSharingCode(listed);
    // Only an Active store reaches the manager assigned to it (the Manager cell says so).
    const activeIds = new Set(listed.filter((store) => store.storeStatus === 'Active').map((store) => store.id));
    return storesShortOfPeople(
      listed,
      (store) => ({ code: store.storeCode, minimum: store.minEmployees, headcount: activeHeadcountOf(store) }),
    )
      .map((v) => ({
        ...v.store,
        activeHeadcount: v.headcount,
        // Every store row this one stands for: a manager assigned to the row goes to them all.
        storeIds: storeIdsOf(v.store),
        activeStoreIds: storeIdsOf(v.store).filter((id) => activeIds.has(id)),
        // GET /stores can't date a store whose only mapped employees have exited (their
        // stale rows still count as deployed there); the people lens dates it from their
        // last working day instead.
        // A past month (month-end mode) dates the vacancy as of its last day, from history —
        // GET /stores' date is today's, so it isn't used there.
        undeployedSince: intelligence?.asOfDate
          ? (v.headcount === 0 ? intelligence.vacantSince?.[v.store.id] ?? null : null)
          : v.store.undeployedSince ?? (v.headcount === 0 ? intelligence?.vacantSince?.[v.store.id] ?? null : null),
      }));
  }, [stores, storeMatchesAttrs, activeHeadcountOf, intelligence]);

  const undeployedStoresList = useMemo(() => {
    // The status the Staffing Status column shows; a store with none is "Not set".
    const list = selectedStaffingStatuses.length === 0
      ? undeployedStoresBase
      : undeployedStoresBase.filter((s) => matchesStaffingStatusFilter(s.staffingStatus, selectedStaffingStatuses));
    // "AON" here = how long the vacancy has been open — for an empty store since it
    // emptied, for an understaffed one since it fell below its minimum. Banded off the
    // SAME value the cell renders (aonSinceOf), so filtering can't disagree with the
    // column: before, understaffed rows had no date and every band silently dropped them.
    if (selectedVacancyBands.length === 0) return list;
    return list.filter((s) => {
      const understaffed = (s.activeHeadcount ?? 0) > 0;
      const b = undeployedAonBandOf(aonSinceOf(s, understaffed, intelligence?.asOfDate), intelligence?.asOfDate);
      return b != null && selectedVacancyBands.includes(b);
    });
  }, [undeployedStoresBase, selectedStaffingStatuses, selectedVacancyBands, intelligence]);

  // The managers a vacant store lies under (Trade Heads and AMs), assigned on this list (the
  // Manager column, the tick boxes and "Assign manager"). Read only while the list is on screen.
  const vacancyManagers = useStoreVacancyManagers({
    shown: activeTab === 'Deployment' && deploymentTable === 'Undeployed Stores',
    ready: !isLoading && intelligenceState !== 'loading',
    projectId: selectedProjectId ?? null,
    month: periodMonth,
    asOf: monthEndAsOf,
    rows: undeployedStoresList,
  });

  // The Actions of Undeployed Stores (product owner, 16 Oct 2026: "link and open that particular
  // store or ... raise and hire on that respective store"). Open the store on the Stores page,
  // searched for it, for someone who holds that page; and Raise hiring (or Ask for hiring), Link
  // an opening and the store's hiring window, the Stores page's windows with its props, only
  // where the server shows this person hiring in this project. Nothing of hiring is asked for
  // until the list is on screen. The column is drawn when either applies. Where hiring is shown,
  // the Stores page's Hiring column comes before it, from the same read (product owner, the
  // same day: a store's linked opening opens where the store is, with its candidates); a link,
  // raise, ask or stop made from either column's windows shows on the row at once.
  const storesPage = useHeldPage(STORES_PAGE);
  const undeployedHiringStores = useMemo(() => undeployedStoresList.map(hiringRowStoreOf), [undeployedStoresList]);
  // A hiring closed as filled placed someone: the stores and the people are read again, as after a redeploy.
  const refreshAfterPlacing = useCallback(() => {
    fetchStores();
    setRefreshNonce((n) => n + 1);
  }, [fetchStores]);
  const undeployedHiring = useStoreRowHiring({
    projectId: selectedProjectId,
    userKey: hiringUserKeyOf(user),
    tableShown: activeTab === 'Deployment' && deploymentTable === 'Undeployed Stores',
    stores: undeployedHiringStores,
    onPlaced: refreshAfterPlacing,
    // A past month lists stores as they stood then; hiring is raised for a store as it is today.
    raiseHeld: monthEndAsOf ? STORE_ROW_HIRING_WORDS.pastMonth(formatDayLabel(monthEndAsOf)) : null,
  });
  const undeployedActionsShown = undeployedHiring.shown || !!storesPage;
  // When the redeploy cockpit's confirm is pressed, hiring at the store(s) someone joins is asked
  // about BEFORE the move or swap is saved, and the store a move left once it is (product owner,
  // 21 and 22 Oct 2026). No list on these tables shows hiring (Undeployed Stores reads its own
  // again when it is next shown); a hiring closed as filled from those windows placed someone,
  // and the stores and people are read again.
  const deployHiring = useDeployHiringCheck({
    projectId: selectedProjectId,
    userKey: hiringUserKeyOf(user),
    onPlaced: refreshAfterPlacing,
  });

  // Aggregate filtered stores by store_code to avoid duplicates in the table
  const aggregatedStores = useMemo(() => {
    const storeMap = new Map<string, StoreAnalytics>();
    
    filteredStores.forEach((store) => {
      const existing = storeMap.get(store.storeCode);
      if (!existing) {
        // First record for this store - clone it
        storeMap.set(store.storeCode, {
          ...store,
          employees: store.employees,
          monthlyCostValue: store.monthlyCostValue,
          deployedEmployees: [...store.deployedEmployees],
        });
      } else {
        // Aggregate: add employees and costs
        existing.employees += store.employees;
        existing.monthlyCostValue += store.monthlyCostValue;
        existing.monthlyCost = formatCurrency(existing.monthlyCostValue);
        // Merge deployed employees
        existing.deployedEmployees = [
          ...existing.deployedEmployees,
          ...store.deployedEmployees,
        ];
        // Recalculate profit/loss
        const representativePayout = existing.payoutMaxValue ?? existing.payoutMinValue ?? 0;
        const profit = representativePayout - existing.monthlyCostValue;
        existing.profitLossValue = `${profit >= 0 ? '+' : '−'}${formatCurrency(Math.abs(profit))}`.replace('−₹', '−₹').replace('+₹', '+₹');
        existing.profitLossPercent = existing.monthlyCostValue > 0 
          ? `${profit >= 0 ? '+' : '−'}${Math.abs((profit / existing.monthlyCostValue) * 100).toFixed(1)}%` 
          : '—';
        existing.profitStatus = profit >= 0 ? 'Profitable' : 'Loss Making';
        // Update compliance - store is compliant if any record shows compliance
        existing.compliance.aon = existing.compliance.aon || store.compliance.aon;
        existing.compliance.seed = existing.compliance.seed || store.compliance.seed;
      }
    });
    
    return Array.from(storeMap.values());
  }, [filteredStores]);

  // Downloading the MisDeployed tab: the rows on screen, each with a verdict on whether
  // a nearby swap would fix BOTH stores. The planning happens server-side because it
  // needs every store's coordinates and full roster, neither of which the dashboard
  // payload carries — so we post the rows we are showing and get a spreadsheet back.
  const [misDeployedExporting, setMisDeployedExporting] = useState(false);
  const handleMisDeployedExport = async () => {
    if (!selectedProjectId || misDeployedStores.length === 0) return;
    setMisDeployedExporting(true);
    try {
      // Only the employees actually driving each store's classification — the same ones
      // the row's Redeploy button targets. Sending the whole roster would ask the planner
      // to find swaps for people who are correctly placed.
      const items = misDeployedStores
        .map((m) => ({
          storeId: m.store.id,
          kind: m.kind,
          employeeCodes: m.employees.filter((e) => e.kind === m.kind).map((e) => e.code),
        }))
        .filter((i) => i.employeeCodes.length > 0);
      if (items.length === 0) return;

      const res = await scorecardAPI.exportMisdeployedReshuffle(selectedProjectId, {
        radiusKm: MISDEPLOYED_EXPORT_RADIUS_KM,
        // The grace these rows were listed with, so swap partners are judged by it too.
        aonGraceMonths,
        items,
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `misdeployed_reshuffle_${new Date().toISOString().slice(0, 10)}${aonGraceMonths > 0 ? `_grace-${aonGraceMonths}m` : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('MisDeployed reshuffle export failed', err);
      alert('Could not generate the reshuffle export. Please try again.');
    } finally {
      setMisDeployedExporting(false);
    }
  };

  // ── MisDeployed Stores ──
  // Stores where a deployed employee's tenure (AON) is the WRONG fit for the store
  // category's eligibility band:
  //   • under-qualified ("Misdeployed")   — AON below the category's requirement
  //   • over-qualified  ("Sub-optimal")   — AON already meets a HIGHER category's
  //                                          requirement (they'd fit a bigger store)
  // Correct band = [own requirement, next-higher requirement). Categories with no
  // criteria (Base / L0) never qualify. Derived client-side from the same filtered
  // store set the tables use, so the Role/attribute filters apply here too.
  // The AON grace period (header select) lowers the floor only: anyone within that many
  // months of the requirement counts as meeting it, so a store about to comply on its own
  // drops out. The over-qualified ceiling stays on actual AON — a grace only shortens the list.
  const misDeployedStores = useMemo(() => {
    const graceDays = aonGraceDays(aonGraceMonths);
    const thresholds = [...new Set(aggregatedStores.map((s) => eligibilityDays(s.eligibility)).filter((t) => t > 0))].sort((a, b) => a - b);
    const nextUp = (lo: number): number => thresholds.find((t) => t > lo) ?? Infinity;
    type MisEmp = {
      name: string; code: string; aonDays: number | null; aonLabel: string; kind: 'under' | 'over' | 'ok';
      /** Days until actual AON reaches the requirement; null once it has (or no DOJ). */
      daysToRequirement: number | null;
      /** 'ok' only because of the grace — still short of the requirement today. */
      withinGrace: boolean;
    };
    const out: { store: StoreAnalytics; reqLabel: string; kind: 'under' | 'over'; employees: MisEmp[] }[] = [];
    for (const store of aggregatedStores) {
      const lo = eligibilityDays(store.eligibility);
      if (lo <= 0) continue; // no eligibility criteria (Base / L0) → never misdeployed
      const roster = store.deployedEmployees ?? [];
      if (roster.length === 0) continue; // undeployed store belongs to the other tab
      const hi = nextUp(lo);
      const employees: MisEmp[] = roster.map((e) => {
        const aonDays = aonDaysFromDoj(e.dateOfJoining);
        const kind: MisEmp['kind'] = aonDays == null ? 'ok' : aonDays + graceDays < lo ? 'under' : aonDays >= hi ? 'over' : 'ok';
        const daysToRequirement = aonDays != null && aonDays < lo ? lo - aonDays : null;
        return {
          name: e.name, code: e.code, aonDays, aonLabel: aonLabelFromDays(aonDays), kind,
          daysToRequirement, withinGrace: kind === 'ok' && daysToRequirement != null,
        };
      });
      // A store counts as "Misdeployed" (under-staffed) ONLY when NO deployed
      // employee meets the requirement — one qualified employee covers the store
      // even if others fall short (an under-qualified extra isn't a staffing gap).
      // Over-qualified employees are still surfaced as "Sub-optimal" (a redeployment
      // opportunity), independent of coverage.
      const anyMeets = employees.some((e) => e.aonDays != null && e.aonDays + graceDays >= lo);
      const hasUnder = employees.some((e) => e.kind === 'under');
      const hasOver = employees.some((e) => e.kind === 'over');
      const kind = !anyMeets && hasUnder ? 'under' : hasOver ? 'over' : null;
      if (!kind) continue; // covered by a qualified employee and none over-qualified
      out.push({ store, reqLabel: eligibilityReqLabel(lo), kind, employees });
    }
    // Contextual MisDeployed filters — eligibility requirement, qualification fit
    // (Under = "Misdeployed" / Over = "Sub-optimal"), and employee tenure (AON).
    const filtered = out.filter((item) => {
      if (selectedEligibilities.length > 0 && !selectedEligibilities.includes(item.reqLabel)) return false;
      if (selectedFits.length > 0) {
        const fitLabel = item.kind === 'under' ? 'Under-qualified' : 'Over-qualified';
        if (!selectedFits.includes(fitLabel)) return false;
      }
      if (selectedAonBands.length > 0) {
        const anyBand = item.employees.some((e) => {
          const b = aonBandOf(e.aonDays);
          return b != null && selectedAonBands.includes(b);
        });
        if (!anyBand) return false;
      }
      return true;
    });
    // Under-qualified first (more urgent), then store name.
    return filtered.sort((a, b) => (a.kind === b.kind ? a.store.name.localeCompare(b.store.name) : a.kind === 'under' ? -1 : 1));
  }, [aggregatedStores, selectedEligibilities, selectedFits, selectedAonBands, aonGraceMonths]);

  // Display order — when a column sort is active it wins; otherwise the default
  // (At-Risk stores first, then lowest avg score; no-data stores at the bottom).
  // Stores that actually have someone deployed. ONE definition, shared by the Total Stores
  // KPI, the Store Performance tab count and the table itself — they previously computed
  // their own, so the card could read 2431 while its own tab read 2530 (the 99 undeployed).
  //
  // Undeployed stores are excluded from Store Performance on merit, not just for symmetry:
  // "store performance = the deployed employees' scorecard standing", so a store with nobody
  // posted to it has nothing to rank, and it already has its own Undeployed Stores tab.
  const activeHeadcountByCode = useMemo(() => {
    // Summed over filteredStores (the RAW rows) rather than aggregatedStores: aggregation
    // keeps only the FIRST row's id for a duplicated store_code, so an id-keyed intelligence
    // lookup after the fact would miss the other rows' people.
    const m = new Map<string, number>();
    for (const store of filteredStores) {
      m.set(store.storeCode, (m.get(store.storeCode) ?? 0) + activeHeadcountOf(store));
    }
    return m;
  }, [filteredStores, activeHeadcountOf]);

  const deployedStores = useMemo(
    () => aggregatedStores.filter((store) => (activeHeadcountByCode.get(store.storeCode) ?? 0) > 0),
    [aggregatedStores, activeHeadcountByCode],
  );

  const displayStores = useMemo(() => {
    // Store Performance-only "# employees deployed" filter (post-aggregation total).
    const base = selectedDeployedCounts.length > 0
      ? deployedStores.filter((s) => selectedDeployedCounts.includes(String(s.employees)))
      : deployedStores;
    if (!intelligence) return base;

    // Per-store value for a sortable column (numeric, or the text the cell shows).
    // Multi-employee stores use a representative aggregate (best standing, highest
    // tenure / score; the Employee column = the names in the order listed).
    const sortValueFor = (s: StoreAnalytics, key: StoreSortKey): number | string | null => {
      const people = intelligence.stores[s.id]?.people;
      switch (key) {
        case 'store':
          return s.name || null;
        case 'category':
          return s.category || null;
        case 'employee': {
          const names = (people?.employees ?? []).map((e) => e.employeeName || 'Unnamed');
          return names.length ? names.join(', ') : null;
        }
        case 'employees':
          return s.employees ?? 0;
        case 'score':
          return people?.avgScore ?? null;
        case 'avg6m':
          return people?.avg6mScore ?? null;
        case 'aon': {
          const days = (people?.employees ?? [])
            .map((e) => e.aonDays)
            .filter((d): d is number => typeof d === 'number');
          return days.length ? Math.max(...days) : null;
        }
        case 'standing': {
          const ranks = (people?.employees ?? []).map((e) => (e.quadrant ? (QUADRANT_RANK[e.quadrant] ?? 0) : 0));
          return ranks.length ? Math.max(...ranks) : null;
        }
        default:
          return null;
      }
    };

    if (sortBy) {
      const dir = sortDir === 'asc' ? 1 : -1;
      return [...base].sort((a, b) => {
        const va = sortValueFor(a, sortBy);
        const vb = sortValueFor(b, sortBy);
        if (va === null && vb === null) return 0;
        if (va === null) return 1; // no data always sinks to the bottom
        if (vb === null) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        // Text columns: locale-aware + numeric ("L2" < "L10"), same as useTableSort.
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir;
      });
    }

    const atRisk = (s: StoreAnalytics): boolean =>
      (intelligence.stores[s.id]?.people?.bottomBoxCount ?? 0) > 0;
    const score = (s: StoreAnalytics): number | null =>
      intelligence.stores[s.id]?.people?.avgScore ?? null;
    // Same rows as the sorted view (deployed stores + the "# employees deployed"
    // filter); only the order differs. It used to list aggregatedStores here, so the
    // table showed undeployed stores until a header was clicked and then dropped them.
    return [...base].sort((a, b) => {
      const ra = atRisk(a) ? 0 : 1;
      const rb = atRisk(b) ? 0 : 1;
      if (ra !== rb) return ra - rb;
      const ka = score(a);
      const kb = score(b);
      if (ka === null && kb === null) return 0;
      if (ka === null) return 1; // no data → bottom
      if (kb === null) return -1;
      return ka - kb; // ascending = worst first
    });
  }, [deployedStores, intelligence, sortBy, sortDir, selectedDeployedCounts]);

  // Apply the Role filter to the Bottom Box + Undeployed Employees lists so their
  // KPI cards, Show-tab pills, and tables all agree with the filtered store table.
  // One definition of "does this employee's role pass the Role filter" — see
  // roleMatchesName above (declared early for filteredStores). Two copies of the
  // trimmed comparison would drift.
  const roleMatches = roleMatchesName;
  /**
   * DISTINCT employees deployed across the currently-visible stores, honouring the
   * employee-level filters.
   *
   * ── WHY THIS EXISTS ──
   * The "Employees Deployed" card used to show two DIFFERENT METRICS under one
   * label: an authoritative distinct-employee count when unfiltered, but
   * `sum(store.employees)` once any filter was applied. That sum counts
   * DEPLOYMENT RECORDS, not people, and it inflated two ways at once:
   *   1. aggregatedStores merges duplicate store rows with `employees += ...` and
   *      concatenates deployedEmployees WITHOUT de-duplicating, so one person
   *      deployed at two stores (or listed twice for one store) counted twice.
   *   2. The Role filter is a STORE filter — a store is kept when ANY of its
   *      employees matches — and the sum then added EVERY employee at that store,
   *      including those of other roles.
   * Net effect: applying a filter could make the number go UP (observed: 2,569
   * unfiltered → 2,681 filtered on Role=iPro), which is impossible for a subset.
   *
   * Counting distinct employee codes fixes both, and makes the value monotonic —
   * a filter can now only ever narrow the set.
   *
   * Built from filteredStores rather than aggregatedStores on purpose:
   * aggregation collapses duplicate store CODES and keeps only the first row's
   * `id`, so the other rows' employees would be unreachable in the intelligence
   * map. De-duping by employee code afterwards makes the double-count harmless.
   *
   * AM / Trade Head are not re-checked per employee — they are not on this record
   * and filteredStores has already applied them at store level, matching how the
   * Store Performance table behaves.
   */
  const distinctDeployedCount = useMemo(() => {
    if (!intelligence) return null; // not loaded yet — caller falls back
    const q = empNameQuery.trim().toLowerCase();
    const qc = empCodeQuery.trim().toLowerCase();
    const seen = new Set<string>();
    for (const store of filteredStores) {
      const people = intelligence.stores?.[store.id]?.people?.employees ?? [];
      for (const e of people) {
        const code = (e.employeeCode || '').trim();
        if (!code) continue;
        if (!roleMatches(e.roleName)) continue;
        if (selectedStandings.length > 0 && !(e.quadrant && selectedStandings.includes(e.quadrant))) continue;
        if (q && !(e.employeeName || '').toLowerCase().includes(q)) continue;
        if (qc && !code.toLowerCase().includes(qc)) continue;
        seen.add(code);
      }
    }
    return seen.size;
  }, [filteredStores, intelligence, roleMatches, selectedStandings, empNameQuery, empCodeQuery]);

  const bottomBoxEmployees = useMemo(
    () =>
      (bottomBox?.employees ?? []).filter((e) => {
        if (!roleMatches(e.roleName)) return false;
        if (filters.storeName && !(e.storeName || '').toLowerCase().includes(filters.storeName.toLowerCase())) return false;
        if (filters.storeCode && !(e.storeCode || '').toLowerCase().includes(filters.storeCode.toLowerCase())) return false;
        if (selectedCities.length > 0 && !selectedCities.includes(e.city || '')) return false;
        if (selectedStandings.length > 0 && !(e.quadrant && selectedStandings.includes(e.quadrant))) return false;
        if (selectedReportingHeads.length > 0 && !(e.managerName && selectedReportingHeads.includes(e.managerName))) return false;
        // Area Manager / Trade Head — so this card + table respect the same
        // people-hierarchy filters Store Performance does.
        if (selectedAMs.length > 0 && !(e.amCode && selectedAMs.includes(e.amCode))) return false;
        if (selectedTradeHeads.length > 0 && !(e.thCode && selectedTradeHeads.includes(e.thCode))) return false;
        const q = empNameQuery.trim().toLowerCase();
        if (q && !(e.employeeName || '').toLowerCase().includes(q)) return false;
        const qc = empCodeQuery.trim().toLowerCase();
        if (qc && !(e.employeeCode || '').toLowerCase().includes(qc)) return false;
        return true;
      }),
    [bottomBox, roleMatches, filters.storeName, filters.storeCode, selectedCities, selectedStandings, selectedReportingHeads, selectedAMs, selectedTradeHeads, empNameQuery, empCodeQuery],
  );
  const bottomBoxStoreCount = useMemo(
    () => new Set(bottomBoxEmployees.map((e) => e.storeId).filter(Boolean)).size,
    [bottomBoxEmployees],
  );
  const undeployedEmpsFiltered = useMemo(
    () =>
      undeployedEmps.filter((e) => {
        if (!roleMatches(e.roleName)) return false;
        if (selectedCities.length > 0 && !selectedCities.includes(e.city || '')) return false;
        // Area Manager / Trade Head — scope this card + list to the selected person's
        // team, the same way Store Performance scopes by these filters.
        if (selectedAMs.length > 0 && !(e.amCode && selectedAMs.includes(e.amCode))) return false;
        if (selectedTradeHeads.length > 0 && !(e.thCode && selectedTradeHeads.includes(e.thCode))) return false;
        const q = empNameQuery.trim().toLowerCase();
        if (q && !(e.name || '').toLowerCase().includes(q)) return false;
        const qc = empCodeQuery.trim().toLowerCase();
        if (qc && !(e.code || '').toLowerCase().includes(qc)) return false;
        return true;
      }),
    [undeployedEmps, roleMatches, selectedCities, selectedAMs, selectedTradeHeads, empNameQuery, empCodeQuery],
  );

  // "Outside Approved – Redeploy" under the dashboard filters, so its pill count and
  // table agree with the rest of the tab: store-attribute filters through the row's
  // store, employee-level ones on the row itself. The row's reporting manager IS the
  // AM the filter lists (same lookup server-side); Trade Head is not on the row, so it
  // is read off the store's people lens, as Store Performance reads it.
  const redeployPendingRows = useMemo(() => {
    const storeById = new Map(allStores.map((s) => [s.id, s]));
    const q = empNameQuery.trim().toLowerCase();
    const qc = empCodeQuery.trim().toLowerCase();
    return redeployPendingAll.filter((r) => {
      const store = storeById.get(r.storeId);
      if (store && !storeMatchesAttrs(store)) return false;
      if (!roleMatches(r.roleName)) return false;
      if (selectedAMs.length > 0 && !(r.reportingManagerCode && selectedAMs.includes(r.reportingManagerCode))) return false;
      if (selectedTradeHeads.length > 0) {
        const heads = intelligence?.stores?.[r.storeId]?.people?.tradeHeads ?? [];
        if (!heads.some((t) => selectedTradeHeads.includes(t.code))) return false;
      }
      if (q && !r.employeeName.toLowerCase().includes(q)) return false;
      if (qc && !r.employeeCode.toLowerCase().includes(qc)) return false;
      return true;
    });
  }, [redeployPendingAll, allStores, storeMatchesAttrs, roleMatches, selectedAMs, selectedTradeHeads, intelligence, empNameQuery, empCodeQuery]);
  // Read against the project on screen, so the rows of the project just left are never
  // shown as this one's — not even for the render before its own load starts.
  const redeployPendingState =
    redeployPendingLoad.projectId === (selectedProjectId ?? null) ? redeployPendingLoad.state : 'loading';

  // ── Client-side column sorting for the Deployment sub-view tables ──
  // Each hook feeds the SAME array the table maps, so the default order is
  // preserved until a header is clicked.
  const bottomBoxSort = useTableSort(bottomBoxEmployees, {
    employee: (e) => e.employeeName,
    store: (e) => e.storeName || '',
    city: (e) => e.city || '',
    reportingHead: (e) => e.managerName || '',
    avg6m: (e) => e.avg6mScore,
    standing: (e) => (e.quadrant ? (QUADRANT_RANK[e.quadrant] ?? null) : null),
  });
  const undeployedStoresSort = useTableSort(undeployedStoresList, {
    store: (s) => s.name,
    category: (s) => s.category || '',
    trade: (s) => s.trade || '',
    state: (s) => s.state || '',
    // Staffing cell reads "active / min"; sort on the active headcount it leads with.
    staffing: (s) => s.activeHeadcount ?? 0,
    staffingType: (s) => (staffingTypeField ? formatStoreCustomFieldValue(s.customFields?.[staffingTypeField.id]) : ''),
    // Unset sorts as '' so "not set" groups together at one end rather than scattering.
    staffingStatus: (s) => s.staffingStatus || '',
    // By the first assigned manager's name (Trade Heads first); a row with none sinks to the
    // bottom, like every blank.
    manager: (s) => vacancyManagers.managerOf(s)?.managerName ?? '',
    // AON = how long the store has been vacant; older vacancy = earlier ISO date.
    aon: (s) => s.undeployedSince || '',
  });

  /**
   * Export the Undeployed Stores table.
   *
   * Exports `sorted`, not the raw list, so the file matches what is on screen —
   * same filters, same order. The two composite cells (Store Details, Category
   * & City) are split into real columns, and the staffing cell becomes three:
   * the counts a spreadsheet can total, plus the Understaffed/Vacant label that
   * the on-screen pill conveys.
   */
  const handleUndeployedStoresExport = useCallback(() => {
    const headers = [
      'Store Name',
      'Store Code',
      'Category',
      'City',
      'Trade',
      'State',
      'Active Headcount',
      'Min Required',
      // Renamed from 'Staffing Status' when the real stores.staffing_status column was
      // added below — two columns with that name meant two different things.
      'Vacancy Type',
      // Only when the project defines the field — mirrors the on-screen column.
      ...(staffingTypeField ? [staffingTypeField.label] : []),
      'Staffing Status',
      // The managers a vacant store lies under, Trade Heads first; blank when none is assigned.
      'Manager',
      // A plain number of days, so the column sorts, filters and totals in a spreadsheet.
      'AON (days)',
      'Undeployed Since',
    ];
    const rows = undeployedStoresSort.sorted.map((s) => {
      const active = s.activeHeadcount ?? 0;
      const min = s.minEmployees ?? 1;
      const understaffed = active > 0 && active < min;
      // Same date the cell ages from — empty store since it emptied, understaffed one
      // since it fell below its minimum. Blank when the date is unknown: a blank cell
      // sorts and filters better in Excel than a dash.
      const aonSince = aonSinceOf(s, understaffed, monthEndAsOf);
      const aonDays = undeployedDays(aonSince, monthEndAsOf);
      // Everyone the cell names, in its order. As the cell reads it: managers on record for a
      // store that is not Active are marked.
      const names = vacancyManagers.managersOf(s).map((m) => m.managerName).join(', ');
      const managerText = !names ? '' : vacancyManagers.managerApplies(s) ? names : `${names} (not applied)`;
      return [
        s.name ?? '',
        s.storeCode ?? '',
        s.category || '',
        s.city ?? '',
        s.trade || '',
        s.state || '',
        String(active),
        String(min),
        understaffed ? 'Understaffed' : 'Vacant',
        ...(staffingTypeField ? [formatStoreCustomFieldValue(s.customFields?.[staffingTypeField.id])] : []),
        s.staffingStatus ?? '',
        managerText,
        aonDays === null ? '' : String(aonDays),
        aonSince ?? '',
      ];
    });
    downloadCsv(
      `Undeployed_stores_${new Date().toISOString().slice(0, 10)}.csv`,
      buildCsv(headers, rows)
    );
  }, [undeployedStoresSort.sorted, staffingTypeField, vacancyManagers.managersOf, vacancyManagers.managerApplies]);
  const misDeployedSort = useTableSort(misDeployedStores, {
    store: (m) => m.store.name,
    category: (m) => m.store.category || '',
    // Employee = the deployed roster's names, in the order the cell lists them.
    employee: (m) => m.employees.map((e) => e.name).join(', '),
    // Representative AON = the misfit employee's tenure (min across the roster).
    aon: (m) => {
      const days = m.employees.map((e) => e.aonDays).filter((d): d is number => typeof d === 'number');
      return days.length ? Math.min(...days) : null;
    },
    deployment: (m) => m.kind,
  });
  const undeployedEmpsSort = useTableSort(undeployedEmpsFiltered, {
    employee: (e) => e.name,
    designation: (e) => e.designation || '',
    city: (e) => e.city || '',
    department: (e) => e.department || '',
  });
  // Unsorted, the rows keep the order they were derived in: longest wait first.
  const redeployPendingSort = useTableSort(redeployPendingRows, {
    employee: (r) => r.employeeName,
    role: (r) => r.roleName || '',
    store: (r) => r.storeName,
    city: (r) => r.city,
    markedOn: (r) => parseRedeployTimestamp(r.markedOn),
    manager: (r) => r.reportingManagerName || '',
  });

  // ── Option lists for the extended (per-view) filters ──
  const reportingHeadOptions = useMemo(
    () =>
      [...new Set((bottomBox?.employees ?? []).map((e) => e.managerName).filter((n): n is string => !!n && n.trim() !== ''))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [bottomBox],
  );
  const eligibilityOptions = useMemo(
    () => [...new Set(aggregatedStores.map((s) => eligibilityReqLabel(eligibilityDays(s.eligibility))))].filter((l) => l !== '—'),
    [aggregatedStores],
  );
  const deployedCountOptions = useMemo(
    () => [...new Set(aggregatedStores.map((s) => s.employees))].sort((a, b) => a - b).map((n) => String(n)),
    [aggregatedStores],
  );
  const bottomBoxCityOptions = useMemo(
    () =>
      [...new Set((bottomBox?.employees ?? []).map((e) => e.city).filter((c): c is string => !!c && c.trim() !== ''))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [bottomBox],
  );
  const undeployedEmpCityOptions = useMemo(
    () => [...new Set(undeployedEmps.map((e) => e.city).filter((c): c is string => !!c && c.trim() !== ''))].sort((a, b) => a.localeCompare(b)),
    [undeployedEmps],
  );

  const statCards = useMemo<StatCard[]>(() => {
    if (!stores.length) {
      return [
        { label: 'Total Stores', value: '—', helper: 'Loading…' },
        { label: 'Employees Deployed', value: '—', helper: 'Across all stores', accent: 'primary' },
        { label: 'Bottom Box', value: '—', helper: '—', accent: 'orange' },
        { label: 'Undeployed Stores', value: '—', helper: '—', accent: 'purple' },
        { label: 'Undeployed Employees', value: '—', helper: '—', accent: 'purple' },
      ];
    }

    // Base the KPIs on the filtered + de-duped store list so they react to the
    // active filters. With no filters this equals the whole project.
    //
    // Total Stores counts only stores that HAVE deployment, so it and the Undeployed
    // Stores card partition the project cleanly: deployed + undeployed = every store.
    //
    // It used to be `aggregatedStores.length` — every store, deployed or not — which made
    // the two cards overlap and produced a confusing jump the moment an employee-level
    // filter was used: a store with nobody deployed can never satisfy a Role/AM/TH
    // predicate, so all the undeployed ones silently vanished from Total Stores while the
    // Undeployed Stores card (which ignores employee filters by design, see
    // undeployedStoresList) went on showing them. Selecting one role appeared to delete
    // ~99 stores that were never in scope for it. Counting only deployed stores here means
    // the number moves only for genuine reasons — a store whose staff don't match the
    // filter — and the two cards can no longer double-count the same store.
    const totalStores = deployedStores.length;
    const sumEmployees = aggregatedStores.reduce((acc, store) => acc + store.employees, 0);
    // ONE metric in BOTH states: distinct employees deployed across the visible
    // stores. Using distinctDeployedCount unfiltered too is what makes the card
    // monotonic — mixing a server total with a client-side sum is exactly how a
    // filter came to INCREASE the number. Fallbacks only cover load order:
    // distinctDeployedCount is null until store intelligence arrives, and
    // sumEmployees is the last resort if the project count is missing as well.
    const employeesDeployed = distinctDeployedCount ?? employeesDeployedCount ?? sumEmployees;
    const undeployedStores = undeployedStoresList.length;
    // Bottom Box = the count of bottom-3% EMPLOYEES (one row per employee in the
    // Bottom Box table). Use the employee total so the card, the "Show" tab pill
    // (which opens that table), and the table's own row count all agree — the card
    // previously showed storeCount (stores), which disagreed with the pill/table
    // and with this card's own "employees" helper. The store spread moves into the
    // helper. Shows "—" until the dedicated 5% fetch lands.
    const bottomBoxCount = bottomBox ? bottomBoxEmployees.length : null;
    // The label is short; whose bottom 3% and across how many stores is on hover (the
    // Bottom Box list says both under its own heading).
    const bottomBoxHelper = bottomBox ? 'Bottom 3% per team' : '—';
    const bottomBoxHint = bottomBox
      ? `Bottom 3% of each manager's team · ${bottomBoxStoreCount} store${bottomBoxStoreCount === 1 ? '' : 's'}`
      : undefined;

    // Filters that describe a PERSON rather than a store. Undeployed stores have no people,
    // so these can never narrow that card — used to caption it honestly.
    const employeeLevelFilterActive =
      selectedRoles.length > 0 ||
      selectedAMs.length > 0 ||
      selectedTradeHeads.length > 0 ||
      selectedStandings.length > 0 ||
      !!empNameQuery.trim() ||
      !!empCodeQuery.trim();

    const scope = hasFiltersApplied ? 'Matching filters' : 'With employees deployed';
    // These three are the roster-derived counts. When the exit check failed they are all
    // wrong, so show nothing instead — a plausible number reads as fact, a dash does not.
    // Bottom Box and Undeployed Employees come from other endpoints and stay as they are.
    const unavailable = 'Live roster unavailable';
    // A number opens its list (`list`) only when the list shows exactly that many rows and
    // there is at least one. Each count below is the length of the very array its table
    // draws, with two exceptions that stay plain:
    //  • Total Stores while the "employees deployed" filter is on. That filter narrows the
    //    Store Performance table (displayStores) but not this number.
    //  • Employees Deployed, always. It counts people once each; no list here does.
    return [
      {
        label: 'Total Stores',
        value: rosterUnresolved ? '—' : totalStores.toString(),
        helper: rosterUnresolved ? unavailable : scope,
        list: !rosterUnresolved && totalStores > 0 && displayStores.length === totalStores ? 'Store Performance' : undefined,
      },
      { label: 'Employees Deployed', value: rosterUnresolved ? '—' : employeesDeployed.toString(), helper: rosterUnresolved ? unavailable : (hasFiltersApplied ? 'In filtered stores' : 'Across all stores'), accent: 'primary' },
      {
        label: 'Bottom Box',
        value: bottomBoxCount != null ? bottomBoxCount.toString() : '—',
        helper: bottomBoxHelper,
        hint: bottomBoxHint,
        accent: 'orange',
        list: bottomBoxCount != null && bottomBoxCount > 0 ? 'Bottom Box' : undefined,
      },
      {
        label: 'Undeployed Stores',
        value: rosterUnresolved ? '—' : undeployedStores.toString(),
        // "Nobody or too few", not "No employee deployed": the number counts the stores
        // below their category's minimum too, as the strip above it says and as the list
        // the tile opens shows.
        helper: rosterUnresolved ? unavailable : 'Nobody or too few',
        // On hover, the whole of it. These stores have nobody deployed, so a role / AM /
        // Trade Head filter cannot apply to them — the figure intentionally holds while
        // Total Stores narrows, and the hover says so, otherwise it reads as a stuck
        // number sitting next to "Matching filters".
        hint: rosterUnresolved
          ? undefined
          : employeeLevelFilterActive
            ? 'Nobody, or too few people · role filters N/A'
            : 'Nobody, or too few people',
        accent: 'purple',
        list: !rosterUnresolved && undeployedStores > 0 ? 'Undeployed Stores' : undefined,
      },
      {
        label: 'Undeployed Employees',
        value: undeployedEmpsFiltered.length.toString(),
        helper: 'Active, not at a store',
        accent: 'purple',
        list: undeployedEmpsFiltered.length > 0 ? 'Undeployed Employees' : undefined,
      },
    ];
  }, [stores, aggregatedStores, deployedStores, displayStores, hasFiltersApplied, selectedRoles, selectedAMs, selectedTradeHeads, selectedStandings, empNameQuery, empCodeQuery, employeesDeployedCount, distinctDeployedCount, intelligence, bottomBox, bottomBoxEmployees, bottomBoxStoreCount, undeployedEmpsFiltered, undeployedStoresList, rosterUnresolved]);

  const lastUpdatedLabel = useMemo(() => {
    if (isLoading) {
      return 'Fetching live data…';
    }
    if (lastUpdated) {
      const timestamp = new Date(lastUpdated);
      return timestamp.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    if (!stores.length) {
      return 'Awaiting data…';
    }
    return 'Sample snapshot · refresh for live data';
  }, [isLoading, lastUpdated, stores.length]);

  // Yellow-flag reasons are configurable per project, so the filter's options come
  // from the API rather than a constant. Fail-soft: on error the list keeps just the
  // "All" entry, so the filter degrades to a no-op instead of blocking the tab.
  useEffect(() => {
    if (selectedProjectId == null) return;
    let cancelled = false;
    yellowFlagDashboardAPI
      .getReasonOptions({ projectId: selectedProjectId })
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data?.data?.reasons ?? []) as Array<{ key: string; label: string }>;
        setYellowReasonOptions([
          { value: '', label: 'All Yellow Flag Reasons' },
          ...rows.map((x) => ({ value: x.key, label: x.label })),
        ]);
      })
      .catch(() => { if (!cancelled) setYellowReasonOptions([{ value: '', label: 'All Yellow Flag Reasons' }]); });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // Red-flag types are per project (custom rules are `c_<id>` of ONE project), so a
  // project switch drops the previous project's list and any custom-rule selection.
  // A standard code stays selected, exactly as before.
  useEffect(() => {
    setRedFlagTypesFromApi(null);
    setRedFlagType((cur) => (cur && !LEGACY_RED_FLAG_TYPE_KEYS.has(cur) ? '' : cur));
  }, [selectedProjectId]);

  const clearFilters = () => {
    setFilters({ storeCode: '', storeName: '' });
    setSelectedTrades([]);
    setSelectedStates([]);
    setSelectedRegions([]);
    setSelectedCities([]);
    setSelectedCategories([]);
    setSelectedAMs([]);
    setSelectedTradeHeads([]);
    setSelectedRoles([]);
    // Budget is counted in the "N active" badge above but was never reset here, so
    // "Clear all" left it applied with the badge still reading 1.
    setSelectedBudgets([]);
    setSelectedTenures([]);
    // Extended (per-view) filters.
    setSelectedStandings([]);
    setEmpNameQuery('');
    setEmpCodeQuery('');
    setSelectedReportingHeads([]);
    setSelectedAonBands([]);
    setSelectedVacancyBands([]);
    setSelectedStaffingStatuses([]);
    setSelectedEligibilities([]);
    setSelectedFits([]);
    setSelectedDeployedCounts([]);
    setRedFlagType('');
    setRedFlagLevel('');
    setYellowReason('');
    setEwsEmployees('active');
  };

  // Toggle a value in a string-array filter (add if absent, remove if present).
  const toggleIn = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (v: string) =>
    setter((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  // 6-mo quadrant standing options (best → worst), shared by Store Performance +
  // Bottom Box filters.
  const STANDING_OPTIONS = ['Stars', 'Achievers', 'Strivers', 'At Risk'];

  // Red-flag parameter (single-select) — Dashboard → Red Flag tab whole-tab scope.
  // '' = all types. Keys + labels come from the API (the trend's `types`: the month's
  // enabled rules, custom ones included); the legacy six are only the fallback. The
  // current selection is always listed, so the <select> never shows "All" while a
  // type filter is silently applied.
  const redFlagTypeOptions = (() => {
    const list = redFlagTypesFromApi
      ? [
          { value: '', label: 'All Red Flag Types' },
          ...redFlagTypesFromApi.map((t) => ({ value: t.key, label: flagTypeLabel(t) })),
        ]
      : LEGACY_RED_FLAG_TYPE_OPTIONS;
    return redFlagType && !list.some((o) => o.value === redFlagType)
      ? [...list, { value: redFlagType, label: redFlagType }]
      : list;
  })();

  // ── Per-dashboard filter sections for the modal ──
  // Every dashboard gets Roles + Month; Deployment sub-views add contextual
  // fields. Computed inline (cheap) so it always reflects live state + options.
  const buildFilterSections = (): FilterSection[] => {
    const opt = (arr: string[]) => arr.map((v) => ({ value: v, label: v }));
    const roles: FilterSection = {
      kind: 'multiselect', key: 'roles', label: 'Roles',
      options: opt(allRoleNames), selected: selectedRoles,
      onToggle: handleRoleToggle, onClear: () => setSelectedRoles([]), searchable: true,
    };
    const month: FilterSection = {
      kind: 'month', key: 'month', label: 'Month', value: periodMonth,
      options: monthOptions, onChange: setPeriodMonth,
    };
    const storeName: FilterSection = { kind: 'text', key: 'storeName', label: 'Store Name', value: filters.storeName, onChange: (v) => setFilters((f) => ({ ...f, storeName: v })) };
    const storeCode: FilterSection = { kind: 'text', key: 'storeCode', label: 'Store Code', value: filters.storeCode, onChange: (v) => setFilters((f) => ({ ...f, storeCode: v })) };
    const empName: FilterSection = { kind: 'text', key: 'empName', label: 'Employee Name', value: empNameQuery, onChange: setEmpNameQuery };
    const empCode: FilterSection = { kind: 'text', key: 'empCode', label: 'Employee Code', value: empCodeQuery, onChange: setEmpCodeQuery };
    const trade: FilterSection = { kind: 'multiselect', key: 'trade', label: 'Store Trade', options: opt(getUniqueTrades()), selected: selectedTrades, onToggle: handleTradeToggle, onClear: () => setSelectedTrades([]), searchable: true };
    const category: FilterSection = { kind: 'multiselect', key: 'category', label: 'Store Category', options: opt(getUniqueCategories()), selected: selectedCategories, onToggle: handleCategoryToggle, onClear: () => setSelectedCategories([]), searchable: true };
    const state: FilterSection = { kind: 'multiselect', key: 'state', label: 'State', options: opt(getUniqueStates()), selected: selectedStates, onToggle: handleStateToggle, onClear: () => setSelectedStates([]), searchable: true };
    // Coarser cut than State, sourced from the 1Office master (see regionByStateKey).
    // ANDs with State like every other filter.
    const region: FilterSection = { kind: 'multiselect', key: 'region', label: 'Region', options: opt(getUniqueRegions()), selected: selectedRegions, onToggle: toggleIn(setSelectedRegions), onClear: () => setSelectedRegions([]) };
    const standing: FilterSection = { kind: 'multiselect', key: 'standing', label: 'Standing', options: opt(STANDING_OPTIONS), selected: selectedStandings, onToggle: toggleIn(setSelectedStandings), onClear: () => setSelectedStandings([]) };
    // Retained hierarchy filters (were on the old drawer; not in the new per-view
    // spec but preserved so no existing functionality is silently lost).
    const am: FilterSection = { kind: 'multiselect', key: 'am', label: 'Assistant Manager', options: getUniqueAMs().map((a) => ({ value: a.code, label: a.name })), selected: selectedAMs, onToggle: handleAMToggle, onClear: () => setSelectedAMs([]), searchable: true };
    const tradeHead: FilterSection = { kind: 'multiselect', key: 'tradeHead', label: 'Trade Head', options: getUniqueTradeHeads().map((t) => ({ value: t.code, label: t.name })), selected: selectedTradeHeads, onToggle: handleTradeHeadToggle, onClear: () => setSelectedTradeHeads([]), searchable: true };
    // Budget — resolved SERVER-side (no store/hierarchy source); options are the
    // per-project 1Office budget list, mirroring Project Management's budget filter.
    const budget: FilterSection = { kind: 'multiselect', key: 'budget', label: 'Budget', options: budgetOptions, selected: selectedBudgets, onToggle: toggleIn(setSelectedBudgets), onClear: () => setSelectedBudgets([]), searchable: true };
    const cityWith = (options: string[]): FilterSection => ({ kind: 'multiselect', key: 'city', label: 'City', options: opt(options), selected: selectedCities, onToggle: handleCityToggle, onClear: () => setSelectedCities([]), searchable: true });

    // Data Hygiene: the Role filter alone. It narrows the Employees side in the browser;
    // the month and the store filters do not apply to a list of what is wrong right now.
    if (roleFilterOnly) return [roles];

    // A custom dashboard: the filters the server applies to every block before it runs
    // (custom-dashboards/dashboard-filters.ts). No Month: its blocks take their own dates.
    if (activeTab === 'Custom') {
      const staffing: FilterSection = {
        kind: 'multiselect', key: 'staffingStatus', label: 'Staffing Status',
        options: staffingStatusFilterOptions(stores, selectedStaffingStatuses),
        selected: selectedStaffingStatuses, onToggle: toggleIn(setSelectedStaffingStatuses), onClear: () => setSelectedStaffingStatuses([]),
      };
      return [staffing, trade, state, cityWith(getUniqueCities()), am, tradeHead];
    }

    // The Red Flag tab additionally offers a single "Red Flag Type" scope — it narrows
    // EVERY module on the tab (trend / distribution / action-TAT / leaderboard / today)
    // to one red-flag parameter, filtering the flag ROWS by type (server-side), which is
    // orthogonal to the employee-scope filters below.
    if (activeTab === 'Red Flag') {
      const redFlagTypeSection: FilterSection = {
        kind: 'select', key: 'redFlagType', label: 'Red Flag Type',
        value: redFlagType, options: redFlagTypeOptions, onChange: setRedFlagType,
      };
      // Whose flags: store-level employees, managers (rules can now target people
      // managers and roles), or everyone. Narrows every module on the tab server-side.
      // Offered only while Red Flag Rules is switched on; otherwise the list is as before.
      const redFlagLevelSections: FilterSection[] = redFlagRulesEnabled
        ? [{
            kind: 'select', key: 'redFlagLevel', label: 'Level',
            value: redFlagLevel, options: RED_FLAG_LEVEL_OPTIONS,
            onChange: (v) => setRedFlagLevel(v === 'frontline' || v === 'manager' ? v : ''),
          }]
        : [];
      return [
        roles, month, redFlagTypeSection, ...redFlagLevelSections, storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
        empName, empCode, standing, am, tradeHead, budget,
      ];
    }

    // Same shape as the Red Flag branch above, but the reason list is fetched (see
    // yellowReasonOptions) because yellow reasons are configurable per project.
    if (activeTab === 'Yellow Flag') {
      const yellowReasonSection: FilterSection = {
        kind: 'select', key: 'yellowReason', label: 'Yellow Flag Reason',
        value: yellowReason, options: yellowReasonOptions, onChange: setYellowReason,
      };
      return [
        roles, month, yellowReasonSection, storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
        empName, empCode, standing, am, tradeHead, budget,
      ];
    }

    // Attrition is a LOOK-BACK metric, so it can only honour filters whose attribute
    // still resolves after an employee has LEFT: Role (user_roles persists), Area
    // Manager / Trade Head (resolved as the manager's include-inactive subtree), and
    // Budget (resolved include-inactive). Store / Store Code / Category /
    // Standing / Employee Name are OMITTED here on purpose — a departed employee has
    // no current store/standing row, so filtering by them would drop the leavers from
    // the numerator while active headcount stayed, understating the rate. Better to
    // not offer a control than to offer one that lies. (Backend enforces the same set.)
    //
    // Region / State / City and Store Trade ARE offered: the backend places every person,
    // leavers included, by their LAST store on record (store_employee_history), so numerator
    // and denominator are narrowed together — and it counts the leavers it could not
    // place, which the tab shows. Tenure is offered too; it narrows which EXITS are
    // counted and leaves the headcount alone, so the tab captions the result as a
    // share of the rate.
    if (activeTab === 'Attrition') {
      // The bands the API counts, once a response has said what they are; the
      // hand-kept copy only until then. A band the copy has wrong would otherwise be
      // sent, ignored by the API, and leave an "active" filter that filters nothing.
      const tenure: FilterSection = {
        kind: 'multiselect', key: 'tenure', label: 'Tenure at exit',
        options: (attritionFacets?.tenures ?? ATTRITION_TENURE_OPTIONS).map((t) => ({ value: t.key, label: t.label })),
        selected: selectedTenures, onToggle: toggleIn(setSelectedTenures), onClear: () => setSelectedTenures([]),
      };
      // Location options: the staffed stores' (as on every tab) PLUS where this
      // viewer's leavers were last posted. The store list alone misses exactly the
      // places attrition has emptied — a city whose only store closed, or, for a
      // manager, a store whose last team member left (see mergeLocationOptions).
      const merge = mergeLocationOptions;
      const leaverPlaces = attritionFacets?.locations ?? [];
      const stateKeys = new Set(selectedStates.map(normalizeLocationKey));
      const attrStates = merge(getUniqueStates(), leaverPlaces.map((p) => p.state));
      const attrCities = merge(
        getUniqueCities(),
        // Narrowed by the selected states, as the store-derived cities already are.
        leaverPlaces.filter((p) => stateKeys.size === 0 || stateKeys.has(normalizeLocationKey(p.state))).map((p) => p.city),
      );
      const attrRegions = merge(getUniqueRegions(), leaverPlaces.map((p) => regionForState(p.state) ?? ''));
      // Trades too: a trade whose stores have all lost their teams is still on offer.
      const attrTrades = merge(getUniqueTrades(), attritionFacets?.trades ?? []);
      return [
        roles, month, am, tradeHead, budget,
        { ...region, options: opt(attrRegions) },
        { ...state, options: opt(attrStates) },
        cityWith(attrCities),
        { ...trade, options: opt(attrTrades) },
        tenure,
      ];
    }

    // EWS takes Score Movement's set, for the same reason (Standing is a six-month band of
    // its own), with its Month offering finished months only.
    if (activeTab === 'EWS') {
      // Who is listed: those working today (the default), those who left during the chosen
      // months, or both (everyone who worked in them, those who left after them included).
      const employees: FilterSection = {
        kind: 'select', key: 'ewsEmployees', label: 'Employees', value: ewsEmployees,
        options: EWS_EMPLOYEE_OPTIONS, onChange: (v) => setEwsEmployees(v as EwsEmployees),
      };
      return [
        employees, roles, { ...month, value: ewsEndMonth, options: ewsMonthOptions },
        storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
        empName, empCode, am, tradeHead, budget,
      ];
    }

    // Score Movement takes the server-side tabs' filter set MINUS Standing. That filter
    // is the SIX-MONTH band; this tab shows each month's own band and may end on an
    // earlier month, so "Standing = Stars" would list people the tab shows as At Risk —
    // and on a month not scored yet it would empty the tab. Better to not offer a
    // control than to offer one that lies. (Backend ignores it for this tab too.)
    if (activeTab === 'Score Movement') {
      return [
        roles, month, storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
        empName, empCode, am, tradeHead, budget,
      ];
    }

    // The server-side tabs (Attendance / Mood / Appeals) share the same rich filter set
    // as Store Performance — MINUS "Employees Deployed" (a store-headcount cut that only
    // makes sense for the deployment view). These narrow the dashboard's employee scope
    // server-side (see dashFilterParams → the tabs).
    if (activeTab !== 'Deployment') {
      return [
        roles, month, storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
        empName, empCode, standing, am, tradeHead, budget,
      ];
    }

    switch (deploymentTable) {
      case 'Store Performance':
        return [
          roles, month, storeName, storeCode, trade, category, cityWith(getUniqueCities()),
          { kind: 'multiselect', key: 'deployed', label: 'Employees Deployed', options: opt(deployedCountOptions), selected: selectedDeployedCounts, onToggle: toggleIn(setSelectedDeployedCounts), onClear: () => setSelectedDeployedCounts([]) },
          empName, empCode, standing, am, tradeHead,
        ];
      case 'Bottom Box':
        return [
          roles, month, storeName, storeCode, cityWith(bottomBoxCityOptions), empName, empCode,
          { kind: 'multiselect', key: 'reportingHead', label: 'Reporting Head', options: opt(reportingHeadOptions), selected: selectedReportingHeads, onToggle: toggleIn(setSelectedReportingHeads), onClear: () => setSelectedReportingHeads([]), searchable: true },
          standing, am, tradeHead,
        ];
      case 'Undeployed Stores':
        // `roles` is deliberately ABSENT. An undeployed store has nobody deployed, so it can
        // never satisfy a role/AM/Trade-Head predicate — undeployedStoresList ignores those
        // filters by design. Offering the dropdown here just showed an active filter chip
        // next to a number that never moved. Store-attribute filters still apply, Store
        // Trade among them (storeMatchesAttrs).
        return [
          month, storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
          // Only the statuses these stores carry (before this filter and the vacancy age
          // one), in the order of the table's columns: Staffing Status, then AON.
          { kind: 'multiselect', key: 'staffingStatus', label: 'Staffing Status', options: staffingStatusFilterOptions(undeployedStoresBase, selectedStaffingStatuses), selected: selectedStaffingStatuses, onToggle: toggleIn(setSelectedStaffingStatuses), onClear: () => setSelectedStaffingStatuses([]) },
          { kind: 'multiselect', key: 'vacancy', label: 'AON (vacancy age)', options: opt([...UNDEPLOYED_AON_BANDS]), selected: selectedVacancyBands, onToggle: toggleIn(setSelectedVacancyBands), onClear: () => setSelectedVacancyBands([]) },
        ];
      case 'MisDeployed Stores':
        return [
          roles, month, storeName, storeCode, cityWith(getUniqueCities()), category,
          { kind: 'multiselect', key: 'eligibility', label: 'Eligibility', options: opt(eligibilityOptions), selected: selectedEligibilities, onToggle: toggleIn(setSelectedEligibilities), onClear: () => setSelectedEligibilities([]) },
          empName, empCode,
          { kind: 'multiselect', key: 'aon', label: 'AON (tenure)', options: opt([...AON_BANDS]), selected: selectedAonBands, onToggle: toggleIn(setSelectedAonBands), onClear: () => setSelectedAonBands([]) },
          { kind: 'multiselect', key: 'fit', label: 'Qualification', options: opt(['Under-qualified', 'Over-qualified']), selected: selectedFits, onToggle: toggleIn(setSelectedFits), onClear: () => setSelectedFits([]) },
        ];
      case 'Undeployed Employees':
        return [roles, month, empName, empCode, cityWith(undeployedEmpCityOptions), am, tradeHead];
      case STORE_STATUS_REDEPLOY:
        // Exactly what redeployPendingRows honours. No Standing: the row carries no score.
        return [
          roles, month, storeName, storeCode, trade, category, cityWith(getUniqueCities()), state, region,
          empName, empCode, am, tradeHead,
        ];
      default:
        return [roles, month];
    }
  };
  // Named as the dashboard's title names it, not by its key.
  const filterModalTitle = activeTab === 'Deployment'
    ? `Filters — ${deploymentTable}`
    : activeTab === 'Custom'
      ? `Filters — ${activeCustomDashboard?.title ?? 'Custom dashboard'}`
      : `Filters — ${activeTab ? dashLabel(activeTab) : activeTab}`;

  // Serialized rich filters for the server-side tabs (Attendance / Red Flag / Mood /
  // Appeals). The SAME filter STATE drives the Deployment view's client-side
  // filtering; here it's shipped as query params (CSV for multi-selects). Memoized
  // so a tab's data effect only refetches when a value actually changes — a new
  // object identity each render would refetch on every keystroke elsewhere. Fields
  // left undefined are dropped by axios, so an unfiltered view sends only its base
  // params. NO "Employees Deployed" (Deployment-only).
  const dashFilterParams = useMemo<DashRichFilterParams>(
    () => ({
      storeName: filters.storeName.trim() || undefined,
      storeCode: filters.storeCode.trim() || undefined,
      trades: selectedTrades.length ? selectedTrades.join(',') : undefined,
      categories: selectedCategories.length ? selectedCategories.join(',') : undefined,
      cities: selectedCities.length ? selectedCities.join(',') : undefined,
      states: selectedStates.length ? selectedStates.join(',') : undefined,
      // The server has no state->region map, so a selected Region is expanded here into
      // its member states from the 1Office master. The backend matches on the NORMALIZED
      // key, so sending canonical master spellings still catches stores saved as "KERALA".
      regionStates: regionStatesParam,
      empName: empNameQuery.trim() || undefined,
      empCode: empCodeQuery.trim() || undefined,
      standings: selectedStandings.length ? selectedStandings.join(',') : undefined,
      amCodes: selectedAMs.length ? selectedAMs.join(',') : undefined,
      thCodes: selectedTradeHeads.length ? selectedTradeHeads.join(',') : undefined,
      budgetIds: selectedBudgets.length ? selectedBudgets.join(',') : undefined,
    }),
    [
      filters.storeName, filters.storeCode, selectedTrades, selectedCategories, selectedCities,
      selectedStates, regionStatesParam,
      empNameQuery, empCodeQuery, selectedStandings, selectedAMs, selectedTradeHeads, selectedBudgets,
    ],
  );

  // The same choices for a custom dashboard, as its run endpoint reads them. Memoized for the
  // same reason: the view re-runs every block when this changes identity.
  const customDashboardFilters = useMemo<CustomDashboardRunFilters | undefined>(() => {
    const f: CustomDashboardRunFilters = {
      ...(selectedTrades.length ? { trades: selectedTrades } : {}),
      ...(selectedStates.length ? { states: selectedStates } : {}),
      ...(selectedCities.length ? { cities: selectedCities } : {}),
      ...(selectedStaffingStatuses.length ? { staffingStatuses: selectedStaffingStatuses } : {}),
      ...(selectedAMs.length ? { amCodes: selectedAMs } : {}),
      ...(selectedTradeHeads.length ? { thCodes: selectedTradeHeads } : {}),
    };
    return Object.keys(f).length ? f : undefined;
  }, [selectedTrades, selectedStates, selectedCities, selectedStaffingStatuses, selectedAMs, selectedTradeHeads]);

  const accentClasses: Record<NonNullable<StatCard['accent']>, string> = {
    primary: 'text-primary-600',
    orange: 'text-orange-600',
    purple: 'text-purple-600',
  };

  const statusConfig: Record<StoreAnalytics['status'], { dot: string; icon: React.ReactNode; label: string; text: string; bg: string }> = {
    Deployed: {
      dot: 'bg-emerald-500',
      icon: <CheckCircle className="h-4 w-4 text-emerald-500" />,
      label: 'Deployed',
      text: 'text-emerald-700',
      bg: 'bg-emerald-50/80',
    },
    Pending: {
      dot: 'bg-amber-400',
      icon: <AlertTriangle className="h-4 w-4 text-amber-500" />,
      label: 'Attention',
      text: 'text-amber-700',
      bg: 'bg-amber-50/80',
    },
  };

  // Resolves true while the cockpit is still open on this store, false once it has been
  // closed or opened on another store meanwhile — a caller with a step to run after the
  // insights load checks it first. `fromPendingList` is set by the "Outside Approved –
  // Redeploy" list alone (see cockpitVacateStoreRef).
  const openInsightsModal = async (store: StoreAnalytics, fromPendingList = false): Promise<boolean> => {
    const opening = ++cockpitOpeningRef.current;
    cockpitVacateStoreRef.current = fromPendingList ? store.id : null;
    setInsightsStore(store); // Show modal immediately with existing data
    setIsInsightsModalOpen(true);
    setIsInsightsLoading(true);
    // Reset the redeployment drill-down for the newly opened store — the employee and
    // destination too, so nothing chosen in an earlier cockpit carries into this one.
    setRedeployEmp(null);
    setRedeployTarget(null);
    setRedeployCandidates(null);
    setRedeployRadiusKm(10);
    setRadiusDraft(10);
    setEmpContexts({});
    setDisplacedContext(null);
    setContextMonth(periodMonth);
    setStoreDetailsOpen(false);
    setNswEmp(null);

    try {
      const response = await storesAPI.getInsights(store.id);
      // Answered after the cockpit was closed (or opened on another store): drop it.
      if (opening !== cockpitOpeningRef.current) return false;
      const apiData = response.data?.data;
      if (apiData) {
        // Merge API data into the store object
        const updatedStore: StoreAnalytics = {
          ...store,
          name: apiData.store?.name ?? store.name,
          trade: apiData.store?.trade ?? store.trade,
          storeCode: apiData.store?.storeCode ?? store.storeCode,
          category: apiData.store?.category ?? store.category,
          city: apiData.store?.city ?? store.city,
          state: apiData.store?.state ?? store.state,
          address: apiData.store?.address ?? store.address,
          pinCode: apiData.store?.pinCode ?? store.pinCode,
          eligibility: apiData.store?.eligibility ?? store.eligibility,
          storeSeedScore: apiData.store?.storeSeedScore ?? store.storeSeedScore,
          monthlyCostValue: apiData.metrics?.monthlyCostValue ?? store.monthlyCostValue,
          monthlyCost: formatCurrency(apiData.metrics?.monthlyCostValue) ?? store.monthlyCost,
          payoutMinValue: apiData.metrics?.payoutMinValue ?? store.payoutMinValue,
          payoutMaxValue: apiData.metrics?.payoutMaxValue ?? store.payoutMaxValue,
          profitLossValue: `${apiData.metrics?.profitValue >= 0 ? '+' : '−'}${formatCurrency(Math.abs(apiData.metrics?.profitValue ?? 0))}`,
          profitLossPercent: apiData.metrics?.monthlyCostValue > 0
            ? `${apiData.metrics?.profitPercentValue >= 0 ? '+' : '−'}${Math.abs(apiData.metrics?.profitPercentValue ?? 0).toFixed(1)}%`
            : '—',
          deployedEmployees: apiData.deployedEmployees ?? store.deployedEmployees,
        };
        setInsightsFinancialsVisible(Boolean(apiData.financialsVisible));
        setInsightsStoreFinancials((apiData.storeFinancials ?? null) as SourceStoreFinancials | null);
        setInsightsStore(updatedStore);
      }
    } catch (err) {
      console.error('Failed to fetch store insights', err);
      // Keep showing existing data if API fails
    } finally {
      if (opening === cockpitOpeningRef.current) setIsInsightsLoading(false);
    }
    return opening === cockpitOpeningRef.current;
  };

  const closeInsightsModal = () => {
    // Disowns an insights request still in flight: its answer, and any step waiting
    // on it, is dropped (see openInsightsModal).
    cockpitOpeningRef.current += 1;
    cockpitVacateStoreRef.current = null;
    setInsightsStore(null);
    setIsInsightsModalOpen(false);
    setIsInsightsLoading(false);
    // Reset the redeployment cockpit.
    setEligibilityMode(false);
    setRedeployEmp(null);
    setNswEmp(null);
    setRedeployCandidates(null);
    setRedeployTarget(null);
    setRedeployFinancialsVisible(false);
    setInsightsFinancialsVisible(false);
    setInsightsStoreFinancials(null);
    setSourceStoreFinancials(null);
    setDisplacedContext(null);
    setRedeployRadiusKm(10);
    setRadiusDraft(10);
  };

  // Begin redeploying employee A out of the current store.
  const startRedeploy = useCallback((emp: StoreAnalytics['deployedEmployees'][0]) => {
    setRedeployEmp(emp);
    // Force a fresh, employee-specific candidate fetch. The load effect only
    // fetches when redeployCandidates === null, so WITHOUT this reset, switching
    // the source employee (Back → pick a different one) would keep the previous
    // employee's list — and the standing gate would stay computed for the WRONG
    // employee's 6-mo avg (re-showing stores stronger than the new employee).
    setRedeployCandidates(null);
    setNswEmp(null);
    setRedeployTarget(null);
    setDisplacedContext(null);
    setSrcCardOpen(false);
  }, []);

  // Open the redeployment cockpit for a MisDeployed row's misfit employee, in
  // ELIGIBILITY mode: pre-select that employee and jump straight to the
  // destination step, where candidates are gated to stores whose tenure band the
  // employee MEETS (a move) or where a valid two-way swap exists.
  const openRedeployForMisdeployed = async (store: StoreAnalytics, empCode: string) => {
    setEligibilityMode(true);
    if (!(await openInsightsModal(store))) return;
    const deployed = store.deployedEmployees ?? [];
    // Pre-select + jump straight to the Destination step ONLY when there is a
    // SINGLE employee at the store. When multiple are deployed, start at Step 1
    // (Employee) so the user picks whom to redeploy — don't auto-pick one.
    if (deployed.length === 1) {
      startRedeploy(deployed.find((e) => e.code === empCode) ?? deployed[0]);
    }
  };

  // Open the redeployment cockpit for one "Outside Approved – Redeploy" row, with that
  // employee already chosen, so it lands on the Destination step. In the DEFAULT mode,
  // not the MisDeployed tab's eligibility mode: that one settles an occupied destination
  // with a two-way swap, which would bring someone else INTO the store being emptied.
  const openRedeployForPending = async (row: RedeployPendingRow) => {
    const store = allStores.find((s) => s.id === row.storeId);
    if (!store) return;
    setEligibilityMode(false);
    if (!(await openInsightsModal(store, true))) return;
    const emp = (store.deployedEmployees ?? []).find((e) => e.code === row.employeeCode);
    if (emp) startRedeploy(emp);
  };

  // Initiate NSW for an employee directly from Step 1: records the separation
  // (status='NSW') AND undeploys them from the store, atomically on the backend.
  // Pick an employee for NSW → go to the Review step (Destination is skipped).
  const startNsw = useCallback((emp: StoreAnalytics['deployedEmployees'][0]) => {
    setNswEmp(emp);
    setRedeployEmp(null);
    setRedeployTarget(null);
    setDisplacedContext(null);
  }, []);

  // Confirm NSW from the Review step: records the separation (status='NSW') AND
  // undeploys the employee, atomically on the backend.
  const confirmNsw = useCallback(async () => {
    if (!insightsStore || !selectedProjectId || !nswEmp) return;
    const ctx = empContexts[nswEmp.code] ?? null;
    setChainExecuting(true);
    try {
      await storeIntelligenceAPI.createExitInitiation(selectedProjectId, {
        employeeCode: nswEmp.code,
        employeeName: nswEmp.name,
        storeId: insightsStore.id,
        storeName: insightsStore.name,
        reason: `NSW initiated — 6-mo avg ${ctx?.score.avg6mScore ?? '—'}${ctx?.score.quadrant ? ` · ${ctx.score.quadrant}` : ''}`,
        avg6m: ctx?.score.avg6mScore ?? null,
        quadrant: ctx?.score.quadrant ?? null,
        status: 'NSW',
      });
      alert(`NSW initiated for ${nswEmp.name} — undeployed from ${insightsStore.name}.`);
      closeInsightsModal();
      fetchStores();
      setRefreshNonce((n) => n + 1);
    } catch (err) {
      console.error('NSW initiation failed', err);
      alert('Failed to initiate NSW. Please try again.');
    } finally {
      setChainExecuting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightsStore, selectedProjectId, nswEmp, empContexts]);

  // Save the redeployment: move A into the target store; if it was occupied,
  // the replaced incumbent B is set to NSW + undeployed (records + undeploys
  // atomically). deploy() auto-removes A from their current store.
  const executeRedeploymentChain = useCallback(async () => {
    if (!insightsStore || !redeployEmp || !redeployTarget || !selectedProjectId) return;
    const aCode = redeployEmp.code;
    const target = redeployTarget;
    const b = target.employee;

    // A vacant target means "no displacement": either empty, or understaffed vs its
    // category minimum (e.g. Apple L3 1→2). In the understaffed case `b` is the lone
    // incumbent we KEEP — deploy() adds A as a 2nd person and we must NOT mark B NSW.
    // Only a genuinely occupied (non-vacant) target displaces its incumbent.
    const displacesIncumbent = !!b && !target.isVacant;
    // When adding alongside a kept incumbent (understaffed store), deploy as
    // NON-primary so the incumbent stays the store's primary. Empty stores and
    // replacements deploy as primary (the endpoint's default).
    const addingAlongsideIncumbent = target.isVacant && !!b;
    // A deploy MOVES a single-store employee but only ADDS a store for a roaming
    // (multi-store) role, which would leave them in the very store they are being moved
    // out of — still on the "Outside Approved – Redeploy" list, the store never emptying.
    // So a cockpit opened from that list names the store to vacate. Opened anywhere
    // else it does not: there a roaming promoter keeping their other stores is intended.
    const vacateStoreId = cockpitVacateStoreRef.current === insightsStore.id ? insightsStore.id : null;
    // Eligibility mode + occupied target → TWO-WAY SWAP: A → target store, and the incumbent
    // B → A's source store (both keep their jobs, both placements become tenure-valid). No NSW.
    const swapping = eligibilityMode && displacesIncumbent && !!b;
    // Hiring at the store(s) someone joins is asked about BEFORE anything is saved (product owner,
    // 22 Oct 2026), over the cockpit; "Don’t deploy" leaves the Review step as it was.
    //   - A swap: both stores were filled again, so neither is left: each one's own hiring.
    //   - A move: the store joined is still short when, with them in it (and anyone replaced
    //     gone), it is below its minimum. The store they came from is asked about once the move
    //     is saved: left, unless a roaming role kept it (the window reads who is still there);
    //     never offered for hiring when it was on Outside Approved – Redeploy before this move
    //     (its people are moved out on purpose, and the server clears the mark as the last one
    //     leaves, so only the store as the cockpit held it can tell).
    const headcountAfter = displacesIncumbent ? target.employeeCount : target.employeeCount + 1;
    const hiringAsk: DeployHiringAsk = swapping && b
      ? {
        joined: [
          { storeId: target.storeId, storeName: target.storeName, personName: redeployEmp.name },
          { storeId: insightsStore.id, storeName: insightsStore.name, personName: b.name },
        ],
      }
      : {
        joined: [{
          storeId: target.storeId,
          storeName: target.storeName,
          personName: redeployEmp.name,
          short: headcountAfter < target.minEmployees,
        }],
        left: {
          storeId: insightsStore.id,
          storeName: insightsStore.name,
          personName: redeployEmp.name,
          personCode: aCode,
          redeployPending: !!vacateStoreId || insightsStore.hiringCard?.raiseRefused === 'redeploy',
        },
      };
    setChainExecuting(true);
    const hiring = await deployHiring.before(hiringAsk);
    if (!hiring.deploy) {
      setChainExecuting(false);
      return;
    }
    let saved = false;
    try {
      if (swapping && b) {
        // swapEmployee moves A to the target and B into the current (source) store in one call.
        await storesAPI.swap({
          currentStoreId: insightsStore.id,
          currentEmployeeCode: aCode,
          newEmployeeCode: b.code,
          newEmployeeName: b.name,
          targetStoreId: target.storeId,
        });
        saved = true;
        alert(`Swap complete — ${redeployEmp.name} → ${target.storeName}; ${b.name} → ${insightsStore.name}. Both remain deployed.`);
      } else {
        await storesAPI.deploy({
          storeId: target.storeId,
          employee_code: aCode,
          isPrimary: !addingAlongsideIncumbent,
          ...(vacateStoreId ? { vacate_store_id: vacateStoreId } : {}),
        });
        saved = true;
        // NSW the displaced incumbent only in the default (scorecard) flow —
        // eligibility mode never removes anyone (occupied → swap above; vacant →
        // simple move here).
        if (displacesIncumbent && b && !eligibilityMode) {
          await storeIntelligenceAPI.createExitInitiation(selectedProjectId, {
            employeeCode: b.code,
            employeeName: b.name,
            storeId: target.storeId,
            storeName: target.storeName,
            reason: `Replaced by ${redeployEmp.name} via redeployment — 6-mo avg ${b.score ?? '—'}${b.quadrant ? ` · ${b.quadrant}` : ''}`,
            avg6m: b.score,
            quadrant: b.quadrant,
            status: 'NSW',
          });
        }
        alert(displacesIncumbent && b
          ? `Redeployment complete — ${b.name} marked NSW and undeployed.`
          : b
            ? `Redeployment complete — added alongside ${b.name} (${target.minEmployees} required at this store).`
            : 'Redeployment complete.');
      }
      closeInsightsModal();
      fetchStores();
      // Refresh the score/roster overlays too — fetchStores only reloads the
      // base store list, not the store-intelligence roster that drives the
      // per-employee rows, so without this the moved employee lingers at the
      // old store until reload.
      setRefreshNonce((n) => n + 1);
    } catch (err) {
      console.error('Redeployment chain failed', err);
      alert('Failed to complete redeployment. Please try again.');
    } finally {
      setChainExecuting(false);
      // Saved: what was chosen for the hiring is done now, after the message. Not saved: nothing is done to hiring.
      if (saved) hiring.saved();
      else hiring.failed();
    }
  }, [insightsStore, redeployEmp, redeployTarget, selectedProjectId, eligibilityMode, deployHiring.before]);

  // Swap modal handlers — the modal is no longer opened from the cockpit (the
  // score-driven tiered flow replaced the cost-basis path), but the modal code
  // stays for the close/confirm path while the feature settles.
  const closeSwapModal = () => {
    setIsSwapModalOpen(false);
    setEmployeeToSwap(null);
    setSwapModalStep(1);
    setSwapSearchTerm('');
    setEligibleEmployees([]);
    setUndeployedEmployees([]);
    setSelectedEmployeeForSwap(null);
    setAvailableStores([]);
    setSelectedNewStore(null);
    setStoreSearchTerm('');
  };

  // Both pop-ups are drawn on Deployment only, but what they hold is the page's, and the
  // page now stays mounted from one dashboard to the next. Left alone, the cockpit came
  // back up unasked on returning to Deployment — and after a project switch it still held
  // the earlier project's store, so an exit could be filed for that store under the new
  // project. They close, and let go of what they held, whenever the open dashboard or the
  // project changes (as the Filters modal does).
  useEffect(() => {
    closeInsightsModal();
    closeSwapModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeCustomId, selectedProjectId]);

  // Load available stores for step 2
  const loadAvailableStores = async () => {
    if (!insightsStore) return;
    setIsLoadingStores(true);
    try {
      // Call API to get available stores with smart sorting
      const response = await storesAPI.getAvailableStores({
        excludeStoreCode: insightsStore.storeCode,
        trade: insightsStore.trade,
        category: insightsStore.category,
        projectId: selectedProjectId ?? undefined,
      });
      const storesData = response.data?.data ?? [];
      setAvailableStores(storesData);
    } catch (err) {
      console.error('Failed to load available stores', err);
      setAvailableStores([]);
    } finally {
      setIsLoadingStores(false);
    }
  };

  // Handle going to step 2
  const handleNextToStoreSelection = () => {
    if (selectedEmployeeForSwap) {
      loadAvailableStores();
      setSwapModalStep(2);
    }
  };

  // Handle going back to step 1
  const handleBackToEmployeeSelection = () => {
    setSwapModalStep(1);
    setSelectedNewStore(null);
    setAvailableStores([]);
    setStoreSearchTerm('');
  };

  // Handle swap execution
  const handleSwapEmployee = async () => {
    if (!insightsStore || !employeeToSwap || !selectedEmployeeForSwap) return;

    const confirmMessage = selectedNewStore
      ? `Are you sure you want to:\n1. Deploy "${selectedEmployeeForSwap.name}" to "${insightsStore.name}" (current store)\n2. Move "${employeeToSwap.name}" to "${selectedNewStore.name}"?`
      : `Are you sure you want to replace "${employeeToSwap.name}" with "${selectedEmployeeForSwap.name}" at "${insightsStore.name}"?`;

    if (!window.confirm(confirmMessage)) return;

    setIsSwapping(true);
    try {
      await storesAPI.swap({
        currentStoreId: insightsStore.id,
        currentEmployeeCode: employeeToSwap.code,
        newEmployeeCode: selectedEmployeeForSwap.code,
        newEmployeeName: selectedEmployeeForSwap.name,
        newEmployeeDateOfJoining: selectedEmployeeForSwap.dateOfJoining,
        newEmployeeSalary: selectedEmployeeForSwap.monthlyCtc ?? undefined,
        newEmployeeDesignation: selectedEmployeeForSwap.designation,
        targetStoreId: selectedNewStore?.id,
      });

      alert('Swap completed successfully!');
      closeSwapModal();
      closeInsightsModal();
      fetchStores(); // Refresh store data
    } catch (err) {
      console.error('Swap failed', err);
      alert('Failed to complete swap. Please try again.');
    } finally {
      setIsSwapping(false);
    }
  };

  // Load the nearby candidate stores once an employee is chosen for redeploy.
  useEffect(() => {
    if (!redeployEmp) return;
    if (redeployCandidates === null) loadRedeployCandidates(redeployRadiusKm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeployEmp]);

  // Rich context (master + team + score) for every employee in the open store,
  // for the month the Step-1 dropdown shows. Refetches when the month changes.
  useEffect(() => {
    if (!isInsightsModalOpen || !insightsStore || !selectedProjectId) return;
    const codes = (insightsStore.deployedEmployees ?? [])
      .map((e) => e.code)
      .filter((c) => c && c !== '—');
    if (codes.length === 0) { setEmpContexts({}); return; }
    let cancelled = false;
    setContextsLoading(true);
    Promise.all(
      codes.map((c) =>
        scorecardAPI.getEmployeeContext(selectedProjectId, c, contextMonth)
          .then((r) => [c, (r.data?.data ?? null) as EmployeeContext | null] as const)
          .catch(() => [c, null] as const)
      )
    )
      .then((entries) => {
        if (cancelled) return;
        const map: Record<string, EmployeeContext> = {};
        for (const [c, ctx] of entries) if (ctx) map[c] = ctx;
        setEmpContexts(map);
      })
      .finally(() => { if (!cancelled) setContextsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsightsModalOpen, insightsStore?.id, insightsStore?.deployedEmployees?.length, contextMonth, selectedProjectId]);

  // Full context of the incumbent at the chosen target store (the employee who
  // will be replaced + set to NSW), anchored to the dashboard month.
  useEffect(() => {
    const code = redeployTarget?.employee?.code;
    if (!code || !selectedProjectId) { setDisplacedContext(null); return; }
    let cancelled = false;
    scorecardAPI.getEmployeeContext(selectedProjectId, code, periodMonth)
      .then((r) => { if (!cancelled) setDisplacedContext((r.data?.data ?? null) as EmployeeContext | null); })
      .catch(() => { if (!cancelled) setDisplacedContext(null); });
    return () => { cancelled = true; };
  }, [redeployTarget?.employee?.code, selectedProjectId, periodMonth]);

  const renderInsightsModal = () => {
    if (!insightsStore || !isInsightsModalOpen) return null;
    const store = insightsStore;
    const monthLabel = monthOptions.find((m) => m.value === periodMonth)?.label ?? '';

    // Candidates arrive tier-sorted from the server (vacant+category →
    // vacant → occupied+category → occupied). Group them for section headers.
    type Tier = { key: string; title: string; accent: string; rows: RedeployCandidate[] };
    const tiers: Tier[] = [
      { key: 'vacant-match', title: `Vacant — same category${store.category ? ` (${store.category})` : ''}`, accent: 'bg-emerald-500', rows: [] },
      { key: 'vacant-other', title: 'Vacant — other categories', accent: 'bg-emerald-300', rows: [] },
      { key: 'occupied-match', title: 'Occupied — same category', accent: 'bg-gray-500', rows: [] },
      { key: 'occupied-other', title: 'Occupied — other categories', accent: 'bg-gray-300', rows: [] },
    ];
    for (const c of redeployCandidates ?? []) {
      const idx = c.isVacant ? (c.categoryMatch ? 0 : 1) : (c.categoryMatch ? 2 : 3);
      tiers[idx].rows.push(c);
    }
    const rowsLoaded = redeployCandidates !== null;

    const selectTarget = (c: RedeployCandidate) => {
      const emp = c.deployedEmployee;
      setRedeployTarget({
        storeId: c.storeId,
        storeName: c.storeName,
        storeCode: c.storeCode,
        isVacant: c.isVacant,
        employeeCount: c.employeeCount,
        minEmployees: c.minEmployees,
        slotsRemaining: c.slotsRemaining,
        employee: emp
          ? { code: emp.employeeCode, name: emp.employeeName, score: emp.avg6mScore, isBottomBoxing: emp.isBottomBoxing, quadrant: emp.quadrant }
          : null,
        // Carried, not recomputed: Review must show the same number the card showed.
        financial: c.financial ?? null,
      });
    };

    const monthLabelFor = (v: string) => monthOptions.find((m) => m.value === v)?.label ?? v;
    const fmtDate = (d?: string | null) => {
      if (!d) return '—';
      const dt = new Date(d);
      return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const Field = ({ label, value, full }: { label: string; value?: string | number | null; full?: boolean }) => (
      <div className={full ? 'col-span-2 sm:col-span-3' : ''}>
        <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
        <p className="truncate font-medium text-gray-800">{value || '—'}</p>
      </div>
    );
    // Label + value with a leading icon — the modern info row used on cards.
    const InfoCell = ({ icon: Icon, label, value }: { icon: any; label: string; value?: string | number | null }) => (
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
          <p className="truncate font-medium text-gray-800">{value || '—'}</p>
        </div>
      </div>
    );
    // Initials for the avatar chip (first + last word).
    const initialsOf = (name: string) => {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '–';
      return ((parts[0][0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
    };
    // Avatar tint by quadrant standing.
    const avatarTint = (q: Quadrant) =>
      q === 'Stars' ? 'bg-emerald-100 text-emerald-700'
      : q === 'Achievers' ? 'bg-sky-100 text-sky-700'
      : q === 'Strivers' ? 'bg-amber-100 text-amber-700'
      : q === 'At Risk' ? 'bg-rose-100 text-rose-700'
      : q === 'New Joiner' ? 'bg-indigo-100 text-indigo-700'
      : 'bg-primary-100 text-primary-700';

    // One candidate-store card; the action depends on the store's state.
    const candidateCard = (c: RedeployCandidate) => {
      const emp = c.deployedEmployee;
      const action = eligibilityMode
        // Eligibility mode: vacant → MOVE the employee in; occupied → a two-way
        // SWAP (nobody is displaced/NSW'd).
        ? (c.isVacant
            ? { label: 'Move here', cls: 'bg-primary-600 hover:bg-primary-700' }
            : { label: 'Swap here', cls: 'bg-primary-600 hover:bg-primary-700' })
        : c.isVacant
          ? { label: 'Deploy here', cls: 'bg-primary-600 hover:bg-primary-700' }
          : emp?.isBottomBoxing
            ? { label: 'Replace here', cls: 'bg-rose-600 hover:bg-rose-700' }
            : { label: 'Replace here', cls: 'bg-amber-500 hover:bg-amber-600' };
      return (
        <div key={c.storeId} className="group rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm transition hover:border-primary-200 hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${c.isVacant ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                <Building2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{c.storeName}</p>
                <p className="truncate text-[11px] text-gray-400">{c.storeCode}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600"><MapPin className="h-3 w-3" />{c.distanceKm} km</span>
              <button
                type="button"
                onClick={() => selectTarget(c)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition ${action.cls}`}
              >
                {action.label}
              </button>
            </div>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
            {/* What this specific button does to the money, across both stores.
                Absent entirely for a user without the financial grant. */}
            {redeployFinancialsVisible && c.financial && <NetImpactChip f={c.financial} compact />}
            {/* Trade drives the ordering now, so it has to be visible on the card —
                otherwise a farther store appearing above a nearer one looks like a bug. */}
            {c.tradeMatch && (
              <span className="rounded-full bg-primary-50 px-2 py-0.5 font-semibold text-primary-700" title="Same store trade as the store you are moving from">
                Same trade
              </span>
            )}
            {c.categoryName && (
              <span className={`rounded-full px-2 py-0.5 font-semibold ${c.categoryMatch ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>
                {c.categoryName}{c.categoryMatch ? ' · match' : ''}
              </span>
            )}
            {/* Eligibility mode: this store's tenure requirement + whether the
                employee being redeployed meets it. */}
            {eligibilityMode && c.eligibility && (
              <span
                className={`rounded-full px-2 py-0.5 font-semibold ${c.eligibility.employeeFits ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}
                title={c.eligibility.employeeFits ? `${redeployEmp?.name || 'This employee'} meets this store's tenure requirement` : `Below this store's tenure requirement`}
              >
                Requires {c.eligibility.reqLabel || 'no tenure'}{c.eligibility.employeeFits ? ' · qualifies ✓' : ''}
              </span>
            )}
            {/* Understaffed-but-occupied: eligible to ADD without displacing anyone. */}
            {c.isVacant && c.employeeCount > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">
                {c.employeeCount}/{c.minEmployees} · needs {c.slotsRemaining} more
              </span>
            )}
            {c.city && <span>{c.city}</span>}
            {c.trade && <><span className="text-gray-300">·</span><span>{c.trade}</span></>}
          </div>
          {c.address && (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-gray-400">
              <MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="line-clamp-1">{c.address}</span>
            </p>
          )}
          <div className="mt-2 rounded-lg bg-gray-50/80 px-2.5 py-2">
            {emp ? (
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  {c.isVacant ? 'Currently working here · stays (adding 1 more)' : 'Currently working here'}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{emp.employeeName || emp.employeeCode}</span>
                  {emp.quadrant ? <QuadrantBadge q={emp.quadrant} /> : null}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span>Score ({monthLabel}) <span className="font-semibold text-gray-700">{emp.monthScore ?? '—'}</span></span>
                  <span>6-mo avg <span className="font-semibold text-gray-700">{emp.avg6mScore ?? '—'}</span></span>
                  <span title="Age On Network — tenure since date of joining">AON <span className="font-semibold text-gray-700">{emp.aonLabel ?? '—'}</span></span>
                  {emp.dateOfJoining && (
                    <span>Joined <span className="font-semibold text-gray-700">{fmtJoinDate(emp.dateOfJoining)}</span></span>
                  )}
                </div>
                {/* Eligibility swap: this incumbent moves into the redeployed
                    employee's CURRENT store — show THAT store's tenure requirement
                    and whether the incumbent meets it (so the swap is valid both ways). */}
                {eligibilityMode && !c.isVacant && (
                  <div className="flex flex-wrap items-center gap-1 text-[11px]">
                    <ArrowLeftRight className="h-3 w-3 shrink-0 text-primary-500" />
                    <span className="text-gray-500">
                      Swaps into {insightsStore?.name} (requires {eligibilityReqLabel(eligibilityDays(insightsStore?.eligibility))})
                    </span>
                    {c.eligibility?.swapValid
                      ? <span className="font-semibold text-emerald-600">· fits ✓</span>
                      : <span className="font-semibold text-amber-600">· tenure short</span>}
                  </div>
                )}
                {emp.reportingManagerName && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-gray-600 ring-1 ring-gray-200">
                    <UserCog className="h-3 w-3 text-gray-400" /> Reports to <span className="font-semibold text-gray-800">{emp.reportingManagerName}</span>
                  </span>
                )}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle className="h-3.5 w-3.5" /> Vacant — ready for deployment</span>
            )}
          </div>
          {/* What this candidate earns today, and what the move does to EACH store —
              the two halves the net figure on the chip above collapses together. */}
          {redeployFinancialsVisible && c.financial && (
            <CandidateFinancialBlock f={c.financial} sourceStoreName={insightsStore?.name ?? 'current store'} />
          )}
        </div>
      );
    };

    // ── Wizard step model ── Employee & action → Destination → Review.
    // NSW skips Destination: picking NSW on Step 1 jumps straight to Review.
    const displacedEmp = redeployTarget?.employee ?? null;
    // An incumbent is only displaced (→ NSW) when the target is genuinely occupied.
    // A "vacant" target with an incumbent is an understaffed store (e.g. Apple L3
    // 1/2): we ADD alongside them, so they are kept, not replaced.
    const willDisplace = !!displacedEmp && !redeployTarget?.isVacant;
    const isNsw = !!nswEmp;
    type Phase = 'employee' | 'destination' | 'review';
    const phase: Phase = isNsw ? 'review' : !redeployEmp ? 'employee' : !redeployTarget ? 'destination' : 'review';
    const wizardSteps = ['Employee', 'Destination', 'Review'];
    const activeIndex = phase === 'employee' ? 0 : phase === 'destination' ? 1 : 2;

    const srcCtx = redeployEmp ? empContexts[redeployEmp.code] ?? null : null;
    const targetCandidate = redeployTarget
      ? (redeployCandidates ?? []).find((c) => c.storeId === redeployTarget.storeId) ?? null
      : null;

    // The master-details grid + score tiles (shared by the full card and the
    // collapsible Step-2 summary).
    const employeeDetailGrid = (em: EmployeeContext['employee'] | undefined, sc: EmployeeContext['score'] | undefined, monthLbl: string, showScores = true) => (
      <>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs sm:grid-cols-3">
          <InfoCell icon={Phone} label="Mobile" value={em?.mobile} />
          <InfoCell icon={UserCog} label="Reporting manager" value={em?.reportingManagerName} />
          <InfoCell icon={CalendarDays} label="Joined" value={fmtDate(em?.dateOfJoining)} />
          <InfoCell icon={Clock} label="AON" value={em?.aon?.label} />
          <InfoCell icon={Users} label="Team size" value={em && em.teamSize ? `${em.teamSize}` : null} />
        </div>
        {showScores && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">Score · {monthLbl}</p>
              <p className="mt-0.5 text-lg font-bold text-gray-900">{sc?.monthScore ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/70 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">6-month avg</p>
              <p className="mt-0.5 text-lg font-bold text-gray-900">{sc?.avg6mScore ?? '—'}</p>
            </div>
          </div>
        )}
      </>
    );

    // Rich employee card — master details + score + status. `actions` adds the
    // Redeploy / NSW buttons (Step 1); omit for read-only context (review).
    const employeeCard = (opts: {
      name: string; code: string; subtitle?: string;
      ctx: EmployeeContext | null; monthLbl: string;
      accent?: 'primary' | 'rose' | 'plain'; label?: string;
      actions?: JSX.Element | null;
      /** Step 1 only: what this store loses by losing this person. */
      money?: { monthlyCtc: number | null; impactIfMoved: number | null } | null;
      /** Step 1 only: fold the master-detail grid away behind a toggle. */
      compact?: boolean;
    }) => {
      const em = opts.ctx?.employee;
      const sc = opts.ctx?.score;
      const displayName = opts.ctx?.employee?.name?.trim() || opts.name?.trim() || 'Unnamed employee';
      const border = opts.accent === 'primary' ? 'border-primary-200'
        : opts.accent === 'rose' ? 'border-rose-200' : 'border-gray-200';
      return (
        <div className={`overflow-hidden rounded-2xl border ${border} bg-white shadow-sm transition hover:shadow-md`}>
          <div className="p-4">
            {/* Identity */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarTint(sc?.quadrant ?? null)}`}>
                  {initialsOf(displayName)}
                </div>
                <div className="min-w-0">
                  {opts.label && <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{opts.label}</span>}
                  <p className="mt-0.5 truncate text-sm font-bold text-gray-900">{displayName}</p>
                  <p className="truncate text-xs text-gray-500">ID {opts.code}{opts.subtitle ? ` · ${opts.subtitle}` : ''}</p>
                </div>
              </div>
              {sc?.quadrant ? <QuadrantBadge q={sc.quadrant} /> : null}
            </div>

            {/* Master details + scores. Compact mode leads with the two facts that
                decide who moves — tenure and score — and folds the rest away. */}
            {opts.compact ? (
              <>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400">AON</p>
                    <p className="font-semibold text-gray-800">{em?.aon?.label ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400">6-month avg</p>
                    <p className="font-semibold text-gray-800">{sc?.avg6mScore ?? '—'}</p>
                  </div>
                </div>
                {opts.money && <EmployeeCtc monthlyCtc={opts.money.monthlyCtc} />}
                <CollapsibleDetails>{employeeDetailGrid(em, sc, opts.monthLbl)}</CollapsibleDetails>
              </>
            ) : (
              <div className="mt-4">{employeeDetailGrid(em, sc, opts.monthLbl)}</div>
            )}

            {opts.actions && <div className="mt-4 flex flex-wrap gap-2">{opts.actions}</div>}
          </div>
        </div>
      );
    };

    // Connector arrow between flow cards (turns downward when the row stacks).
    const flowArrow = (tone: 'primary' | 'rose') => (
      <div className="flex items-center justify-center py-1 sm:py-0">
        <span className={`flex h-8 w-8 items-center justify-center rounded-full ${tone === 'rose' ? 'bg-rose-100 text-rose-600' : 'bg-primary-100 text-primary-700'}`}>
          <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" />
        </span>
      </div>
    );

    // The terminal NSW outcome card.
    const nswCard = (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 p-4 text-center shadow-sm">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600"><AlertTriangle className="h-5 w-5" /></div>
        <p className="mt-2 text-sm font-bold text-rose-700">NSW</p>
        <p className="mt-0.5 text-[11px] text-rose-600/80">Marked NSW &amp; undeployed on confirm</p>
      </div>
    );

    // Compact store node for the review timeline (just the essentials).
    const flowStoreNode = (opts: {
      label: string; name: string; code: string;
      categoryName?: string | null; categoryMatch?: boolean; distanceKm?: number | null; status?: string;
      tone?: 'primary' | 'rose' | 'plain';
    }) => {
      const tone = opts.tone === 'primary' ? 'border-primary-200' : opts.tone === 'rose' ? 'border-rose-200' : 'border-gray-200';
      return (
        <div className={`flex flex-col rounded-xl border ${tone} bg-white p-3 shadow-sm`}>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500"><Building2 className="h-4 w-4" /></div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{opts.label}</p>
              <p className="truncate text-sm font-bold text-gray-900">{opts.name}</p>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
            <span className="text-gray-400">{opts.code}</span>
            {opts.categoryName && <span className={`rounded-full px-1.5 py-0.5 font-semibold ${opts.categoryMatch ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>{opts.categoryName}{opts.categoryMatch ? ' · match' : ''}</span>}
            {opts.distanceKm != null && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{opts.distanceKm} km</span>}
            {opts.status && <span className="truncate text-gray-400">· {opts.status}</span>}
          </div>
        </div>
      );
    };

    // Compact employee node for the review timeline.
    const flowEmpNode = (opts: { label: string; name: string; code: string; ctx: EmployeeContext | null; monthLbl: string; tone?: 'primary' | 'rose' }) => {
      const sc = opts.ctx?.score;
      const displayName = opts.ctx?.employee?.name?.trim() || opts.name?.trim() || 'Unnamed employee';
      const tone = opts.tone === 'rose' ? 'border-rose-200 bg-rose-50/30' : 'border-primary-200 bg-primary-50/30';
      return (
        <div className={`flex flex-col rounded-xl border ${tone} p-3 shadow-sm`}>
          <div className="flex items-center gap-2.5">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${avatarTint(sc?.quadrant ?? null)}`}>{initialsOf(displayName)}</div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{opts.label}</p>
              <p className="truncate text-sm font-bold text-gray-900">{displayName}</p>
              <p className="truncate text-[11px] text-gray-400">ID {opts.code}</p>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
            <span>Score ({opts.monthLbl}) <span className="font-semibold text-gray-800">{sc?.monthScore ?? '—'}</span></span>
            <span>6-mo <span className="font-semibold text-gray-800">{sc?.avg6mScore ?? '—'}</span></span>
            {sc?.quadrant ? <QuadrantBadge q={sc.quadrant} /> : null}
          </div>
        </div>
      );
    };

    // Redeployment timeline: Old store → Employee → New store (compact).
    const renderMoveComparison = () => (
      <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
        {flowStoreNode({ label: 'From (current) store', name: store.name, code: store.storeCode, categoryName: store.category })}
        {flowArrow('primary')}
        {redeployEmp && flowEmpNode({ label: 'Employee', name: redeployEmp.name, code: redeployEmp.code, ctx: srcCtx, monthLbl: monthLabelFor(contextMonth), tone: 'primary' })}
        {flowArrow('primary')}
        {targetCandidate && flowStoreNode({ label: 'To (new) store', name: targetCandidate.storeName, code: targetCandidate.storeCode, categoryName: targetCandidate.categoryName, categoryMatch: targetCandidate.categoryMatch, distanceKm: targetCandidate.distanceKm, status: targetCandidate.deployedEmployee ? (targetCandidate.isVacant ? `Understaffed ${targetCandidate.employeeCount}/${targetCandidate.minEmployees} · ${targetCandidate.deployedEmployee.employeeName || targetCandidate.deployedEmployee.employeeCode} stays` : `Occupied · ${targetCandidate.deployedEmployee.employeeName || targetCandidate.deployedEmployee.employeeCode}`) : 'Vacant', tone: 'primary' })}
      </div>
    );

    // NSW timeline for the replaced employee: their store → Employee → NSW.
    const renderNswFlow = () => !displacedEmp || !targetCandidate ? null : (
      <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
        {flowStoreNode({ label: 'From (current) store', name: targetCandidate.storeName, code: targetCandidate.storeCode, categoryName: targetCandidate.categoryName, categoryMatch: targetCandidate.categoryMatch })}
        {flowArrow('rose')}
        {flowEmpNode({ label: 'Employee (being replaced)', name: displacedEmp.name, code: displacedEmp.code, ctx: displacedContext, monthLbl: monthLabelFor(periodMonth), tone: 'rose' })}
        {flowArrow('rose')}
        {nswCard}
      </div>
    );

    // Eligibility SWAP — the OTHER leg of the swap: the incumbent moves FROM the
    // target store INTO the redeployed employee's current (source) store. Mirrors
    // renderMoveComparison so the review shows both directions of the swap.
    const renderSwapBackFlow = () => !displacedEmp || !targetCandidate ? null : (
      <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
        {flowStoreNode({ label: 'From (their) store', name: targetCandidate.storeName, code: targetCandidate.storeCode, categoryName: targetCandidate.categoryName, categoryMatch: targetCandidate.categoryMatch })}
        {flowArrow('primary')}
        {flowEmpNode({ label: 'Employee (swapping in)', name: displacedEmp.name, code: displacedEmp.code, ctx: displacedContext, monthLbl: monthLabelFor(periodMonth), tone: 'primary' })}
        {flowArrow('primary')}
        {flowStoreNode({ label: 'To (your) store', name: store.name, code: store.storeCode, categoryName: store.category, tone: 'primary' })}
      </div>
    );

    return createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
        onClick={closeInsightsModal}
      >
        <div
          className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header — navy modal header with the step indicator inline */}
        <div className="flex-shrink-0 bg-primary-600 px-6 pt-4 pb-3">
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs uppercase tracking-wide text-primary-200">Store Intelligence · Redeployment</p>
            <button
              type="button"
              onClick={closeInsightsModal}
              className="rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 flex items-center">
            {wizardSteps.map((label, i) => {
              // Destination (index 1) is skipped during an NSW action.
              const skipped = isNsw && i === 1;
              const active = i === activeIndex;
              const done = i < activeIndex && !skipped;
              return (
                <div key={label} className="flex items-center">
                  <div className={`flex items-center gap-2 rounded-full py-1 pl-1 pr-3 transition ${active ? 'bg-white/15' : ''}`}>
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition ${
                      done ? 'bg-emerald-400 text-white shadow-sm'
                        : active ? 'bg-white text-primary-700 shadow'
                        : 'border-2 border-white/30 text-white/60'
                    }`}>
                      {done ? <Check className="h-4 w-4" strokeWidth={3} /> : skipped ? '–' : i + 1}
                    </span>
                    <span className={`whitespace-nowrap text-[13px] ${active ? 'font-semibold text-white' : done ? 'font-medium text-white/85' : 'font-medium text-white/50'} ${skipped ? 'line-through' : ''}`}>{skipped ? 'Skipped' : label}</span>
                  </div>
                  {i < wizardSteps.length - 1 && <span className={`mx-2 h-1 w-10 rounded-full ${done ? 'bg-emerald-400/60' : 'bg-white/15'}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body — one focused wizard step at a time */}
        <div className="flex-1 overflow-y-auto bg-gray-50/40 px-6 py-6">
          <div className="mx-auto max-w-6xl">
            {/* ── Step 1: store + employee context, then Redeploy / NSW ── */}
            {phase === 'employee' && (
              <div className="space-y-5">
                {/* Store details — compact, collapsible (collapsed by default). */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <button
                    type="button"
                    onClick={() => setStoreDetailsOpen((v) => !v)}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition hover:bg-gray-50"
                  >
                    {storeDetailsOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700"><StoreIcon className="h-3.5 w-3.5" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Store details</p>
                      {!storeDetailsOpen && (
                        <p className="truncate text-xs text-gray-500">{[store.name, store.storeCode, store.trade, store.category, store.city].filter(Boolean).join(' · ')}</p>
                      )}
                    </div>
                  </button>
                  {storeDetailsOpen && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-gray-100 p-4 text-xs sm:grid-cols-3">
                      <Field label="Store name" value={store.name} />
                      <Field label="Store code" value={store.storeCode} />
                      <Field label="Trade" value={store.trade} />
                      <Field label="Category" value={store.category} />
                      <Field label="City" value={store.city} />
                      <Field label="State" value={store.state} />
                      <Field label="Pincode" value={store.pinCode} />
                      <div className="col-span-2 sm:col-span-3">
                        <InfoCell icon={MapPin} label="Address" value={store.address} />
                      </div>
                    </div>
                  )}
                </div>

                {/* What this store bills, what its people cost, and whether that is a
                    profit or a loss — the same strip step 2 shows, so the two steps
                    cannot state different numbers for the same store. */}
                {insightsFinancialsVisible && insightsStoreFinancials && (
                  <SourceStoreFinancialStrip f={insightsStoreFinancials} storeName={store.name} />
                )}

                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {store.deployedEmployees.length === 1 ? 'Who is here' : 'Who do you want to move?'}
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {store.deployedEmployees.length === 1
                        ? 'Redeploy them to another store, or initiate NSW.'
                        : 'Pick one — their CTC is shown on each card.'}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500">
                    Score month
                    <select
                      value={contextMonth}
                      onChange={(e) => setContextMonth(e.target.value)}
                      className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none"
                    >
                      {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </label>
                </div>

                {isInsightsLoading ? (
                  <p className="text-sm text-gray-400"><InlineLoader label="Loading store details…" className="flex" /></p>
                ) : store.deployedEmployees.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-400">This store is vacant — no employees deployed.</p>
                ) : (
                  <div className="grid gap-4 xl:grid-cols-2">
                    {store.deployedEmployees.map((emp) => (
                      <div key={`${emp.code}-${emp.name}`}>
                        {employeeCard({
                          name: emp.name,
                          code: emp.code,
                          ctx: empContexts[emp.code] ?? null,
                          monthLbl: monthLabelFor(contextMonth),
                          compact: true,
                          // Absent (not zeroed) for anyone without the financial grant —
                          // the card then simply has no money row.
                          money: redeployFinancialsVisible || insightsFinancialsVisible
                            ? { monthlyCtc: emp.monthlyCtc ?? null, impactIfMoved: emp.impactIfMoved ?? null }
                            : null,
                          actions: (
                            <>
                              <button
                                type="button"
                                onClick={() => startRedeploy(emp)}
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary-700"
                              >
                                Redeploy to another store <span aria-hidden>→</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => startNsw(emp)}
                                className="inline-flex items-center justify-center gap-1 rounded-lg border border-rose-300 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                              >
                                Initiate NSW <span aria-hidden>→</span>
                              </button>
                            </>
                          ),
                        })}
                      </div>
                    ))}
                    {contextsLoading && <p className="text-center text-xs text-gray-400"><InlineLoader label="Loading employee details…" size="xs" className="flex justify-center" /></p>}
                  </div>
                )}
              </div>
            )}

            {/* ── Step 2: pick the destination store ── */}
            {phase === 'destination' && redeployEmp && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setRedeployEmp(null)}
                    className="rounded-lg border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50 hover:text-gray-800"
                    aria-label="Back to employee selection"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Pick a destination store</h3>
                    {eligibilityMode ? (
                      <p className="mt-0.5 text-xs text-gray-500">
                        Stores <span className="font-semibold text-gray-700">{redeployEmp?.name}</span> qualifies for. Same trade first, then nearest.
                      </p>
                    ) : srcCtx?.score?.avg6mScore != null && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        Only stores whose current employee is <span className="font-semibold text-rose-600">At Risk</span> <span className="font-semibold">and</span> has a 6-mo average below{' '}
                        <span className="font-semibold text-gray-700">{srcCtx.score.avg6mScore}</span> are shown. <span className="font-medium text-gray-600">New employees are never displaced</span> — initiate NSW instead.
                      </p>
                    )}
                  </div>
                </div>

                {/* Persistent context — who is being moved (compact, collapsible). */}
                {redeployEmp && (() => {
                  const sc = srcCtx?.score;
                  const displayName = srcCtx?.employee?.name?.trim() || redeployEmp.name;
                  return (
                    <div className="overflow-hidden rounded-xl border border-primary-200 bg-white shadow-sm">
                      <button
                        type="button"
                        onClick={() => setSrcCardOpen((v) => !v)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-primary-50/40"
                      >
                        {srcCardOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${avatarTint(sc?.quadrant ?? null)}`}>{initialsOf(displayName)}</div>
                        <div className="min-w-0 flex-1">
                          <span className="inline-block rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700">Redeploying</span>
                          <p className="mt-0.5 truncate text-sm font-bold text-gray-900">{displayName} <span className="font-normal text-gray-400">· ID {redeployEmp.code}</span></p>
                        </div>
                        <div className="hidden shrink-0 items-center gap-3 text-xs text-gray-500 sm:flex">
                          <span>Score <span className="font-bold text-gray-900">{sc?.monthScore ?? '—'}</span></span>
                          <span>6-mo <span className="font-bold text-gray-900">{sc?.avg6mScore ?? '—'}</span></span>
                          {sc?.quadrant ? <QuadrantBadge q={sc.quadrant} /> : null}
                        </div>
                      </button>
                      {srcCardOpen && (
                        <div className="border-t border-gray-100 p-4">
                          {employeeDetailGrid(srcCtx?.employee, srcCtx?.score, monthLabelFor(contextMonth), false)}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Where the money stands BEFORE anything moves — the baseline every
                    candidate's "Net" chip below is measured against. */}
                {redeployFinancialsVisible && sourceStoreFinancials && (
                  <SourceStoreFinancialStrip f={sourceStoreFinancials} storeName={insightsStore?.name ?? 'This store'} />
                )}

                {/* Search radius slider — drag to widen the search and pull in more stores. */}
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-700">Search radius</span>
                    <span className="rounded-full bg-primary-50 px-2.5 py-0.5 font-bold text-primary-700">{radiusDraft} km</span>
                  </div>
                  <input
                    type="range"
                    min={5}
                    max={50}
                    step={5}
                    value={radiusDraft}
                    onChange={(e) => setRadiusDraft(Number(e.target.value))}
                    onMouseUp={() => loadRedeployCandidates(radiusDraft)}
                    onTouchEnd={() => loadRedeployCandidates(radiusDraft)}
                    onKeyUp={() => loadRedeployCandidates(radiusDraft)}
                    className="mt-3 w-full cursor-pointer accent-primary-600"
                    aria-label="Search radius in kilometres"
                  />
                  <div className="mt-1 flex justify-between text-[10px] font-medium text-gray-400">
                    <span>5 km</span><span>50 km</span>
                  </div>
                  <p className="mt-2 text-[11px] text-gray-500">Vacant stores first (same category prioritised), then occupied stores. Replacing an occupied store sets its current employee to NSW.</p>
                </div>

                <div className="space-y-5">
                  {redeployLoading || !rowsLoaded ? (
                    <p className="px-2 py-6 text-center text-sm text-gray-400"><InlineLoader label="Finding stores…" className="flex justify-center" /></p>
                  ) : (redeployCandidates ?? []).length === 0 ? (
                    <p className="px-2 py-6 text-center text-sm text-gray-400">No stores within {redeployRadiusKm} km. Drag the slider to widen the search.</p>
                  ) : tiers.filter((t) => t.rows.length > 0).map((t) => (
                    <div key={t.key}>
                      <div className="sticky top-0 z-10 -mx-1 mb-2 flex items-center justify-between gap-2 bg-gray-50/95 px-1 py-1.5 backdrop-blur-sm">
                        <div className="flex items-center gap-2">
                          <span className={`h-4 w-1 rounded-full ${t.accent}`} />
                          <span className="text-sm font-bold uppercase tracking-wide text-gray-800">{t.title}</span>
                        </div>
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-500 shadow-sm">{t.rows.length}</span>
                      </div>
                      <div className="grid gap-3 lg:grid-cols-2">{t.rows.map(candidateCard)}</div>
                    </div>
                  ))}

                  {/* Explains a list that looks short for the radius: stores with no
                      category are withheld, because with no category there is no
                      eligibility criteria to validate the move against. */}
                  {rowsLoaded && !redeployLoading && redeployExcludedUncat > 0 && (
                    <p className="px-2 pt-1 text-center text-[11px] text-gray-400">
                      {redeployExcludedUncat} nearby {redeployExcludedUncat === 1 ? 'store is' : 'stores are'} hidden — no store category defined, so there is no eligibility criteria to check.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Step 3: review & confirm NSW (Destination skipped) ── */}
            {phase === 'review' && nswEmp && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setNswEmp(null)}
                    className="rounded-lg border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50 hover:text-gray-800"
                    aria-label="Back to employees"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Review &amp; confirm</h3>
                    <p className="mt-0.5 text-xs text-gray-500">Confirm the action below.</p>
                  </div>
                </div>

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Initiate NSW</p>
                {/* Flow: current store → employee → NSW. */}
                <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
                  {flowStoreNode({ label: 'From (current) store', name: store.name, code: store.storeCode, categoryName: store.category })}
                  {flowArrow('rose')}
                  {flowEmpNode({ label: 'Employee (NSW)', name: nswEmp.name, code: nswEmp.code, ctx: empContexts[nswEmp.code] ?? null, monthLbl: monthLabelFor(contextMonth), tone: 'rose' })}
                  {flowArrow('rose')}
                  {nswCard}
                </div>

                <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                  <p className="text-sm font-semibold text-rose-700">Initiate NSW</p>
                  <p className="mt-0.5 text-xs text-rose-600/90">{empContexts[nswEmp.code]?.employee?.name?.trim() || nswEmp.name} will be marked NSW and undeployed from {store.name} when you confirm.</p>
                </div>

                <button
                  type="button"
                  onClick={confirmNsw}
                  disabled={chainExecuting}
                  className="w-full rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:opacity-50"
                >
                  {chainExecuting ? 'Applying…' : 'Confirm NSW'}
                </button>
              </div>
            )}

            {/* ── Step 3: review & confirm (replaced employee → NSW) ── */}
            {phase === 'review' && redeployEmp && redeployTarget && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setRedeployTarget(null)}
                    className="rounded-lg border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50 hover:text-gray-800"
                    aria-label="Back to store selection"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Review &amp; confirm</h3>
                    <p className="mt-0.5 text-xs text-gray-500">Check the move below, then confirm.</p>
                  </div>
                </div>

                {/* Redeployment flow: Old store → Employee → New store. */}
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Redeployment</p>
                {renderMoveComparison()}

                {willDisplace && displacedEmp ? (
                  eligibilityMode ? (
                    // Eligibility mode → a TWO-WAY SWAP: the incumbent moves into A's
                    // source store (they meet its tenure band); nobody is NSW'd.
                    // Show the incumbent's leg of the swap visually (mirrors the
                    // redeployment timeline above).
                    <div className="space-y-3 rounded-2xl border border-primary-200 bg-primary-50/50 p-4">
                      <div>
                        <p className="text-sm font-semibold text-primary-700">Two-way swap · both stay deployed</p>
                        <p className="mt-0.5 text-xs text-primary-700/90">
                          {displacedContext?.employee?.name?.trim() || displacedEmp.name} moves into {insightsStore?.name} (they meet its tenure requirement), and {srcCtx?.employee?.name?.trim() || redeployEmp.name} takes their place. No NSW.
                        </p>
                      </div>
                      {renderSwapBackFlow()}
                    </div>
                  ) : (
                    <div className="space-y-3 rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                      <div>
                        <p className="text-sm font-semibold text-rose-700">Replaced employee → NSW</p>
                        <p className="mt-0.5 text-xs text-rose-600/90">{displacedContext?.employee?.name?.trim() || displacedEmp.name} will be marked NSW and undeployed from {redeployTarget.storeName} when you confirm.</p>
                      </div>
                      {/* NSW flow: their store → Employee → NSW. */}
                      {renderNswFlow()}
                    </div>
                  )
                ) : displacedEmp ? (
                  // Understaffed target (e.g. Apple L3 1/2): add alongside, keep incumbent.
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-800">
                    Understaffed store ({redeployTarget.employeeCount}/{redeployTarget.minEmployees}) — {srcCtx?.employee?.name?.trim() || redeployEmp.name} will be added alongside {displacedContext?.employee?.name?.trim() || displacedEmp.name}, who stays.
                  </p>
                ) : (
                  <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-700">
                    This store is vacant — {srcCtx?.employee?.name?.trim() || redeployEmp.name} will simply move in.
                  </p>
                )}

                {/* What this move costs or earns, once both stores are re-priced. */}
                {redeployFinancialsVisible && redeployTarget.financial && (
                  <FinancialImpactPanel
                    f={redeployTarget.financial}
                    fromName={insightsStore?.name ?? 'Current store'}
                    toName={redeployTarget.storeName}
                    employeeName={srcCtx?.employee?.name?.trim() || redeployEmp.name}
                    incumbentName={displacedContext?.employee?.name?.trim() || redeployTarget.employee?.name || null}
                  />
                )}

                <button
                  type="button"
                  onClick={executeRedeploymentChain}
                  disabled={chainExecuting}
                  className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-50 ${willDisplace && !eligibilityMode ? 'bg-rose-600 hover:bg-rose-700' : 'bg-primary-600 hover:bg-primary-700'}`}
                >
                  {chainExecuting
                    ? 'Applying…'
                    : eligibilityMode
                      ? (willDisplace ? 'Confirm swap' : 'Confirm move')
                      : willDisplace ? 'Confirm redeployment + NSW' : 'Confirm redeployment'}
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderSwapModal = () => {
    if (!isSwapModalOpen || !employeeToSwap || !insightsStore) return null;

    return createPortal(
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/30 backdrop-blur-sm">
        <div className="flex h-[90vh] w-full max-w-6xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
          {/* Left Side - Employee Selection */}
          <div className="flex flex-1 flex-col">
            {/* Header */}
            <div className="flex-shrink-0 rounded-tl-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-blue-800">Smart Employee & Store Suggestions</h2>
                  <div className="mt-2 flex items-center">
                    <div className={`flex items-center ${swapModalStep === 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                      <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${swapModalStep === 1 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>1</div>
                      <span className="ml-2 text-sm font-medium">Select Employee</span>
                    </div>
                    <div className="mx-3 h-px w-8 bg-gray-300"></div>
                    <div className={`flex items-center ${swapModalStep === 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                      <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${swapModalStep === 2 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>2</div>
                      <span className="ml-2 text-sm font-medium">Select Store</span>
                    </div>
                  </div>
                </div>
                <button onClick={closeSwapModal} className="text-blue-400 hover:text-blue-600" aria-label="Close">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Step 1 Content - Employee Selection */}
            {swapModalStep === 1 && (
              <>
                {/* Tabs */}
                <div className="flex-shrink-0 border-b border-gray-100 bg-white">
                  <nav className="-mb-px flex" aria-label="Tabs">
                    <button
                      onClick={() => setSwapModalActiveTab('deployed')}
                      className={`w-1/2 border-b-2 px-1 py-4 text-center text-sm font-medium ${
                        swapModalActiveTab === 'deployed'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      Deployed (Eligible)
                      <span className={`ml-2 rounded-full px-2 py-1 text-xs ${swapModalActiveTab === 'deployed' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                        {isLoadingEligibleEmployees ? '...' : eligibleEmployees.length}
                      </span>
                    </button>
                    <button
                      onClick={() => setSwapModalActiveTab('undeployed')}
                      className={`w-1/2 border-b-2 px-1 py-4 text-center text-sm font-medium ${
                        swapModalActiveTab === 'undeployed'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      Undeployed (Eligible)
                      <span className={`ml-2 rounded-full px-2 py-1 text-xs ${swapModalActiveTab === 'undeployed' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                        {isLoadingEligibleEmployees ? '...' : undeployedEmployees.length}
                      </span>
                    </button>
                  </nav>
                </div>

                {/* Search */}
            <div className="flex-shrink-0 p-4">
              <input
                type="text"
                placeholder={`Search ${swapModalActiveTab} employees by name or code...`}
                value={swapSearchTerm}
                onChange={(e) => setSwapSearchTerm(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingEligibleEmployees ? (
                <InlineLoader label="Loading employees..." className="flex justify-center py-8" />
              ) : swapModalActiveTab === 'undeployed' ? (
                undeployedEmployees.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="mb-2 mt-2 text-lg font-medium text-gray-900">No Undeployed Employees Available</h3>
                    <p className="text-sm">No undeployed employees meet the eligibility criteria for this store category. Try switching to the other tab.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {undeployedEmployees
                      .filter(emp => {
                        if (!swapSearchTerm) return true;
                        const term = swapSearchTerm.toLowerCase();
                        return emp.name.toLowerCase().includes(term) || emp.code.toLowerCase().includes(term);
                      })
                      .map((emp, idx) => {
                        const costDiff = (employeeToSwap?.monthlyCtc ?? 0) - (emp.monthlyCtc ?? 0);
                        const cityMatch = insightsStore?.city === emp.city;
                        const isSelected = selectedEmployeeForSwap?.code === emp.code;
                        return (
                          <div 
                            key={`${emp.code}-${idx}`} 
                            onClick={() => setSelectedEmployeeForSwap(emp)}
                            className={`cursor-pointer rounded-lg border p-5 transition-all hover:shadow-sm ${
                              isSelected 
                                ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}>
                            <div className="mb-4 flex items-start justify-between">
                              <div>
                                <h4 className="mb-1 text-lg font-semibold text-gray-800">{emp.name}</h4>
                                <div className="flex items-center space-x-2 text-sm text-gray-500">
                                  <span>Code: {emp.code}</span>
                                  <span>•</span>
                                  <span>Joined: {emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString() : '—'}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="mb-1 text-xs text-gray-500">Cost Impact</div>
                                <div className={`text-lg font-bold ${costDiff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {costDiff > 0 ? '+' : ''}₹{costDiff.toLocaleString()}
                                </div>
                              </div>
                            </div>

                            <div className="mb-4 grid grid-cols-2 gap-4">
                              <div className="rounded-md bg-gray-50 p-3">
                                <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Status</h5>
                                <p className="text-sm font-medium text-orange-600">Not Deployed</p>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="rounded-md bg-gray-50 p-3">
                                  <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Monthly CTC</h5>
                                  <p className="text-sm font-medium text-gray-800">{formatCurrency(emp.monthlyCtc)}</p>
                                </div>
                                <div className="rounded-md bg-gray-50 p-3">
                                  <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Seed Score</h5>
                                  <p className={`text-sm font-medium ${emp.seedScore ? 'text-blue-600' : 'text-gray-400'}`}>{emp.seedScore ?? 'N/A'}</p>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {cityMatch && <span className="rounded-full border border-purple-200 bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">✓ City Match</span>}
                              {costDiff > 0 && <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">✓ Cost Reduction</span>}
                              <span className="rounded-full border border-orange-200 bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">Available</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )
              ) : eligibleEmployees.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mb-2 mt-2 text-lg font-medium text-gray-900">No Deployed Employees Found</h3>
                  <p className="text-sm">No other deployed employees available for swap.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {eligibleEmployees
                    .filter(emp => {
                      if (!swapSearchTerm) return true;
                      const term = swapSearchTerm.toLowerCase();
                      return emp.name.toLowerCase().includes(term) || emp.code.toLowerCase().includes(term);
                    })
                    .map((emp, idx) => {
                      const costDiff = (employeeToSwap?.monthlyCtc ?? 0) - (emp.monthlyCtc ?? 0);
                      const tradeMatch = insightsStore?.trade === emp.store.trade;
                      const cityMatch = insightsStore?.city === emp.store.city;
                      const categoryMatch = insightsStore?.category === emp.store.category;
                      const isSelected = selectedEmployeeForSwap?.code === emp.code;
                      return (
                        <div 
                          key={`${emp.code}-${idx}`} 
                          onClick={() => setSelectedEmployeeForSwap(emp)}
                          className={`cursor-pointer rounded-lg border p-5 transition-all hover:shadow-sm ${
                            isSelected 
                              ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}>
                          <div className="mb-4 flex items-start justify-between">
                            <div>
                              <h4 className="mb-1 text-lg font-semibold text-gray-800">{emp.name}</h4>
                              <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <span>Code: {emp.code}</span>
                                <span>•</span>
                                <span>Joined: {emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString() : '—'}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="mb-1 text-xs text-gray-500">Cost Impact</div>
                              <div className={`text-lg font-bold ${costDiff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {costDiff > 0 ? '+' : ''}₹{costDiff.toLocaleString()}
                              </div>
                            </div>
                          </div>

                          <div className="mb-4 grid grid-cols-2 gap-4">
                            <div className="rounded-md bg-gray-50 p-3">
                              <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Deployment Status</h5>
                              <p className="text-sm font-medium text-blue-600">{emp.store.name}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="rounded-md bg-gray-50 p-3">
                                <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Monthly CTC</h5>
                                <p className="text-sm font-medium text-gray-800">{formatCurrency(emp.monthlyCtc)}</p>
                              </div>
                              <div className="rounded-md bg-gray-50 p-3">
                                <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Seed Score</h5>
                                <p className={`text-sm font-medium ${emp.seedScore ? 'text-blue-600' : 'text-gray-400'}`}>{emp.seedScore ?? 'N/A'}</p>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {tradeMatch && <span className="rounded-full border border-green-200 bg-green-100 px-3 py-1 text-xs font-medium text-green-700">✓ Trade Match</span>}
                            {cityMatch && <span className="rounded-full border border-purple-200 bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">✓ City Match</span>}
                            {categoryMatch && <span className="rounded-full border border-blue-200 bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">✓ Category Match</span>}
                            {costDiff > 0 && <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">✓ Cost Reduction</span>}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Footer - Step 1 */}
            <div className="flex-shrink-0 border-t border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {selectedEmployeeForSwap 
                    ? `Selected: ${selectedEmployeeForSwap.name}` 
                    : 'Please select an employee to continue'}
                </div>
                <div className="flex items-center space-x-3">
                  <button type="button" onClick={closeSwapModal} className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200">
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={handleNextToStoreSelection}
                    disabled={!selectedEmployeeForSwap}
                    className={`rounded-md px-4 py-2 text-sm font-medium ${
                      selectedEmployeeForSwap
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'cursor-not-allowed bg-gray-200 text-gray-400'
                    }`}
                  >
                    Next: Select Store
                  </button>
                </div>
              </div>
            </div>
              </>
            )}

            {/* Step 2 - Store Selection */}
            {swapModalStep === 2 && (
              <>
                {/* Store Selection Header */}
                <div className="flex-shrink-0 border-b border-gray-200 bg-white p-4">
                  <h3 className="text-lg font-medium text-gray-800">Select Store for {employeeToSwap?.name}</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Choose a new store where the existing employee should be deployed after being replaced by {selectedEmployeeForSwap?.name}
                  </p>
                </div>

                {/* Store Search */}
                <div className="flex-shrink-0 p-4">
                  <input
                    type="text"
                    placeholder="Search stores by name, city, or Store Code..."
                    value={storeSearchTerm}
                    onChange={(e) => setStoreSearchTerm(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                {/* Store List */}
                <div className="flex-1 overflow-y-auto p-4">
                  {isLoadingStores ? (
                    <InlineLoader label="Loading available stores..." className="flex justify-center py-8" />
                  ) : availableStores.length === 0 ? (
                    <div className="py-8 text-center text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <h3 className="mb-2 mt-2 text-lg font-medium text-gray-900">No Available Stores</h3>
                      <p className="text-sm">No eligible stores found for this employee.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {availableStores
                        .filter(store => {
                          if (!storeSearchTerm) return true;
                          const term = storeSearchTerm.toLowerCase();
                          return (
                            store.name.toLowerCase().includes(term) ||
                            store.city.toLowerCase().includes(term) ||
                            store.storeCode.toLowerCase().includes(term)
                          );
                        })
                        .map((store) => {
                          const isStoreSelected = selectedNewStore?.id === store.id;
                          const tradeMatch = store.trade === insightsStore?.trade;
                          const categoryMatch = store.category?.name === insightsStore?.category;
                          const payoutRange = store.category?.payoutMin && store.category?.payoutMax 
                            ? `${formatCurrency(store.category.payoutMin)} - ${formatCurrency(store.category.payoutMax)}`
                            : store.category?.payoutMax 
                              ? formatCurrency(store.category.payoutMax) 
                              : 'N/A';
                          const isVacant = !store.employeeCode || store.status === 'Vacant';
                          return (
                            <div
                              key={store.id}
                              onClick={() => setSelectedNewStore(store)}
                              className={`cursor-pointer rounded-lg border p-5 transition-all ${
                                isStoreSelected
                                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="mb-3 flex items-start justify-between">
                                <div>
                                  <h4 className="font-medium text-gray-900">{store.name}</h4>
                                  <p className="text-sm text-gray-600">Store Code: {store.storeCode}</p>
                                </div>
                                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                  store.status === 'Deployed' ? 'bg-green-100 text-green-800' 
                                    : store.status === 'Vacant' ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {store.status}
                                </span>
                              </div>

                              <div className="mb-3 grid grid-cols-4 gap-3 text-sm">
                                <div>
                                  <span className="block text-xs font-medium text-gray-600">Location:</span>
                                  <p className="text-xs text-gray-900">{store.city}{store.state ? `, ${store.state}` : ''}</p>
                                </div>
                                <div>
                                  <span className="block text-xs font-medium text-gray-600">Trade:</span>
                                  <p className="text-xs text-gray-900">{store.trade || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="block text-xs font-medium text-gray-600">Category:</span>
                                  <p className="text-xs text-gray-900">{store.category?.name || 'N/A'}</p>
                                </div>
                                <div>
                                  <span className="block text-xs font-medium text-gray-600">Payout Range:</span>
                                  <p className="text-xs font-medium text-gray-900">{payoutRange}</p>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {tradeMatch && <span className="rounded-full border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">✓ Trade Match</span>}
                                {categoryMatch && <span className="rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">✓ Category Match</span>}
                                {isVacant && <span className="rounded-full border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">Vacant</span>}
                              </div>

                              {store.employeeName && (
                                <div className="mt-2 border-t border-gray-100 pt-2">
                                  <span className="text-xs text-gray-500">Current Employee: {store.employeeName}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Footer - Step 2 */}
                <div className="flex-shrink-0 border-t border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {selectedNewStore 
                        ? `Selected store: ${selectedNewStore.name}` 
                        : 'Please select a store to complete the process'}
                    </div>
                    <div className="flex items-center space-x-3">
                      <button 
                        type="button" 
                        onClick={handleBackToEmployeeSelection} 
                        className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                      >
                        Back
                      </button>
                      <button 
                        type="button" 
                        onClick={handleSwapEmployee}
                        disabled={!selectedNewStore || isSwapping}
                        className={`rounded-md px-4 py-2 text-sm font-medium ${
                          selectedNewStore && !isSwapping
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'cursor-not-allowed bg-gray-200 text-gray-400'
                        }`}
                      >
                        {isSwapping ? 'Processing...' : 'Complete Swap'}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Side - Current Employee & Store Info */}
          <div className="flex w-96 flex-col rounded-tr-xl border-l border-gray-200 bg-gray-50">
            <div className="border-b border-gray-200 p-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Current Employee */}
                <div>
                  <h3 className="mb-3 text-lg font-medium text-gray-800">Current Employee</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Employee Name:</span>
                      <span className="text-xs text-gray-800">{employeeToSwap.name}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Employee Code:</span>
                      <span className="text-xs text-gray-800">{employeeToSwap.code}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Fixed CTC (Monthly):</span>
                      <span className="text-xs font-medium text-green-600">{formatCurrency(employeeToSwap.monthlyCtc)}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Date of Joining:</span>
                      <span className="text-xs text-gray-800">{employeeToSwap.dateOfJoining ? new Date(employeeToSwap.dateOfJoining).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Seed Score:</span>
                      <span className={`text-xs font-medium ${employeeToSwap.seedScore ? 'text-blue-600' : 'text-gray-400'}`}>{employeeToSwap.seedScore ?? 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Current Store */}
                <div>
                  <h3 className="mb-3 text-lg font-medium text-gray-800">Current Store</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Store Name:</span>
                      <span className="text-xs text-gray-800">{insightsStore.name}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Store Code:</span>
                      <span className="text-xs text-gray-800">{insightsStore.storeCode}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Trade:</span>
                      <span className="text-xs text-gray-800">{insightsStore.trade}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Store Category:</span>
                      <span className="text-xs text-gray-800">{insightsStore.category}</span>
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-600">Required Seed Score:</span>
                      <span className={`text-xs font-medium ${insightsStore.storeSeedScore ? 'text-blue-600' : 'text-gray-400'}`}>{insightsStore.storeSeedScore ?? 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Eligibility Criteria */}
              <div className="mt-4 border-t border-gray-200 pt-3">
                <h4 className="mb-2 text-sm font-medium text-gray-700">Eligibility Criteria</h4>
                <div className="space-y-2 text-xs">
                  {insightsStore.eligibility ? (
                    <div className="rounded bg-blue-50 p-2 text-gray-800">{insightsStore.eligibility}</div>
                  ) : (
                    <div className="italic text-gray-500">No specific criteria defined</div>
                  )}
                  {insightsStore.storeSeedScore && (
                    <div className="flex items-center">
                      <span className="mr-2 h-2 w-2 rounded-full bg-purple-500"></span>
                      <span className="text-gray-700">Required Seed Level: {insightsStore.storeSeedScore}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Smart Sorting Criteria */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="mr-2 h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                    <h4 className="text-sm font-medium text-blue-800">Smart Sorting Criteria</h4>
                  </div>
                  <button className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200">
                    Customize Sorting
                  </button>
                </div>
                <div className="space-y-1 text-xs text-blue-700">
                  {defaultSortingCriteria.filter(c => c.enabled).map((criterion, index) => (
                    <div key={criterion.id} className="flex items-center">
                      <span className="mr-1 font-medium text-blue-800">{index + 1}.</span>
                      <span>{criterion.name}</span>
                      {criterion.id === 'tradeMatch' && <span className="ml-1 text-blue-600">({insightsStore.trade})</span>}
                      {criterion.id === 'categoryMatch' && <span className="ml-1 text-blue-600">({insightsStore.category})</span>}
                      {criterion.id === 'cityMatch' && <span className="ml-1 text-blue-600">({insightsStore.city})</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-blue-600">
                  <span className="font-medium">Note:</span> Employees are sorted by the criteria above in priority order.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (leaveForHome) return <Navigate to={DASHBOARD_HOME_PATH} replace />;

  // Onboarding, Uniform & Asset Management and Data Hygiene are always-live lists (see
  // their notes further down): the month reaches none of them, and of the filters only
  // Role reaches one, Data Hygiene, which keeps the Filters button and drops the Month.
  const isLiveDashboard = activeTab !== null && activeTab !== 'Custom' && DASHBOARD_INFO[activeTab].live === true;

  return (
    <div className={PAGE}>
      {/* The dashboard's name, with the Month filter (anchors per-employee scores + the
          6-month window that decides quadrants and At-Risk highlighting) and the Filters
          button on the right. No sentence under the name. The platform is real-time, so
          there's no manual refresh. */}
      <DashboardHeader
        tab={activeTab === 'Custom' ? null : activeTab}
        customTitle={activeCustomDashboard?.title}
        // "Last updated" and the two amber messages are about the store fetch, which
        // only Deployment draws from. On any other dashboard they described data that
        // was not on screen.
        status={activeTab === 'Deployment' ? (
          <>
            <p className="text-xs text-gray-400">Last updated: {lastUpdatedLabel}</p>
            {error && <p className="text-xs text-amber-600">{error}</p>}
          </>
        ) : undefined}
        // Every always-live dashboard says so the same way, in two words (the reason is
        // on hover). Uniform & Asset Management no longer prints a sentence of its own.
        liveNote={isLiveDashboard}
        allowedTabs={shownTabs}
        customDashboards={customDashboards}
        activeCustomId={activeCustomId}
        // Hiring's own buttons are the whole row: no "How to read this", no "Other dashboards".
        plain={activeTab === 'Hiring'}
        controls={activeTab === 'Hiring' ? (
          <div ref={setHiringActionSlot} className="flex flex-wrap items-center gap-2" />
        ) : isLiveDashboard && !roleFilterOnly ? undefined : (
          <>
            {/* Not on a custom dashboard: its blocks take their own From and To dates, and
                a Month box there changed nothing. */}
            {!isLiveDashboard && activeTab !== 'Custom' && (
              <>
                <span className="text-xs font-medium text-gray-400">Month</span>
                <select
                  value={activeTab === 'EWS' ? ewsEndMonth : periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                  className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none"
                  title={activeTab === 'EWS' ? 'The month shown' : 'Month under review'}
                >
                  {(activeTab === 'EWS' ? ewsMonthOptions : monthOptions).map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </>
            )}
            {/* Opens the generic filter modal (Roles + Month always, plus the
                contextual fields for the active dashboard / Deployment sub-view). On a
                custom dashboard it offers the six filters the server applies to every
                block (custom-dashboards/dashboard-filters.ts). */}
            {(
              <button
                type="button"
                onClick={() => setShowFilterModal(true)}
                className={`flex h-10 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold shadow-sm transition ${
                  shownFilterCount > 0
                    ? 'border-primary-200 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Open filters"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {shownFilterCount > 0 && (
                  <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-bold text-white">
                    {shownFilterCount}
                  </span>
                )}
              </button>
            )}
          </>
        )}
      />

      {activeTab === 'Custom' && activeCustomDashboard && (
        <CustomDashboardView
          // Remount on dashboard switch so per-view state (parameter values, run
          // results) cannot bleed from one dashboard into the next.
          key={activeCustomDashboard.id}
          dashboard={activeCustomDashboard}
          canAuthor={canAuthorDashboards}
          onChanged={(next) =>
            setCustomDashboards((list) => list.map((d) => (d.id === next.id ? next : d)))
          }
          // Out of the list, its address names a dashboard that is gone: the page
          // leaves for the home screen by the same rule as any other dead address.
          onDeleted={() =>
            setCustomDashboards((list) => list.filter((d) => d.id !== activeCustomDashboard.id))
          }
          filters={customDashboardFilters}
        />
      )}

      {activeTab === 'Deployment' && (
        <>
      {/* The only place that says WHY the counts are blank and what to do about it —
          the cards themselves just read "Live roster unavailable". Not cached
          server-side in this state, so a reload genuinely retries the lookup. */}
      {rosterUnresolved && !isLoading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span className="font-semibold">Deployment counts unavailable.</span>{' '}
          The live employee roster could not be reached, so store and headcount totals would
          still include people who have already exited. They are hidden rather than shown wrong —
          reload to retry.
        </div>
      )}

      {/* A past month: which day the tab shows (month-end mode). Amber only when that day
          predates deployment history, i.e. the picture is approximate. */}
      {monthEndAsOf && !isLoading && (
        <div className={`rounded-lg border px-4 py-2.5 text-sm ${monthEndApproximate ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-700'}`}>
          Showing stores and deployments as they stood on{' '}
          <span className="font-semibold">{formatDayLabel(monthEndAsOf)}</span>, the last day of the selected month.
          {monthEndApproximate && intelligence?.historyStartsOn && (
            <>{' '}Deployment history only starts on {formatDayLabel(intelligence.historyStartsOn)}, so this month is approximate: moves and exits before then weren&apos;t recorded.</>
          )}{' '}
          Undeployed Employees and {STORE_STATUS_REDEPLOY} still show today.
        </div>
      )}

      {/* The headline numbers: a grey label, one bold number, and a short grey line under
          it (the longer explanation is the tile's hover text). A number that has a list
          behind it is a button that opens that list (see statCards). */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {isLoading ? (
          <>
            {/* The tile's shape, each bar in a line as tall as the words it stands for, so
                nothing under the strip moves when the numbers arrive. */}
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className={TILE}>
                <div className="flex h-5 items-center"><Skeleton className="h-3.5 w-24 max-w-full" /></div>
                <div className="mt-2 flex h-9 items-center"><Skeleton className="h-7 w-14" /></div>
                <div className="mt-1 flex h-4 items-center"><Skeleton className="h-3 w-28 max-w-full" /></div>
              </div>
            ))}
          </>
        ) : (
          statCards.map((card) => {
            const { list } = card;
            const figures = (
              <>
                {/* mt-auto: where a label beside it runs to a second line, the numbers of
                    the row still stand level. */}
                <p className={`mt-auto pt-2 text-3xl font-bold tabular-nums tracking-tight ${card.accent ? accentClasses[card.accent] : 'text-gray-900'}`}>
                  {card.value}
                </p>
                <p className="mt-1 truncate text-xs text-gray-500">{card.helper}</p>
              </>
            );
            // Never cut: on a narrow window a long label runs to a second line, as it does
            // on the other dashboards' tiles.
            const label = <p className="min-w-0 text-sm font-medium text-gray-500">{card.label}</p>;
            if (!list) {
              return (
                <div key={card.label} className={`flex flex-col ${TILE}`} title={card.hint ?? card.helper}>
                  {label}
                  {figures}
                </div>
              );
            }
            // The same tile as a button, with a card's hover and focus ring and the home
            // screen's arrow, so a number that opens a list looks the same on every
            // dashboard.
            return (
              <button
                key={card.label}
                type="button"
                onClick={() => openDeploymentList(list)}
                title={card.hint ?? card.helper}
                className={`group flex flex-col ${TILE} text-left transition hover:border-primary-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
              >
                {/* items-start, and the arrow a hair down: level with the label's first line. */}
                <div className="flex items-start justify-between gap-2">
                  {label}
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600" aria-hidden />
                </div>
                {figures}
              </button>
            );
          })
        )}
      </section>

      {/* Table selector — show ONE report table at a time, picked from the top,
          so the user chooses what to see instead of scrolling past all three.
          Counts (where loaded) help the user decide which table to open. */}
      {/* scroll-mt: when a number brings the strip to the top, it stops clear of the top
          bar, which stays pinned over the page. */}
      <div ref={deploymentListsRef} className="flex scroll-mt-32 flex-wrap items-center gap-2">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1">
          {([
            { key: 'Store Performance' as const, count: displayStores.length },
            { key: 'Bottom Box' as const, count: bottomBox ? bottomBoxEmployees.length : null },
            { key: 'Undeployed Stores' as const, count: rosterUnresolved ? null : undeployedStoresList.length },
            { key: 'MisDeployed Stores' as const, count: misDeployedStores.length },
            { key: 'Undeployed Employees' as const, count: undeployedEmpsFiltered.length },
            { key: STORE_STATUS_REDEPLOY, count: redeployPendingState === 'ready' ? redeployPendingRows.length : null },
          ]).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setDeploymentTable(t.key)}
              className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                deploymentTable === t.key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t.key}{t.count != null ? ` (${t.count})` : ''}
            </button>
          ))}
        </div>
      </div>
      {/* No line here saying what the open list holds: the list's own card says it, under
          its heading, and saying it twice was one sentence too many. */}

      {deploymentTable === 'Store Performance' && (
      <section className={LIST_CARD}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Store Performance Analytics</h2>
            {/* One line, one fact. An unscored project still lists its full roster — say why
                the two scorecard columns are blank instead of describing a ranking it has no
                data for. */}
            {intelligence && !intelligence.lenses?.scorecard ? (
              <p className="text-xs text-amber-700">
                No scorecard is configured for this project — Standing and 6-mo Avg stay blank. Deployment details are live.
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                Store performance = the deployed employees' scorecard standing. At-Risk stores first.
              </p>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <SortHeader label="Store Details" sortKey="store" active={sortBy === 'store'} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Category & City" sortKey="category" active={sortBy === 'category'} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Employees Deployed" sortKey="employees" active={sortBy === 'employees'} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Employee" sortKey="employee" active={sortBy === 'employee'} dir={sortDir} onSort={handleSort} />
                <SortHeader label="AON" sortKey="aon" active={sortBy === 'aon'} dir={sortDir} onSort={handleSort} title="Age On Network — tenure since date of joining" />
                <SortHeader label="Standing" sortKey="standing" active={sortBy === 'standing'} dir={sortDir} onSort={handleSort} />
                <SortHeader label="6-mo Avg" sortKey="avg6m" active={sortBy === 'avg6m'} dir={sortDir} onSort={handleSort} align="right" />
                <th className="sticky right-0 z-20 whitespace-nowrap bg-gray-50 px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white text-left">
              {isLoading ? (
                <>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-3">
                          <Skeleton className="mt-1 h-2.5 w-2.5 rounded-full" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-32" />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-20" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Skeleton className="h-4 w-8" />
                      </td>
                      <td className="px-6 py-4">
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Skeleton className="h-4 w-16 ml-auto" />
                      </td>
                      <td className="px-6 py-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-16 rounded-full" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                      </td>
                      <td className="sticky right-0 z-20 bg-white px-6 py-4 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">
                        <div className="flex items-center justify-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-lg" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : displayStores.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-sm text-gray-500">
                    No stores match the current filters.
                  </td>
                </tr>
              ) : (
                displayStores.map((store) => {
                  const status = statusConfig[store.status];
                  // At Risk = the store has a deployed employee in the bottom 3% of
                  // their reporting manager's hierarchy (Bottom Box). Row highlighted.
                  const isAtRisk = (storeIntel(store)?.people?.bottomBoxCount ?? 0) > 0;
                  // NSW marker: the store has a recorded NSW exit AND the vacated
                  // slot is still unfilled (store currently has no one deployed).
                  // It auto-clears once anyone is redeployed in (employees > 0).
                  const nswRows = nswByStore.get(store.id) ?? [];
                  const showNsw = nswRows.length > 0 && (store.employees ?? 0) === 0;
                  return (
                    <tr key={store.id} className={`group text-gray-800 transition ${isAtRisk ? 'bg-rose-50/50 hover:bg-rose-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-start gap-3">
                          {isAtRisk ? (
                            <span title="Store at risk — employee in the bottom 3% of their manager's team">
                              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" />
                            </span>
                          ) : (
                            <span className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${status.dot}`} />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{store.name}</p>
                              {showNsw && (
                                <span
                                  title={formatNswTooltip(nswRows)}
                                  className="inline-flex flex-shrink-0 items-center rounded-full border border-rose-200 bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700"
                                >
                                  NSW
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              Trade: {store.trade} · Store Code: {store.storeCode}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{store.category || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{store.city || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <span className="text-sm font-semibold text-gray-900">{store.employees || 0}</span>
                      </td>
                      {(() => {
                        const emps = storeIntel(store)?.people?.employees ?? [];
                        if (emps.length === 0) {
                          return (
                            <>
                              <td className="px-6 py-4 text-sm text-gray-400" colSpan={3}>No employee data</td>
                              <td className="px-6 py-4 text-right text-gray-400">—</td>
                              <td className="px-6 py-4 text-right text-gray-400">—</td>
                            </>
                          );
                        }
                        return (
                          <>
                            {/* Employee name + code (one line per deployed employee) */}
                            <td className="px-6 py-4">
                              <div className="space-y-1.5">
                                {emps.map((e) => (
                                  <div key={e.employeeCode} className="leading-tight">
                                    <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                                      {e.employeeName || 'Unnamed'}
                                      {e.onNotice && (
                                        <span
                                          title={e.lastWorkingDay ? `On notice period · last working day ${e.lastWorkingDay}` : 'On notice period'}
                                          className="inline-flex flex-shrink-0 items-center rounded-full border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                                        >
                                          Notice
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-[11px] text-gray-400">{e.employeeCode}</p>
                                  </div>
                                ))}
                              </div>
                            </td>
                            {/* AON — age on network (tenure since joining), per employee */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="space-y-1.5">
                                {emps.map((e) => (
                                  <div key={e.employeeCode} className="flex h-[34px] items-center whitespace-nowrap text-sm text-gray-600">{e.aonLabel ?? '—'}</div>
                                ))}
                              </div>
                            </td>
                            {/* Standing — quadrant badge / bottom-boxing / insufficient */}
                            <td className="px-6 py-4">
                              <div className="space-y-1.5">
                                {emps.map((e) => (
                                  <div key={e.employeeCode} className="flex h-[34px] items-center gap-1.5">
                                    <QuadrantBadge q={e.quadrant} />
                                    {!e.quadrant && !e.isBottomBoxing && <span className="text-xs text-gray-300">—</span>}
                                    {e.isBottomBoxing && <BottomBoxBadge />}
                                  </div>
                                ))}
                              </div>
                            </td>
                            {/* 6-mo avg ending at the selected month. (The single-month score
                                column was removed — the 6-month average is the standing
                                signal this table acts on.) */}
                            <td className="px-6 py-4 text-right">
                              <div className="space-y-1.5">
                                {emps.map((e) => (
                                  <div key={e.employeeCode} className="flex h-[34px] items-center justify-end text-sm font-semibold text-gray-900">{e.avg6mScore ?? '—'}</div>
                                ))}
                              </div>
                            </td>
                          </>
                        );
                      })()}
                      <td className="sticky right-0 z-20 bg-white px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                        <TableActionGroup>
                          <TableActionButton action="analysis" label={`View insights for ${store.name}`} type="button" onClick={() => openInsightsModal(store)} title={store.profitStatus === 'Profitable' ? 'View performance insights' : 'Optimize store performance'} />
                        </TableActionGroup>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* Bottom Box — bottom 3% of each employee's reporting manager's hierarchy + their store. */}
      {deploymentTable === 'Bottom Box' && (() => {
        // Filtered list — shared with the KPI card + Show-tab pill so all agree.
        const bbEmps = bottomBoxEmployees;
        return (
      <section className={LIST_CARD}>
        <div className="border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Bottom Box</h2>
          <p className="text-xs text-gray-500">
            Bottom 3% of employees by 6-month average score, within each employee's reporting manager's hierarchy.
            {bottomBox ? ` ${bbEmps.length} employee${bbEmps.length === 1 ? '' : 's'} across ${bottomBoxStoreCount} store${bottomBoxStoreCount === 1 ? '' : 's'}.` : ''}
          </p>
        </div>
        <div className="overflow-x-auto">
          {!bottomBox ? (
            <div className="px-6 py-10 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></div>
          ) : bbEmps.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-gray-500">No bottom-3% employees for this month.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                <tr>
                  <SortTh label="Employee" sortKey="employee" sort={bottomBoxSort} className="whitespace-nowrap px-6 py-4" />
                  <SortTh label="Store" sortKey="store" sort={bottomBoxSort} className="whitespace-nowrap px-6 py-4" />
                  <SortTh label="City" sortKey="city" sort={bottomBoxSort} className="whitespace-nowrap px-6 py-4" />
                  <SortTh label="Reporting Head" sortKey="reportingHead" sort={bottomBoxSort} className="whitespace-nowrap px-6 py-4" />
                  <SortTh label="6-mo Avg" sortKey="avg6m" sort={bottomBoxSort} align="right" className="whitespace-nowrap px-6 py-4" />
                  <SortTh label="Standing" sortKey="standing" sort={bottomBoxSort} className="whitespace-nowrap px-6 py-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 bg-white">
                {bottomBoxSort.sorted.map((e) => (
                  <tr key={e.employeeCode} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{e.employeeName}</div>
                      <div className="text-xs text-gray-400">{e.employeeCode}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-900">{e.storeName || '—'}</div>
                      {e.storeCode && <div className="text-xs text-gray-400">{e.storeCode}</div>}
                    </td>
                    <td className="px-6 py-4 text-gray-700">{e.city || '—'}</td>
                    <td className="px-6 py-4 text-gray-700">{e.managerName || '—'}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">{e.avg6mScore.toFixed(1)}</td>
                    <td className="px-6 py-4">{e.quadrant ? <QuadrantBadge q={e.quadrant} /> : <span className="text-gray-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
        );
      })()}

      {/* Undeployed Stores — stores with no employee currently deployed. */}
      {deploymentTable === 'Undeployed Stores' && (() => {
        const undeployed = undeployedStoresList;
        return (
          <section className={LIST_CARD}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Undeployed Stores</h2>
                <p className="text-xs text-gray-500">
                  Stores below their category's minimum staffing — vacant, or understaffed (e.g. an L3 store needing 2 with only 1 deployed). {undeployed.length} store{undeployed.length === 1 ? '' : 's'}.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <AssignManagerButton list={vacancyManagers} />
                <button
                  type="button"
                  onClick={handleUndeployedStoresExport}
                  disabled={undeployed.length === 0}
                  title="Download these stores as a CSV — same filters and order as shown"
                  className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </button>
              </div>
            </div>
            <AssignStoreManagerModal
              isOpen={vacancyManagers.assigning}
              onClose={vacancyManagers.closeAssign}
              projectId={selectedProjectId ?? null}
              storeIds={vacancyManagers.tickedStoreIds}
              storeCount={vacancyManagers.tickedCount}
              canRemove={vacancyManagers.tickedHaveManager}
              current={vacancyManagers.tickedCurrent}
              onSaved={vacancyManagers.saved}
            />
            <div className="overflow-x-auto">
              {undeployed.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  {/* Stores are short of people, but none with the staffing status or vacancy age chosen. */}
                  {undeployedStoresBase.length > 0 ? 'No store matches the filters chosen.' : "Every store meets its category's minimum staffing."}
                </div>
              ) : (
                // px-3 between columns (px-6 at the left edge, under the title), and a Hiring word
                // that may wrap ("Needs / attention"), so the table fits its box at a laptop's width
                // with the Hiring column and every Actions control: the sticky Actions column then
                // covers nothing.
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                    <tr>
                      {vacancyManagers.canAssign && (
                        <th className="w-px py-4 pl-6"><TickAllVacantStores list={vacancyManagers} /></th>
                      )}
                      <SortTh label="Store Details" sortKey="store" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4 first:pl-6" />
                      <SortTh label="Category & City" sortKey="category" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" />
                      <SortTh label="Trade" sortKey="trade" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" />
                      <SortTh label="State" sortKey="state" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" />
                      <SortTh label="Staffing" sortKey="staffing" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" title="Active headcount deployed vs the category's minimum required. Highlighted when someone is deployed but below the minimum." />
                      {staffingTypeField && (
                        <SortTh label={staffingTypeField.label} sortKey="staffingType" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" title="Store attribute from Master Data → Stores → Custom Fields" />
                      )}
                      <SortTh label="Staffing Status" sortKey="staffingStatus" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" title="Where the store sits in the manpower pipeline, from Master Data → Stores. Independent of whether the store is Active." />
                      <SortTh label="Manager" sortKey="manager" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" title="Who sees this store in Store Visits while nobody is deployed there: each Trade Head and AM named, and those above them" />
                      <SortTh label="AON" sortKey="aon" sort={undeployedStoresSort} className="whitespace-nowrap px-3 py-4" title="How long the store has been vacant (since the last employee left, or since it was added if never deployed). Not applicable to understaffed stores that still have someone deployed." />
                      {/* The Stores page's Hiring column (product owner, 16 Oct 2026: a store's linked
                          opening opens where the store is), only where the server shows this person
                          hiring here. The header and its cell below are written under the same condition. */}
                      {undeployedHiring.shown && <th className="px-3 py-4">Hiring</th>}
                      {/* Open the store (a holder of the Stores page) and hiring for it (where the server
                          shows this person hiring here). The header and its cell below are written
                          under the same condition. */}
                      {undeployedActionsShown && (
                        <th className="sticky right-0 z-20 whitespace-nowrap bg-gray-50 px-3 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {undeployedStoresSort.sorted.map((s) => {
                      const active = s.activeHeadcount ?? 0;
                      const min = s.minEmployees ?? 1;
                      const understaffed = active > 0 && active < min;
                      const hiringStore = hiringRowStoreOf(s);
                      const hiring = undeployedHiring.rowOf(hiringStore);
                      return (
                      <tr key={s.id} className="group hover:bg-gray-50">
                        {vacancyManagers.canAssign && (
                          // Only a store with nobody deployed takes a manager here.
                          <td className="py-4 pl-6">{active === 0 && <TickVacantStore list={vacancyManagers} row={s} />}</td>
                        )}
                        <td className="px-3 py-4 first:pl-6">
                          <div className="font-medium text-gray-900">{s.name}</div>
                          <div className="text-xs text-gray-400">Store Code: {s.storeCode}</div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-gray-900">{s.category || '—'}</div>
                          <div className="text-xs text-gray-400">{s.city}</div>
                        </td>
                        <td className="px-3 py-4 text-gray-700">{s.trade || '—'}</td>
                        <td className="px-3 py-4 text-gray-700">{s.state || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-4">
                          <span className="font-medium text-gray-700">{active} / {min}</span>
                          {understaffed && (
                            <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-600">Understaffed</span>
                          )}
                        </td>
                        {staffingTypeField && (
                          <td className="px-3 py-4 text-gray-700">{formatStoreCustomFieldValue(s.customFields?.[staffingTypeField.id]) || '—'}</td>
                        )}
                        <td className="whitespace-nowrap px-3 py-4">
                          {s.staffingStatus ? (
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${STAFFING_STATUS_BADGE[s.staffingStatus] ?? STAFFING_STATUS_UNSET_BADGE}`}>
                              {s.staffingStatus}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        {/* Wraps: a long name must not push the Hiring word under the sticky Actions column. */}
                        <td className="px-3 py-4">
                          <StoreManagerCell
                            known={vacancyManagers.known}
                            understaffed={active > 0}
                            managers={vacancyManagers.managersOf(s)}
                            applies={vacancyManagers.managerApplies(s)}
                          />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 font-medium text-gray-700" title={(() => {
                        const since = aonSinceOf(s, understaffed, monthEndAsOf);
                        if (!since) return understaffed ? 'Store has someone deployed but below the required minimum' : 'Undeployed date unknown';
                        return understaffed ? `Below the required minimum since ${since}` : `Undeployed since ${since}`;
                      })()}>{formatUndeployedAon(aonSinceOf(s, understaffed, monthEndAsOf), monthEndAsOf)}</td>
                        {/* One dot and one word, as on the Stores page: the word opens the store's hiring
                            window (its openings, each with Details, Team and Candidates). A dash when
                            nothing is followed or waiting, and when the list could not be read. */}
                        {undeployedHiring.shown && (
                          <td className="px-3 py-4">
                            {undeployedHiring.listStatus === 'ready' ? (
                              <StoreHiringDot
                                storeName={s.name}
                                links={hiring.links}
                                // The store's hiring request that waits for confirmation, for those shown them.
                                request={hiring.request}
                                onOpen={() => undeployedHiring.openHiring(hiringStore)}
                              />
                            ) : undeployedHiring.listStatus === 'failed' ? (
                              <span className="text-xs text-gray-400">—</span>
                            ) : (
                              <Skeleton className="h-5 w-24 rounded-full" />
                            )}
                          </td>
                        )}
                        {undeployedActionsShown && (
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                            <TableActionGroup>
                              {/* The store on the Stores page, searched for it, in a new tab so this list keeps
                                  its place. Only for someone who holds that page: anyone else would land on "no access". */}
                              {storesPage && (
                                <TableActionLink action="open" label={`Open ${s.name} in ${storesPage.name}`}
                                  href={storeFixLink({ storeCode: hiringStore.storeCode, storeName: s.name })}
                                  target="_blank" />
                              )}
                              {/* Greyed while the store's own request has not ended (no second one is offered),
                                  for a store the server refuses, and on a past month: the hover says which. */}
                              {undeployedHiring.canRaise && (
                                <TableActionButton action="opening" label={STORE_ROW_HIRING_WORDS.raise(s.name)}
                                  disabled={!!hiring.raiseRefused || hiring.raiseBusy}
                                  title={hiring.raiseRefused ?? (hiring.raiseBusy ? STORE_ROW_HIRING_WORDS.raiseBusy : undefined)}
                                  onClick={() => undeployedHiring.openRaise(hiringStore)} />
                              )}
                              {/* For a person who may ask and may not raise: never beside Raise. */}
                              {undeployedHiring.canAsk && (
                                <TableActionButton action="submit" label={STORE_ROW_HIRING_WORDS.ask(s.name)}
                                  disabled={!!hiring.raiseRefused || hiring.askWaiting || hiring.raiseBusy}
                                  title={hiring.raiseRefused ?? (hiring.askWaiting
                                    ? STORE_ROW_HIRING_WORDS.askWaiting
                                    : hiring.raiseBusy ? STORE_ROW_HIRING_WORDS.raiseBusy : undefined)}
                                  onClick={() => undeployedHiring.openAsk(hiringStore)} />
                              )}
                              {undeployedHiring.canLink && (
                                <TableActionButton action="link" label={STORE_ROW_HIRING_WORDS.link(s.name)}
                                  onClick={() => undeployedHiring.openLink(hiringStore)} />
                              )}
                              {/* Only for a store with hiring followed, being raised or waiting for confirmation. */}
                              {hiring.status && (
                                <TableActionButton action="openings" label={STORE_ROW_HIRING_WORDS.open(s.name)}
                                  title={hiring.openHover ?? undefined}
                                  onClick={() => undeployedHiring.openHiring(hiringStore)} />
                              )}
                            </TableActionGroup>
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        );
      })()}

      {/* MisDeployed Stores — deployed with a wrong-tenure employee for the store
          category. Under-qualified (below the requirement) or over-qualified (meets
          a higher category). Client-side from the filtered store set. */}
      {deploymentTable === 'MisDeployed Stores' && (() => {
        // Under / over-qualified filtering now lives in the Filters modal
        // (Qualification). This view just renders the current MisDeployed set.
        const rows = misDeployedStores;
        return (
          <section className={LIST_CARD}>
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">MisDeployed Stores</h2>
                  {aonGraceMonths > 0 && (
                    <p className="text-xs text-gray-500">
                      Employees within {aonGraceMonths} month{aonGraceMonths === 1 ? '' : 's'} of their store&apos;s tenure requirement count as meeting it — here and in the reshuffle plan.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    className="flex items-center gap-2 text-xs font-medium text-gray-500"
                    title="Grace period on AON: treat employees who will reach their store's tenure requirement within this many months as qualified"
                  >
                    AON grace
                    <select
                      value={aonGraceMonths}
                      onChange={(e) => setAonGraceMonths(Number(e.target.value))}
                      className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none"
                    >
                      <option value={0}>None</option>
                      {Array.from({ length: MAX_AON_GRACE_MONTHS }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n} month{n === 1 ? '' : 's'}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={handleMisDeployedExport}
                    disabled={rows.length === 0 || misDeployedExporting}
                    title={`Download these stores as an Excel file, each with whether a swap within ${MISDEPLOYED_EXPORT_RADIUS_KM} km would leave both stores correctly deployed`}
                    className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {misDeployedExporting ? 'Preparing…' : 'Download reshuffle plan'}
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              {rows.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  {misDeployedStores.length === 0
                    ? `Every deployed store matches its category eligibility${aonGraceMonths > 0 ? ` within the ${aonGraceMonths}-month grace` : ''}.`
                    : 'No stores in this category.'}
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                    <tr>
                      <SortTh label="Store Details" sortKey="store" sort={misDeployedSort} className="whitespace-nowrap px-6 py-4" />
                      <SortTh label="Category & eligibility" sortKey="category" sort={misDeployedSort} className="whitespace-nowrap px-6 py-4" />
                      <SortTh label="Employee" sortKey="employee" sort={misDeployedSort} className="whitespace-nowrap px-6 py-4" />
                      <SortTh label="AON" sortKey="aon" sort={misDeployedSort} className="whitespace-nowrap px-6 py-4" title="Age On Network — tenure since date of joining" />
                      <SortTh label="Deployment" sortKey="deployment" sort={misDeployedSort} className="whitespace-nowrap px-6 py-4" />
                      <th className="text-center whitespace-nowrap px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {misDeployedSort.sorted.map((m) => (
                      <tr key={m.store.id} className="align-top hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{m.store.name}</div>
                          <div className="text-xs text-gray-400">Store Code: {m.store.storeCode}</div>
                          <div className="text-xs text-gray-400">{m.store.city}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-gray-900">{m.store.category || '—'}</div>
                          <div className="text-xs text-gray-400">requires {m.reqLabel}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {m.employees.map((e) => (
                              <div key={e.code} className="flex h-[34px] items-center whitespace-nowrap text-gray-900">
                                {e.name}<span className="ml-1 text-xs text-gray-400">({e.code})</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {m.employees.map((e) => (
                              <div key={e.code} className="flex h-[34px] items-center whitespace-nowrap text-gray-700">
                                {e.aonLabel}
                                {/* With a grace set, show the exact gap: the label rounds to months, so a
                                    "1y 10m" can still be a few days beyond a 2-month grace. */}
                                {aonGraceMonths > 0 && e.daysToRequirement != null && (
                                  <span className="ml-1.5 text-xs text-gray-400">· qualifies in {e.daysToRequirement}d</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {m.employees.map((e) => (
                              <div key={e.code} className="flex h-[34px] items-center">
                                {e.kind === 'under' ? (
                                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600">Under-qualified</span>
                                ) : e.kind === 'over' ? (
                                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-600">Over-qualified</span>
                                ) : e.withinGrace ? (
                                  <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-600">Within grace</span>
                                ) : (
                                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">OK</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-center px-6 py-4"><TableActionGroup>
                          {(() => {
                            // Target the misfit employee (the one driving this store's
                            // under/over classification). Opens the redeploy cockpit in
                            // eligibility mode with them pre-selected.
                            const misfit = m.employees.find((e) => e.kind === m.kind);
                            return misfit ? (
                              <TableActionButton action="transfer" label={`Redeploy ${misfit.name}`} type="button" onClick={() => openRedeployForMisdeployed(m.store, misfit.code)} title={`Redeploy ${misfit.name} — suggest eligible stores (move or swap)`} />
                            ) : null;
                          })()}
                        </TableActionGroup></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        );
      })()}

      {/* Undeployed Employees — active project employees not placed at any store.
          Sourced from stores/undeployed-employees; roleName (user_roles → roles.name)
          is attached server-side so the Role filter scopes this list too. */}
      {deploymentTable === 'Undeployed Employees' && (
        <section className={LIST_CARD}>
          <div className="border-b border-gray-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Undeployed Employees</h2>
            <p className="text-xs text-gray-500">
              Active employees not currently deployed at any store. {undeployedEmpsFiltered.length} employee{undeployedEmpsFiltered.length === 1 ? '' : 's'}.
            </p>
          </div>
          <div className="overflow-x-auto">
            {undeployedEmpsFiltered.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-gray-500">No undeployed employees.</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <SortTh label="Employee" sortKey="employee" sort={undeployedEmpsSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="Designation" sortKey="designation" sort={undeployedEmpsSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="City & State" sortKey="city" sort={undeployedEmpsSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="Department" sortKey="department" sort={undeployedEmpsSort} className="whitespace-nowrap px-6 py-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 bg-white">
                  {undeployedEmpsSort.sorted.map((e) => (
                    <tr key={e.code} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{e.name}</div>
                        <div className="text-xs text-gray-400">{e.code}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{e.designation || '—'}</td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">{e.city || '—'}</div>
                        <div className="text-xs text-gray-400">{e.state || '—'}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{e.department || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {/* Outside Approved – Redeploy — active employees still deployed at a store marked
          outside the approved list. The store keeps running as an active store until
          the last of them is moved; Redeploy opens the cockpit on that person. */}
      {deploymentTable === STORE_STATUS_REDEPLOY && (
        <section className={LIST_CARD}>
          <div className="border-b border-gray-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">{STORE_STATUS_REDEPLOY}</h2>
            <p className="text-xs text-gray-500">
              Employees still deployed at a store marked {STORE_STATUS_REDEPLOY}, longest wait first.
              {redeployPendingState === 'ready' && <> {redeployPendingRows.length} employee{redeployPendingRows.length === 1 ? '' : 's'}.</>}
            </p>
          </div>
          <div className="overflow-x-auto">
            {redeployPendingState === 'failed' ? (
              // Not "nobody is waiting": the stores did not load, so that is not known.
              <div className="px-6 py-10 text-center text-sm text-gray-500">
                The employees waiting to be redeployed could not be loaded.
              </div>
            ) : redeployPendingState === 'ready' && redeployPendingRows.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-gray-500">
                {/* Never "nobody is waiting" when it is only the filters hiding them. */}
                {redeployPendingAll.length === 0
                  ? 'No employees are waiting to be redeployed.'
                  : 'No employees match the current filters.'}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <SortTh label="Employee" sortKey="employee" sort={redeployPendingSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="Role" sortKey="role" sort={redeployPendingSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="Store" sortKey="store" sort={redeployPendingSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="City" sortKey="city" sort={redeployPendingSort} className="whitespace-nowrap px-6 py-4" />
                    <SortTh label="Marked on" sortKey="markedOn" sort={redeployPendingSort} className="whitespace-nowrap px-6 py-4" title="The day the store was marked Outside Approved – Redeploy, and how long its employees have been waiting to be moved" />
                    <SortTh label="Reporting Manager" sortKey="manager" sort={redeployPendingSort} className="whitespace-nowrap px-6 py-4" />
                    <th className="text-center whitespace-nowrap px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 bg-white">
                  {/* Skeleton rows under the real header, as Store Performance loads. */}
                  {redeployPendingState === 'loading' ? Array.from({ length: 6 }).map((_, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </td>
                      <td className="px-6 py-4"><Skeleton className="h-4 w-28" /></td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </td>
                      <td className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </td>
                      <td className="px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                      <td className="text-center px-6 py-4"><Skeleton className="mx-auto h-5 w-5" /></td>
                    </tr>
                  )) : redeployPendingSort.sorted.map((r) => {
                    const waiting = formatRedeployWaiting(redeployWaitingDays(r.markedOn));
                    const who = r.employeeName || r.employeeCode;
                    return (
                      // A roaming promoter can be on two such stores, hence the store in the key.
                      <tr key={`${r.storeId}:${r.employeeCode}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{r.employeeName || '—'}</div>
                          <div className="text-xs text-gray-400">{r.employeeCode}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-700">{r.roleName || '—'}</td>
                        <td className="px-6 py-4">
                          <div className="text-gray-900">{r.storeName || '—'}</div>
                          {r.storeCode && <div className="text-xs text-gray-400">{r.storeCode}</div>}
                        </td>
                        <td className="px-6 py-4 text-gray-700">{r.city || '—'}</td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-gray-900">{formatTimestampDay(r.markedOn)}</div>
                          {waiting && <div className="text-xs text-gray-400">{waiting}</div>}
                        </td>
                        <td className="px-6 py-4 text-gray-700">{r.reportingManagerName || '—'}</td>
                        <td className="text-center px-6 py-4"><TableActionGroup>
                          <TableActionButton action="transfer" label={`Redeploy ${who}`} type="button" onClick={() => openRedeployForPending(r)} title={`Redeploy ${who} to another store`} />
                        </TableActionGroup></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {renderInsightsModal()}
      {renderSwapModal()}
        </>
      )}

      {/* A role-filtered dashboard waits for the store-level Role default (see
          roleDefaultSettled): until then it is not drawn, and this is the spinner it
          shows while its own request is out. */}
      {awaitingRoleDefault && (
        <BlockLoader className="h-72" />
      )}

      {activeTab === 'Attendance' && roleDefaultSettled && (
        <AttendanceTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {activeTab === 'Red Flag' && roleDefaultSettled && (
        <RedFlagTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
          flagType={redFlagType || undefined}
          level={(redFlagRulesEnabled && redFlagLevel) || undefined}
          onTypesLoaded={handleRedFlagTypesLoaded}
        />
      )}

      {activeTab === 'Yellow Flag' && roleDefaultSettled && (
        <YellowFlagTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
          reason={yellowReason || undefined}
        />
      )}

      {activeTab === 'Mood' && roleDefaultSettled && (
        <MoodTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {activeTab === 'Appeals' && roleDefaultSettled && (
        <AppealsTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {/* Attrition takes only the LOOK-BACK-SAFE filters: Role / Area Manager / Trade
          Head / Budget, Region / State / City (placed by last store on record), and
          Tenure at exit. dashFilterParams also carries store name / trade / category
          / standing values from the shared filter state, but the backend ignores
          those for attrition and the modal doesn't offer them on this tab — so
          nothing here can produce a misleading denominator. */}
      {activeTab === 'Attrition' && roleDefaultSettled && (
        <AttritionTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
          tenures={selectedTenures.join(',') || undefined}
          onFacetsLoaded={handleAttritionFacetsLoaded}
        />
      )}

      {activeTab === 'Approvals' && roleDefaultSettled && (
        <ApprovalsTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {/* Onboarding takes only projectId. It is a live worklist of who joined in
          the last few days, not a view of a reporting period, so the month picker
          and the dashboard attribute filters do not apply — a joiner from three
          days ago is equally relevant whichever month is selected. */}
      {activeTab === 'Onboarding' && <OnboardingTab projectId={selectedProjectId} />}

      {activeTab === 'Training' && roleDefaultSettled && (
        <TrainingTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {/* Score Movement reads the nightly scorecard snapshots for the six months
          ending at the selected month (or the latest scored month before it — the
          tab says which). The Role and rich filters narrow whose scores are in view. */}
      {activeTab === 'Score Movement' && roleDefaultSettled && (
        <ScoreMovementTab
          projectId={selectedProjectId}
          month={periodMonth.slice(0, 7)}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {/* EWS: three finished months per employee, ending at the month picked (a month
          still running reads as the one before it). The Role and rich filters narrow who
          is listed, as on Score Movement. */}
      {activeTab === 'EWS' && roleDefaultSettled && (
        <EwsTab
          projectId={selectedProjectId}
          month={ewsEndMonth.slice(0, 7)}
          employees={ewsEmployees}
          roleId={selectedRoleIds.join(',') || undefined}
          filters={dashFilterParams}
        />
      )}

      {/* Uniform & Asset Management takes only projectId, like Onboarding. It is
          1Office Admin Management's uniform dashboard for the selected project: its
          SLA card is always the current India-time month (upstream takes no month),
          and its rows are 1Office uniform requests — of candidates as well as
          employees — not ChannelPlex employees the attribute filters could narrow.
          So neither the month picker nor the Filters modal reaches it. */}
      {activeTab === 'Uniform & Asset Management' && <UniformTab projectId={selectedProjectId} />}

      {/* Data Hygiene lists what is wrong in the store and people records as they stand
          right now, so the month does not reach it. The Role filter does, on its
          Employees side only, and it is drawn at once: the Stores side needs no role,
          and the Employees side waits for the store-level default itself. */}
      {activeTab === 'Data Hygiene' && (
        <DataHygieneTab projectId={selectedProjectId} roles={selectedRoles} rolesPending={!roleDefaultSettled} />
      )}

      {/* Hiring: every opening this project follows, live from the hiring list. Neither the
          month nor the filters reach it; its own search and status filter do. */}
      {activeTab === 'Hiring' && <HiringDashboardTab actionSlot={hiringActionSlot} />}

      {/* The hiring windows of the Undeployed Stores Actions: outside the table, so a re-read of it never closes one. */}
      {undeployedHiring.windows}

      {/* The hiring question when the redeploy cockpit's confirm is pressed: over the cockpit, before anything is saved. */}
      {deployHiring.window}

      {activeTab !== null && activeTab !== 'Custom' && <RelatedRow tab={activeTab} />}

      {/* Generic, per-dashboard filter modal (Roles + Month always; contextual
          fields per active Deployment sub-view). */}
      <DashboardFilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title={filterModalTitle}
        sections={showFilterModal ? buildFilterSections() : []}
        activeCount={shownFilterCount}
        onClearAll={roleFilterOnly ? () => setSelectedRoles([]) : clearFilters}
      />
    </div>
  );
}
